import React, { useEffect, useState, useCallback, useRef } from 'react';
import API from '../api';
import ChatWindow from './ChatWindow';
import { unregisterPushSubscription } from '../push';
import { AdminMaterials } from './Materials';
import { generateStudentPDF } from '../utils/studentReport';
import { StudentBillingOverview, BillsList } from './StudentBilling';
import { ExamPaperBrowser, StudentPicker } from './ExamPapers';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIMEZONES = [
  { value: 'Asia/Kolkata',      label: 'India (IST, UTC+5:30)' },
  { value: 'Asia/Dubai',        label: 'UAE / Dubai (GST, UTC+4)' },
  { value: 'Asia/Singapore',    label: 'Singapore (SGT, UTC+8)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia (MYT, UTC+8)' },
  { value: 'Asia/Riyadh',       label: 'Saudi Arabia (AST, UTC+3)' },
  { value: 'Asia/Qatar',        label: 'Qatar (AST, UTC+3)' },
  { value: 'Asia/Kuwait',       label: 'Kuwait (AST, UTC+3)' },
  { value: 'Asia/Bahrain',      label: 'Bahrain (AST, UTC+3)' },
  { value: 'Asia/Muscat',       label: 'Oman (GST, UTC+4)' },
  { value: 'Europe/London',     label: 'UK (GMT/BST)' },
  { value: 'America/New_York',  label: 'USA Eastern (EST/EDT)' },
  { value: 'America/Chicago',   label: 'USA Central (CST/CDT)' },
  { value: 'America/Los_Angeles', label: 'USA Pacific (PST/PDT)' },
  { value: 'Australia/Sydney',  label: 'Australia Sydney (AEST)' },
  { value: 'Australia/Melbourne', label: 'Australia Melbourne (AEST)' },
  { value: 'Australia/Perth',   label: 'Australia Perth (AWST, UTC+8)' },
  { value: 'Europe/Berlin',     label: 'Germany / Central Europe (CET)' },
  { value: 'Europe/Paris',      label: 'France / Paris (CET)' },
  { value: 'Asia/Colombo',      label: 'Sri Lanka (IST, UTC+5:30)' },
  { value: 'Asia/Dhaka',        label: 'Bangladesh (BST, UTC+6)' },
  { value: 'Asia/Karachi',      label: 'Pakistan (PKT, UTC+5)' },
  { value: 'Asia/Kathmandu',    label: 'Nepal (NPT, UTC+5:45)' },
  { value: 'Africa/Nairobi',    label: 'Kenya / East Africa (EAT, UTC+3)' },
  { value: 'Africa/Lagos',      label: 'Nigeria / West Africa (WAT, UTC+1)' },
  { value: 'America/Toronto',   label: 'Canada Eastern (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Canada Pacific (PST/PDT)' },
  { value: 'Pacific/Auckland',  label: 'New Zealand (NZST)' },
];

// ── SearchableSelect ────────────────────────────────────────────────────────────
// options: [{ value, label }], value: string, onChange: (value: string) => void
function SearchableSelect({ options, value, onChange, placeholder = '— Select —' }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = React.useRef();
  const selectedLabel = (options || []).find(o => String(o.value) === String(value))?.label || '';
  const filtered = (options || []).filter(o => (o.label || '').toLowerCase().includes(search.toLowerCase()));
  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className="form-control"
        style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', userSelect:'none', minHeight: 38 }}
        onClick={() => { setOpen(o => !o); setSearch(''); }}>
        <span style={{ color: selectedLabel ? 'inherit' : '#94a3b8', fontSize: 14 }}>{selectedLabel || placeholder}</span>
        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ position:'absolute', zIndex:1200, top:'100%', left:0, right:0, background:'var(--surface, #fff)', border:'1px solid var(--border, #e2e8f0)', borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,0.13)', marginTop:2, display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border, #e2e8f0)' }}>
            <input autoFocus className="form-control" style={{ padding:'5px 10px', fontSize:13 }} placeholder="Search…" value={search}
              onChange={e => setSearch(e.target.value)} onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ overflowY:'auto', maxHeight:200 }}>
            <div style={{ padding:'7px 12px', fontSize:13, color:'#94a3b8', cursor:'pointer' }}
              onMouseDown={() => { onChange(''); setOpen(false); }}>{placeholder}</div>
            {filtered.length === 0
              ? <div style={{ padding:'10px 12px', fontSize:13, color:'#94a3b8' }}>No results</div>
              : filtered.map(o => (
                <div key={o.value}
                  style={{ padding:'8px 12px', fontSize:13, cursor:'pointer', background: String(o.value) === String(value) ? 'rgba(37,84,184,0.08)' : 'transparent', fontWeight: String(o.value) === String(value) ? 700 : 400 }}
                  onMouseDown={() => { onChange(String(o.value)); setOpen(false); }}>
                  {o.label}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function LiveClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      let h = now.getHours();
      const m = now.getMinutes();
      const s = now.getSeconds();
      const period = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      setTime(`${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ${period}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span style={{ fontSize:11, color:'#94a3b8', fontWeight:600, fontFamily:'monospace', letterSpacing:0.5 }}>{time}</span>;
}
const PRICING_PLANS = ['Starter Scholar', 'Rising Star', 'Prime Scholar'];

const SYLLABUS_OPTIONS = ['CBSE', 'ICSE', 'Kerala State', 'IGCSE'];

// Picks one of the standard syllabi or accepts a custom name via "Other".
// Stores the final value back into a single string via `onChange(value)`.
function SyllabusPicker({ value, onChange, label = 'Syllabus' }) {
  const isStandard = value && SYLLABUS_OPTIONS.includes(value);
  const initialType = !value ? '' : (isStandard ? value : 'Other');
  const [type, setType] = useState(initialType);
  const [custom, setCustom] = useState(isStandard ? '' : (value || ''));

  // Keep local state in sync if parent value changes externally
  useEffect(() => {
    const std = value && SYLLABUS_OPTIONS.includes(value);
    setType(!value ? '' : (std ? value : 'Other'));
    setCustom(std ? '' : (value || ''));
  }, [value]);

  const handleType = (t) => {
    setType(t);
    if (t === '') onChange('');
    else if (t === 'Other') onChange(custom.trim() || '');
    else onChange(t);
  };
  const handleCustom = (c) => {
    setCustom(c);
    if (type === 'Other') onChange(c.trim());
  };

  return (
    <>
      <div className="form-group">
        <label>{label}</label>
        <select className="form-control" value={type} onChange={e => handleType(e.target.value)}>
          <option value="">Select Syllabus</option>
          {SYLLABUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          <option value="Other">Other (specify)</option>
        </select>
      </div>
      {type === 'Other' && (
        <div className="form-group">
          <label>Custom Syllabus *</label>
          <input className="form-control" value={custom} maxLength={50}
            placeholder="e.g. NIOS, IB, State board name…"
            onChange={e => handleCustom(e.target.value)} />
        </div>
      )}
    </>
  );
}

// converts "HH:MM:SS" or "HH:MM" → "H:MM AM/PM"
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

// converts "2024-01-15T14:30:00" → "2024-01-15 2:30 PM"
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    // Append 'Z' so JS interprets the UTC timestamp correctly and converts to local time
    const utcIso = iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
    const d = new Date(utcIso);
    const datePart = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
    const h = d.getHours(), m = d.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${datePart} ${hour}:${String(m).padStart(2, '0')} ${period}`;
  } catch { return iso; }
}

// ── 12-hour TimePicker component ───────────────────────────────────────────────
function TimePicker12h({ value, onChange, className }) {
  // value is "HH:MM" (24h), onChange gives "HH:MM" (24h)
  const toDisplay = (v) => {
    if (!v) return { h: '12', m: '00', period: 'AM' };
    const [hh, mm] = v.split(':');
    let h = parseInt(hh);
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return { h: String(h), m: mm || '00', period };
  };
  const { h, m, period } = toDisplay(value);
  const to24 = (hStr, mStr, p) => {
    let h = parseInt(hStr);
    if (p === 'AM' && h === 12) h = 0;
    if (p === 'PM' && h !== 12) h += 12;
    return `${String(h).padStart(2,'0')}:${String(mStr).padStart(2,'0')}`;
  };
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
      <select className={className} value={h} style={{ width:74 }}
        onChange={e => onChange(to24(e.target.value, m, period))}>
        {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{String(i+1).padStart(2,'0')}</option>)}
      </select>
      <span>:</span>
      <select className={className} value={m} style={{ width:74 }}
        onChange={e => onChange(to24(h, e.target.value, period))}>
        {['00','05','10','15','20','25','30','35','40','45','50','55'].map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <select className={className} value={period} style={{ width:80 }}
        onChange={e => onChange(to24(h, m, e.target.value))}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

// Convert IST time to user's local timezone for display
function toLocalTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return timeStr || '';
  try {
    const istDate = new Date(`${dateStr}T${timeStr}+05:30`);
    const h = istDate.getHours();
    const m = istDate.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2,'0')} ${period}`;
  } catch { return timeStr; }
}
function toLocalDatetime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : '+05:30'));
    const h = d.getHours(), m = d.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${d.toLocaleDateString()} ${hour}:${String(m).padStart(2,'0')} ${period}`;
  } catch { return isoStr; }
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
function useUnreadChats() {
  const [unreadRooms, setUnreadRooms] = useState({});
  const check = useCallback(() => {
    API.get('/chat/rooms').then(r => {
      const map = {};
      (r.data || []).forEach(room => {
        const seen = parseInt(localStorage.getItem(`chat_seen_${room.room_id}`) || '0');
        map[room.room_id] = (room.last_message_id || 0) > seen;
      });
      setUnreadRooms(map);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [check]);
  return [unreadRooms, check];
}

function Sidebar({ active, setActive, unreadCount, sidebarOpen, setSidebarOpen }) {
  const name = localStorage.getItem('name') || 'Admin';
  const logout = async () => { await unregisterPushSubscription(); try { await API.post('/auth/logout'); } catch(_){} localStorage.clear(); sessionStorage.clear(); window.location.href = '/login'; };

  const items = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'users',     icon: '👥', label: 'Users' },
    { id: 'inquiries', icon: '📨', label: 'Inquiries' },
    { id: 'studreg',   icon: '🎓', label: 'Student Registration' },
    { id: 'tutorreg',  icon: '👨‍🏫', label: 'Companion Registration' },
    { id: 'classes',   icon: '🏫', label: 'Classes' },
    { id: 'subjects',  icon: '📖', label: 'Subjects' },
    { id: 'enroll',    icon: '📋', label: 'Enroll Student' },
    { id: 'schedule',  icon: '📅', label: 'Timetable' },
    { id: 'logs',      icon: '📝', label: 'Class Logs' },
    { id: 'payments',  icon: '💰', label: 'Payments & Hours' },
    { id: 'chat',      icon: '💬', label: 'Chat Rooms' },
    { id: 'classreq',    icon: '📋', label: 'Class Requests' },
    { id: 'bulkleave',    icon: '📅', label: 'Companion Bulk Leave' },
    { id: 'studbulklv',   icon: '🎓', label: 'Student Bulk Leave' },
    { id: 'leavereq',     icon: '🗓️', label: 'Leave Requests' },
    { id: 'feedback',     icon: '💬', label: 'Student Feedback' },
    { id: 'emergency',    icon: '🚨', label: 'Emergency Leaves' },
    { id: 'activitylog',  icon: '🔍', label: 'Activity Log'     },
    { id: 'wasettings',   icon: '🔔', label: 'WhatsApp Settings' },
    { id: 'pushsettings', icon: '📲', label: 'Push Notifications' },
    { id: 'exams',        icon: '✏️', label: 'Exam Papers' },
    { id: 'cqueries',     icon: '❓', label: 'Companion Queries' },
    { id: 'vr',           icon: '🕶️', label: 'VR Learning'       },
    { id: 'teams',        icon: '🎥', label: 'Teams Meetings'    },
    { id: 'materials',    icon: '📚', label: 'Study Materials'   },
    { id: 'testgrades',  icon: '📝', label: 'Tests & Grades'    },
    { id: 'homework',    icon: '📚', label: 'Homework'          },
  ];

  return (
    <div className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
      <div className="sidebar-brand">
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
          <div style={{ width:40, height:40, borderRadius:12, background:'#ffffff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid rgba(255,255,255,0.3)', overflow:'hidden', padding:4, flexShrink:0 }}>
            <img src="/logo.png" alt="logo" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
          </div>
          <div>
            <h1 style={{ fontSize:13, letterSpacing:0.2 }}>Sapience Edu Connect</h1>
            <p style={{ fontSize:9.5, marginTop:1, fontWeight:500 }}>Connecting minds shaping futures</p>
          </div>
        </div>
        <p style={{ fontSize:11, marginTop:2 }}>Admin Portal</p>
        <LiveClock />
      </div>
      <nav className="sidebar-nav">
        <div className="nav-section">Main</div>
        {items.map(i => (
          <div
            key={i.id}
            className={`nav-item ${active === i.id ? 'active' : ''}`}
            onClick={() => { setActive(i.id); setSidebarOpen(false); }}
            style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}
          >
            <span><span className="nav-icon">{i.icon}</span> {i.label}</span>
            {i.id === 'chat' && unreadCount > 0 && (
              <span style={{ background:'#dc2626', color:'#fff', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:800, marginLeft:4, flexShrink:0 }}>{unreadCount}</span>
            )}
          </div>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="user-chip">
          <div className="user-chip-avatar">{name[0]}</div>
          <div className="user-chip-info">
            <div className="user-chip-name">{name}</div>
            <div className="user-chip-role">Admin</div>
          </div>
          <button className="logout-btn" onClick={logout} title="Logout" style={{ background:'none', border:'none', cursor:'pointer', padding:'4px 6px', WebkitTapHighlightColor:'transparent' }}>⏻</button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState({ students: 0, tutors: 0, enrollments: 0, sessions: 0 });
  const [todayLeaves, setTodayLeaves] = useState({ tutor_leaves:[], student_leaves:[], date:'' });
  const [emergencyList, setEmergencyList] = useState([]);
  const [pendingQueries, setPendingQueries] = useState(0);
  const [pendingClassReqs, setPendingClassReqs] = useState(0);
  const [pendingMaterialReqs, setPendingMaterialReqs] = useState(0);
  const [pendingVR, setPendingVR] = useState(0);
  const [endingSoon, setEndingSoon] = useState([]);
  const [absences, setAbsences] = useState([]);

  const loadEmergency = useCallback(() => {
    API.get('/emergency/today').then(r => setEmergencyList(r.data || [])).catch(() => {});
  }, []);

  const loadLeaves = useCallback(() => {
    API.get('/leave/today').then(r => setTodayLeaves(r.data)).catch(() => {});
  }, []);

  const loadPending = useCallback(() => {
    API.get('/companion-queries/').then(r => setPendingQueries((r.data || []).filter(q => q.status === 'pending').length)).catch(() => {});
    API.get('/class-request/all').then(r => setPendingClassReqs((r.data || []).filter(c => c.status === 'pending').length)).catch(() => {});
    API.get('/materials/requests').then(r => setPendingMaterialReqs((r.data || []).filter(m => m.status === 'pending').length)).catch(() => {});
    API.get('/vr/requests').then(r => setPendingVR((r.data || []).filter(v => v.status === 'pending').length)).catch(() => {});
    API.get('/schedule/ending-soon').then(r => setEndingSoon(r.data || [])).catch(() => {});
    API.get('/schedule/unverified-absences').then(r => setAbsences(r.data || [])).catch(() => {});
  }, []);

  const verifyAbsence = async (schedId) => {
    try {
      await API.post(`/schedule/${schedId}/verify-absence`);
      setAbsences(prev => prev.filter(a => a.schedule_id !== schedId));
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to verify'); }
  };

  // allSettled, not all: one failing request must not zero out the other
  // three cards. A fulfilled-but-non-array result keeps the PREVIOUS value
  // rather than overwriting with 0, so a transient failure never blanks the
  // dashboard once it has loaded successfully.
  const loadStats = useCallback(() => {
    // Some admins (toggled by super-admin) should see only ACTIVE students /
    // companions in the counts. Read the preference from /auth/me each load.
    API.get('/auth/me').then(me => {
      const activeOnly = !!me?.data?.dashboard_active_only;
      Promise.allSettled([
        API.get('/auth/users/student'),
        API.get('/auth/users/tutor'),
        API.get('/enrollment/'),
        API.get('/schedule/'),
      ]).then(([s, t, e, sc]) => {
        setStats(prev => {
          const count = (r, name, fallback, filterActive) => {
            if (r.status === 'fulfilled' && Array.isArray(r.value?.data)) {
              const arr = r.value.data;
              return (filterActive && activeOnly)
                ? arr.filter(u => u.is_active !== 'inactive').length
                : arr.length;
            }
            console.warn(`[dashboard] ${name} fetch failed:`, r.reason?.response?.status || r.reason?.message || 'unknown');
            return fallback;  // keep last known good count instead of zeroing
          };
          return {
            students: count(s, 'students', prev.students, true),
            tutors: count(t, 'tutors', prev.tutors, true),
            enrollments: count(e, 'enrollments', prev.enrollments, false),
            sessions: count(sc, 'sessions', prev.sessions, false),
          };
        });
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
    loadLeaves();
    loadEmergency();
    loadPending();
  }, [loadStats, loadEmergency, loadLeaves, loadPending]);

  // Re-fetch when the app returns to the foreground. iOS suspends the PWA and
  // kills in-flight requests when it's backgrounded; without this the admin
  // reopens the app to a stale/zero dashboard until a manual reload.
  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      loadStats();
      loadLeaves();
      loadEmergency();
      loadPending();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [loadStats, loadEmergency, loadLeaves, loadPending]);

  // Auto-refresh leaves & emergency at midnight
  useEffect(() => {
    const lastDate = { val: new Date().toDateString() };
    const interval = setInterval(() => {
      const today = new Date().toDateString();
      if (today !== lastDate.val) {
        lastDate.val = today;
        loadLeaves();
        loadEmergency();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [loadLeaves, loadEmergency]);

  const today = new Date().toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

  return (
    <>
      <div className="page-header">
        <div><h2>Admin Dashboard</h2><p>Overview of your LMS</p></div>
      </div>
      <div className="page-body">
        <div className="grid-4 mb-2">
          {[
            { icon: '👨‍🎓', label: 'Students',    value: stats.students },
            { icon: '👨‍🏫', label: 'Companions',   value: stats.tutors },
            { icon: '📋', label: 'Enrollments', value: stats.enrollments },
            { icon: '📅', label: 'Sessions',    value: stats.sessions },
          ].map(s => (
            <div className="stat-card" key={s.label}>
              <span className="stat-icon">{s.icon}</span>
              <div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pending Actions Alert */}
        {(pendingQueries > 0 || pendingClassReqs > 0 || pendingMaterialReqs > 0 || pendingVR > 0) && (
          <div className="card" style={{ marginBottom: 20, background:'linear-gradient(135deg,#fff7ed,#ffedd5)', border:'1.5px solid rgba(245,158,11,0.3)' }}>
            <div className="card-title" style={{ color:'#b45309' }}>⚡ Pending Actions</div>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:10 }}>
              {pendingQueries > 0 && (
                <div style={{ flex:'1 1 240px', padding:'14px 18px', background:'#fff', borderRadius:10, border:'1px solid rgba(245,158,11,0.25)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0f1f45', marginBottom:3 }}>❓ Pending Companion Queries</div>
                    <div style={{ fontSize:24, fontWeight:800, color:'#b45309' }}>{pendingQueries}</div>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'cqueries' })); }}
                    style={{ padding:'8px 14px', borderRadius:8, background:'#b45309', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>View →</a>
                </div>
              )}
              {pendingClassReqs > 0 && (
                <div style={{ flex:'1 1 240px', padding:'14px 18px', background:'#fff', borderRadius:10, border:'1px solid rgba(245,158,11,0.25)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0f1f45', marginBottom:3 }}>📋 Pending Class Requests</div>
                    <div style={{ fontSize:24, fontWeight:800, color:'#b45309' }}>{pendingClassReqs}</div>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'classreq' })); }}
                    style={{ padding:'8px 14px', borderRadius:8, background:'#b45309', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>View →</a>
                </div>
              )}
              {pendingMaterialReqs > 0 && (
                <div style={{ flex:'1 1 240px', padding:'14px 18px', background:'#fff', borderRadius:10, border:'1px solid rgba(245,158,11,0.25)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0f1f45', marginBottom:3 }}>📚 Study Material Requests</div>
                    <div style={{ fontSize:24, fontWeight:800, color:'#b45309' }}>{pendingMaterialReqs}</div>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'materials' })); }}
                    style={{ padding:'8px 14px', borderRadius:8, background:'#b45309', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>View →</a>
                </div>
              )}
              {pendingVR > 0 && (
                <div style={{ flex:'1 1 240px', padding:'14px 18px', background:'#fff', borderRadius:10, border:'1px solid rgba(245,158,11,0.25)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0f1f45', marginBottom:3 }}>🕶️ Pending VR Set Requests</div>
                    <div style={{ fontSize:24, fontWeight:800, color:'#b45309' }}>{pendingVR}</div>
                  </div>
                  <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'vr' })); }}
                    style={{ padding:'8px 14px', borderRadius:8, background:'#b45309', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>View →</a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Student Absences to verify */}
        {absences.length > 0 && (
          <div className="card" style={{ marginBottom: 20, background:'linear-gradient(135deg,#eff6ff,#dbeafe)', border:'1.5px solid rgba(37,84,184,0.3)' }}>
            <div className="card-title" style={{ color:'#1e40af', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
              <span>👤 Student Absences — verify</span>
              <span style={{ fontSize:11, fontWeight:600, color:'#1e3a8a', background:'#fff', padding:'3px 10px', borderRadius:20 }}>{absences.length} to review</span>
            </div>
            <p style={{ fontSize:12, color:'#475569', marginTop:6 }}>Companions marked these classes as <strong>Student Absent</strong>. Verify each once confirmed.</p>
            <div style={{ display:'grid', gap:8, marginTop:10, gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {absences.map(a => (
                <div key={a.schedule_id} style={{ padding:'12px 14px', background:'#fff', borderRadius:10, border:'1px solid rgba(37,84,184,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#0f1f45' }}>{a.student_name}</div>
                    <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>{a.subject || '—'} · {a.tutor_name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>📅 {a.date}{a.start_time ? ` · ${a.start_time}–${a.end_time}` : ''}</div>
                  </div>
                  <button onClick={() => verifyAbsence(a.schedule_id)}
                    style={{ padding:'8px 14px', borderRadius:8, background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', border:'none', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                    ✓ Verified
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Schedules Ending Soon */}
        {endingSoon.length > 0 && (
          <div className="card" style={{ marginBottom: 20, background:'linear-gradient(135deg,#fef2f2,#fee2e2)', border:'1.5px solid rgba(239,68,68,0.3)' }}>
            <div className="card-title" style={{ color:'#b91c1c', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
              <span>⏰ Schedules Ending — add more classes</span>
              <span style={{ fontSize:11, fontWeight:600, color:'#7f1d1d', background:'#fff', padding:'3px 10px', borderRadius:20 }}>{endingSoon.length} student{endingSoon.length > 1 ? 's' : ''}</span>
            </div>
            <div style={{ display:'grid', gap:8, marginTop:10, gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {endingSoon.map(s => {
                const isEnded = s.status === 'ended';
                const isNoSched = s.status === 'no_schedule';
                const tag = isNoSched ? 'No schedule yet'
                          : isEnded ? `Ended ${Math.abs(s.days_remaining)}d ago`
                          : s.days_remaining === 0 ? 'Ends today'
                          : `${s.days_remaining}d left`;
                const tagColor = isEnded || isNoSched ? '#b91c1c'
                          : s.days_remaining <= 1 ? '#b91c1c'
                          : '#b45309';
                return (
                  <div key={s.student_id} style={{ padding:'12px 14px', background:'#fff', borderRadius:10, border:'1px solid rgba(239,68,68,0.2)', display:'flex', flexDirection:'column', gap:6 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#0f1f45', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.student_name}</div>
                      <span style={{ fontSize:10, fontWeight:700, color:tagColor, background:'#fef2f2', padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap' }}>{tag}</span>
                    </div>
                    {s.last_scheduled_date && (
                      <div style={{ fontSize:11, color:'#64748b' }}>Last class: <strong>{s.last_scheduled_date}</strong></div>
                    )}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'schedule' })); }}
                      style={{ fontSize:11, color:'#b91c1c', fontWeight:700, textDecoration:'none', marginTop:2 }}>+ Add more classes →</a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Today's Leaves */}
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, flexWrap:'wrap', gap:8 }}>
            <div className="card-title" style={{ margin:0 }}>🗓️ Today's Leave Summary</div>
            <span style={{ fontSize:13, fontWeight:600, color:'#64748b' }}>{today}</span>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            {/* Tutor Leaves */}
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                <span style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>👨‍🏫 Companion Leaves</span>
                {todayLeaves.tutor_leaves.length > 0 && (
                  <span style={{ background:'rgba(220,38,38,0.1)', color:'#dc2626', fontWeight:700, fontSize:11, padding:'2px 9px', borderRadius:20, border:'1px solid rgba(220,38,38,0.2)' }}>
                    {todayLeaves.tutor_leaves.length} on leave
                  </span>
                )}
              </div>
              {todayLeaves.tutor_leaves.length === 0 ? (
                <p style={{ color:'#94a3b8', fontSize:13, background:'#f8faff', borderRadius:10, padding:'14px 16px', margin:0 }}>No Companions on leave today</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {todayLeaves.tutor_leaves.map(l => (
                    <div key={l.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:12, background:'rgba(220,38,38,0.04)', border:'1.5px solid rgba(220,38,38,0.15)' }}>
                      <div style={{ width:38, height:38, borderRadius:10, background:'linear-gradient(135deg,#dc2626,#f87171)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>🏖️</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:800, color:'#0f1f45', fontSize:13 }}>{l.name}</div>
                        <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                          {l.leave_type} · {l.start_date === l.end_date ? l.start_date : `${l.start_date} – ${l.end_date}`}
                        </div>
                        {l.reason && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2, fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>"{l.reason}"</div>}
                      </div>
                      {l.cancelled_classes_today > 0 && (
                        <div style={{ textAlign:'center', flexShrink:0 }}>
                          <div style={{ fontWeight:800, color:'#dc2626', fontSize:18, lineHeight:1 }}>{l.cancelled_classes_today}</div>
                          <div style={{ fontSize:10, color:'#dc2626', fontWeight:600 }}>cancelled</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Student Leaves */}
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                <span style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>👨‍🎓 Student Leaves</span>
                {todayLeaves.student_leaves.length > 0 && (
                  <span style={{ background:'rgba(124,58,237,0.1)', color:'#7c3aed', fontWeight:700, fontSize:11, padding:'2px 9px', borderRadius:20, border:'1px solid rgba(124,58,237,0.2)' }}>
                    {todayLeaves.student_leaves.length} on leave
                  </span>
                )}
              </div>
              {todayLeaves.student_leaves.length === 0 ? (
                <p style={{ color:'#94a3b8', fontSize:13, background:'#f8faff', borderRadius:10, padding:'14px 16px', margin:0 }}>No students on leave today</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {todayLeaves.student_leaves.map(l => (
                    <div key={l.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:12, background:'rgba(124,58,237,0.04)', border:'1.5px solid rgba(124,58,237,0.15)' }}>
                      <div style={{ width:38, height:38, borderRadius:10, background:'linear-gradient(135deg,#7c3aed,#a855f7)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>📝</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:800, color:'#0f1f45', fontSize:13 }}>{l.name}</div>
                        <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                          {l.leave_type} · {l.start_date === l.end_date ? l.start_date : `${l.start_date} – ${l.end_date}`}
                        </div>
                        {l.reason && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2, fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>"{l.reason}"</div>}
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, background: l.status==='pending' ? 'rgba(217,119,6,0.1)' : 'rgba(22,163,74,0.1)', color: l.status==='pending' ? '#d97706' : '#16a34a', border:`1px solid ${l.status==='pending' ? 'rgba(217,119,6,0.25)' : 'rgba(22,163,74,0.25)'}`, flexShrink:0 }}>{l.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Emergency Leaves Section */}
        {emergencyList.length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <span style={{ fontSize:20 }}>🚨</span>
              <div className="card-title" style={{ margin:0 }}>Emergency Cancellations Today</div>
              <span style={{ background:'rgba(220,38,38,0.1)', color:'#dc2626', fontWeight:700, fontSize:11, padding:'2px 9px', borderRadius:20, border:'1px solid rgba(220,38,38,0.2)' }}>
                {emergencyList.length} today
              </span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {emergencyList.map(em => (
                <div key={em.id} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', borderRadius:12, background:'rgba(220,38,38,0.04)', border:'1.5px solid rgba(220,38,38,0.15)' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:800, color:'#0f1f45', fontSize:13 }}>{em.user_name}</div>
                    <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                      {em.role} · {em.subject ? `${em.subject} · ` : ''}{em.reason || 'No reason provided'}
                    </div>
                    {em.schedule_date && (
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>
                        Class: {em.schedule_date} {em.schedule_start_time ? fmtTime(em.schedule_start_time) : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                    {(em.status === 'pending') && (
                      <>
                        <button className="btn"
                          style={{ background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', border:'none', borderRadius:8, padding:'5px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                          onClick={async () => {
                            try { await API.patch(`/emergency/${em.id}`, { action: 'accept' }); loadEmergency(); }
                            catch (e) { alert(e.response?.data?.detail || 'Error'); }
                          }}>✓ Accept</button>
                        <button className="btn"
                          style={{ background:'none', border:'1px solid #fca5a5', color:'#dc2626', borderRadius:8, padding:'5px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                          onClick={async () => {
                            if (!window.confirm(`Reject and reinstate class for ${em.user_name}?`)) return;
                            try { await API.patch(`/emergency/${em.id}`, { action: 'reject' }); loadEmergency(); }
                            catch (e) { alert(e.response?.data?.detail || 'Error'); }
                          }}>✗ Reject</button>
                      </>
                    )}
                    {em.status !== 'pending' && (
                      <span style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:20,
                        background: em.status === 'accepted' ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
                        color: em.status === 'accepted' ? '#16a34a' : '#dc2626',
                        border: `1px solid ${em.status === 'accepted' ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.25)'}` }}>
                        {em.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Users ──────────────────────────────────────────────────────────────────────
function Users({ canViewProfit = false }) {
  const [users, setUsers]             = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [roleFilter, setRoleFilter]   = useState('all');
  const [modeFilter, setModeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]           = useState('');
  const [editUser, setEditUser]         = useState(null);
  const [viewUser, setViewUser]         = useState(null);
  const [editForm, setEditForm]         = useState({});
  const [editMsg, setEditMsg]           = useState('');
  const [editLoading, setEditLoading]   = useState(false);
  const [classes, setClasses]           = useState([]);
  const [allSubjects, setAllSubjects]   = useState([]);
  const [editSelectedSubjects, setEditSelectedSubjects] = useState([]);
  const [availability, setAvailability] = useState({}); // { tutor_id: [slots] }
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [selectedTutors, setSelectedTutors] = useState([]);
  const [bulkPw, setBulkPw] = useState('');
  const [bulkPwMsg, setBulkPwMsg] = useState('');
  const [bulkPwLoading, setBulkPwLoading] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkStudentPw, setBulkStudentPw] = useState('');
  const [bulkStudentPwMsg, setBulkStudentPwMsg] = useState('');
  const [bulkStudentPwLoading, setBulkStudentPwLoading] = useState(false);

  const load = useCallback(() => {
    API.get('/auth/users').then(r => {
      setUsers(r.data);
      // Fetch availability for every tutor
      const tutors = r.data.filter(u => u.role === 'tutor');
      Promise.all(tutors.map(t => API.get(`/availability/${t.id}`).then(res => ({ id: t.id, slots: res.data })).catch(() => ({ id: t.id, slots: [] }))))
        .then(results => {
          const map = {};
          results.forEach(({ id, slots }) => { map[id] = slots; });
          setAvailability(map);
        });
    }).catch(() => {});
    API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    API.get('/classes/').then(r => setClasses(r.data)).catch(() => {});
    API.get('/subjects/').then(r => setAllSubjects(r.data)).catch(() => {});
  }, [load]);

  const fmt = fmtTime;

  // Group slots by day for a tutor
  const getTutorAvail = (tutorId) => {
    const slots = availability[tutorId] || [];
    const grouped = {};
    slots.forEach(s => {
      if (!grouped[s.day]) grouped[s.day] = [];
      grouped[s.day].push(`${fmt(s.start_time)} – ${fmt(s.end_time)}`);
    });
    return grouped;
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      uid: user.uid || '',
      first_name: user.first_name || '',
      middle_name: user.middle_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      alt_email: user.alt_email || '',
      phone: user.phone || '',
      whatsapp1: user.whatsapp1 || '',
      whatsapp2: user.whatsapp2 || '',
      gender: user.gender || '',
      dob: user.dob || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      pincode: user.pincode || '',
      class_name: user.class_name || '',
      syllabus: user.syllabus || '',
      package: user.package || '',
      pricing_plan: user.pricing_plan || '',
      mode: user.mode || '',
      is_active: user.is_active || 'active',
      pause_reason: user.pause_reason || '',
      hours_per_month: user.hours_per_month || '',
      timezone: user.timezone || 'Asia/Kolkata',
      password: '',
    });
    setEditSelectedSubjects(user.selected_subjects ? user.selected_subjects.split(',').map(s => s.trim()).filter(Boolean) : []);
    setEditMsg('');
  };

  const closeEdit = () => { setEditUser(null); setEditForm({}); setEditSelectedSubjects([]); setEditMsg(''); };

  const ef = (label, key, type = 'text') => {
    const maxLen = key === 'uid' ? 10 : key === 'pincode' ? 6 : key === 'phone' ? 10 : (key === 'whatsapp1' || key === 'whatsapp2') ? 15 : undefined;
    return (
      <div className="form-group">
        <label style={{ fontSize: '13px' }}>{label}</label>
        <input className="form-control" type={type} value={editForm[key] || ''}
          maxLength={maxLen}
          onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} />
      </div>
    );
  };

  const toggleClassLogUnlock = async (tutorId, newUnlock) => {
    try {
      await API.patch(`/class/tutor-unlock/${tutorId}?unlock=${newUnlock}`);
      setUsers(prev => prev.map(u => u.id === tutorId ? { ...u, class_log_unlock: newUnlock } : u));
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Failed to update lock status');
    }
  };

  const saveEdit = async () => {
    if (editForm.is_active === 'paused' && !(editForm.pause_reason || '').trim()) {
      setEditMsg('Please enter a reason for pausing this student.');
      return;
    }
    setEditLoading(true);
    setEditMsg('');
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      // Convert empty strings to null so Pydantic accepts them
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.hours_per_month !== null && payload.hours_per_month !== undefined)
        payload.hours_per_month = parseInt(payload.hours_per_month) || null;
      // Strip the profit fields if this admin doesn't have the gate (the server
      // would ignore them anyway, but cleaner not to send them).
      if (!canViewProfit) { delete payload.profit_per_hour; delete payload.profit_per_hour_online; delete payload.profit_per_hour_offline; }
      if (editUser.role === 'tutor' || editUser.role === 'student')
        payload.selected_subjects = editSelectedSubjects.join(',');
      await API.patch(`/auth/users/${editUser.id}`, payload);
      setEditMsg('✓ Saved successfully!');
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setEditMsg(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Update failed'));
    }
    setEditLoading(false);
  };

  // Mode filter: students carry their own mode; tutors match via any active
  // enrollment in that mode. Admins are unaffected.
  const matchMode = (u) => {
    if (modeFilter === 'all') return true;
    if (u.role === 'student') return (u.mode || '').toLowerCase() === modeFilter;
    if (u.role === 'tutor') return enrollments.some(e => e.tutor_id === u.id && e.mode === modeFilter && e.status !== 'inactive');
    return true;
  };
  const matchStatus = (u) => {
    if (statusFilter === 'all') return true;
    const st = u.is_active || 'active';
    if (statusFilter === 'active') return st !== 'inactive' && st !== 'paused';
    return st === statusFilter;  // 'inactive' | 'paused'
  };

  const statusBadge = (u) => {
    const st = u.is_active || 'active';
    if (st === 'inactive') return <span className="badge badge-cancelled">Inactive</span>;
    if (st === 'paused') return (
      <span className="badge" style={{ background:'#fef3c7', color:'#b45309', border:'1px solid #fde68a' }}
        title={u.pause_reason ? `Paused: ${u.pause_reason}` : 'Paused'}>⏸ Paused</span>
    );
    return <span className="badge badge-completed">Active</span>;
  };

  const filtered = users
    .filter(u => roleFilter === 'all' || u.role === roleFilter)
    .filter(u => u.name.toLowerCase().includes(search.toLowerCase()))
    .filter(matchMode)
    .filter(matchStatus)
    .sort((a, b) => a.name.localeCompare(b.name));

  // helpers to get enrollment data per user (exclude inactive)
  const assignedStudents = (tutorId) => {
    const ids = [...new Set(enrollments.filter(e => e.tutor_id === tutorId && e.status !== 'inactive').map(e => e.student_id))];
    return users.filter(u => ids.includes(u.id)).map(u => u.name);
  };

  const assignedTutorsSubjects = (studentId) => {
    const rows = enrollments.filter(e => e.student_id === studentId && e.status !== 'inactive');
    return rows.map(e => ({ tutor: e.tutor_name || '—', subject: e.subject }));
  };

  const TABS = ['all', 'student', 'tutor', 'admin'];

  return (
    <>
      <div className="page-header">
        <div><h2>Users</h2><p>Manage students, Companions and admins</p></div>
      </div>
      <div className="page-body">

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {TABS.map(tab => (
              <button key={tab} onClick={() => setRoleFilter(tab)}
                className={`btn ${roleFilter === tab ? 'btn-primary' : 'btn-ghost'}`}
                style={{ textTransform: 'capitalize', padding: '6px 16px' }}>
                {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1) + 's'}
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', maxWidth: '260px' }}>
            <input
              className="form-control"
              placeholder="Search by name..."
              value={search}
              autoComplete="off"
              onChange={e => { setSearch(e.target.value); setShowUserSuggestions(true); }}
              onFocus={() => setShowUserSuggestions(true)}
              onBlur={() => setTimeout(() => setShowUserSuggestions(false), 180)}
              style={{ width: '100%' }}
            />
            {showUserSuggestions && search && (() => {
              const matches = users
                .filter(u => (roleFilter === 'all' || u.role === roleFilter) && u.name.toLowerCase().includes(search.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name));
              if (!matches.length) return null;
              return (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #dbe1f0', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:200, maxHeight:220, overflowY:'auto' }}>
                  {matches.map(u => (
                    <div key={u.id} onMouseDown={() => { setSearch(u.name); setShowUserSuggestions(false); }}
                      style={{ padding:'9px 14px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                      onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                      onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                      <span><strong>{u.name}</strong></span>
                      <span style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize', background:'#f1f5f9', borderRadius:4, padding:'2px 7px' }}>{u.role}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Mode filter (students + tutors) */}
          {roleFilter !== 'admin' && (
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <span style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase' }}>Mode</span>
              {[['all','All'],['online','🌐 Online'],['offline','🏫 Offline']].map(([val, label]) => (
                <button key={val} onClick={() => setModeFilter(val)}
                  style={{ padding:'4px 12px', borderRadius:20, border:'1.5px solid', fontSize:12, fontWeight:700, cursor:'pointer',
                    borderColor: modeFilter === val ? '#2554b8' : '#e2e8f0',
                    background: modeFilter === val ? '#2554b8' : '#fff',
                    color: modeFilter === val ? '#fff' : '#64748b' }}>{label}</button>
              ))}
            </div>
          )}

          {/* Active / Inactive status filter (students + tutors) */}
          {roleFilter !== 'admin' && (
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <span style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase' }}>Status</span>
              {[['all','All'],['active','Active'],['paused','Paused'],['inactive','Inactive']].map(([val, label]) => (
                <button key={val} onClick={() => setStatusFilter(val)}
                  style={{ padding:'4px 12px', borderRadius:20, border:'1.5px solid', fontSize:12, fontWeight:700, cursor:'pointer',
                    borderColor: statusFilter === val ? '#16a34a' : '#e2e8f0',
                    background: statusFilter === val ? '#16a34a' : '#fff',
                    color: statusFilter === val ? '#fff' : '#64748b' }}>{label}</button>
              ))}
            </div>
          )}
        </div>

        {/* Tutor table */}
        {(roleFilter === 'all' || roleFilter === 'tutor') && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div className="card-title" style={{ margin:0 }}>Companions ({filtered.filter(u => u.role === 'tutor').length})</div>
              </div>
              {selectedTutors.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:13, color:'#64748b' }}>{selectedTutors.length} selected</span>
                  <input type="password" placeholder="New password" value={bulkPw} onChange={e => setBulkPw(e.target.value)}
                    style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, width:170, outline:'none' }} />
                  <button className="btn btn-primary btn-sm" disabled={bulkPwLoading}
                    onClick={async () => {
                      if (!bulkPw || bulkPw.length < 6) { setBulkPwMsg('Min. 6 characters'); return; }
                      if (!window.confirm(`Reset password for ${selectedTutors.length} companion(s)? They will be forced to change it on next login.`)) return;
                      setBulkPwLoading(true); setBulkPwMsg('');
                      try {
                        const res = await API.post('/auth/bulk-reset-password', { tutor_ids: selectedTutors, new_password: bulkPw });
                        setBulkPwMsg(`✅ ${res.data.message}`);
                        setSelectedTutors([]); setBulkPw('');
                      } catch(e) { setBulkPwMsg(e?.response?.data?.detail || 'Failed'); }
                      finally { setBulkPwLoading(false); }
                    }}>
                    {bulkPwLoading ? 'Resetting…' : '🔑 Reset Password'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedTutors([]); setBulkPw(''); setBulkPwMsg(''); }}>Clear</button>
                </div>
              )}
            </div>
            {bulkPwMsg && <div style={{ fontSize:13, color: bulkPwMsg.startsWith('✅') ? '#16a34a' : '#dc2626', marginBottom:10 }}>{bulkPwMsg}</div>}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:36 }}>
                      <input type="checkbox"
                        checked={filtered.filter(u => u.role === 'tutor').length > 0 && filtered.filter(u => u.role === 'tutor').every(u => selectedTutors.includes(u.id))}
                        onChange={e => {
                          const ids = filtered.filter(u => u.role === 'tutor').map(u => u.id);
                          setSelectedTutors(e.target.checked ? ids : []);
                        }} />
                    </th>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Availability</th>
                    <th>Assigned Students</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const visibleTutors = filtered.filter(u => u.role === 'tutor');
                    if (visibleTutors.length === 0) return (
                      <tr><td colSpan="7"><div className="empty-state"><div className="empty-icon">👨‍🏫</div><p>No Companions found</p></div></td></tr>
                    );
                    return visibleTutors.map(u => {
                    const students = assignedStudents(u.id);
                    const avail = getTutorAvail(u.id);
                    const days = Object.keys(avail);
                    return (
                      <tr key={u.id}>
                        <td><input type="checkbox" checked={selectedTutors.includes(u.id)}
                          onChange={e => setSelectedTutors(e.target.checked ? [...selectedTutors, u.id] : selectedTutors.filter(id => id !== u.id))} /></td>
                        <td className="mono text-muted">{u.uid || '—'}</td>
                        <td style={{ fontWeight: 500 }}>{u.name}</td>
                        <td>{statusBadge(u)}</td>
                        <td>
                          {days.length === 0 ? <span className="text-muted">—</span> : (
                            <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                              {days.map(day => (
                                <div key={day}>
                                  <span style={{ fontWeight: 600, minWidth: '36px', display: 'inline-block' }}>{day.slice(0, 3)}</span>
                                  <span className="text-muted"> {avail[day].join(', ')}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {students.length === 0 ? <span className="text-muted">—</span>
                            : students.map((s, i) => <span key={i} className="badge badge-scheduled" style={{ marginRight: '4px' }}>{s}</span>)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '13px', marginRight: 6 }} onClick={() => setViewUser(u)}>👁 View</button>
                          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '13px', marginRight: 6 }} onClick={() => openEdit(u)}>✏ Edit</button>
                          <button
                            style={{ padding: '3px 9px', fontSize: 11, fontWeight: 700, borderRadius: 5, border: 'none', cursor: 'pointer', background: u.class_log_unlock ? '#fef3c7' : '#f1f5f9', color: u.class_log_unlock ? '#b45309' : '#64748b' }}
                            title={u.class_log_unlock ? 'Class log editing is unlocked — click to lock' : 'Class log editing is locked to 6h window — click to unlock'}
                            onClick={() => toggleClassLogUnlock(u.id, !u.class_log_unlock)}>
                            {u.class_log_unlock ? '🔓 Log Unlocked' : '🔒 Log Locked'}
                          </button>
                        </td>
                      </tr>
                    );
                  });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Student table */}
        {(roleFilter === 'all' || roleFilter === 'student') && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:14 }}>
              <div className="card-title" style={{ margin:0 }}>Students ({filtered.filter(u => u.role === 'student').length})</div>
              {selectedStudents.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:13, color:'#64748b' }}>{selectedStudents.length} selected</span>
                  <input type="password" placeholder="New password" value={bulkStudentPw} onChange={e => setBulkStudentPw(e.target.value)}
                    style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, width:170, outline:'none' }} />
                  <button className="btn btn-primary btn-sm" disabled={bulkStudentPwLoading}
                    onClick={async () => {
                      if (!bulkStudentPw || bulkStudentPw.length < 6) { setBulkStudentPwMsg('Min. 6 characters'); return; }
                      if (!window.confirm(`Reset password for ${selectedStudents.length} student(s)? They will be forced to change it on next login.`)) return;
                      setBulkStudentPwLoading(true); setBulkStudentPwMsg('');
                      try {
                        const res = await API.post('/auth/bulk-reset-password-students', { student_ids: selectedStudents, new_password: bulkStudentPw });
                        setBulkStudentPwMsg(`✅ ${res.data.message}`);
                        setSelectedStudents([]); setBulkStudentPw('');
                      } catch(e) { setBulkStudentPwMsg(e?.response?.data?.detail || 'Failed'); }
                      finally { setBulkStudentPwLoading(false); }
                    }}>
                    {bulkStudentPwLoading ? 'Resetting…' : '🔑 Reset Password'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedStudents([]); setBulkStudentPw(''); setBulkStudentPwMsg(''); }}>Clear</button>
                </div>
              )}
            </div>
            {bulkStudentPwMsg && <div style={{ fontSize:13, color: bulkStudentPwMsg.startsWith('✅') ? '#16a34a' : '#dc2626', marginBottom:10 }}>{bulkStudentPwMsg}</div>}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:36 }}>
                      <input type="checkbox"
                        checked={filtered.filter(u => u.role === 'student').length > 0 && filtered.filter(u => u.role === 'student').every(u => selectedStudents.includes(u.id))}
                        onChange={e => {
                          const ids = filtered.filter(u => u.role === 'student').map(u => u.id);
                          setSelectedStudents(e.target.checked ? ids : []);
                        }} />
                    </th>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th>Syllabus</th>
                    <th>Mode</th>
                    <th>Status</th>
                    <th>Assigned Companions & Subjects</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.filter(u => u.role === 'student').length === 0 ? (
                    <tr><td colSpan="9"><div className="empty-state"><div className="empty-icon">🎓</div><p>No students found</p></div></td></tr>
                  ) : filtered.filter(u => u.role === 'student').map(u => {
                    const tutorSubjects = assignedTutorsSubjects(u.id);
                    return (
                      <tr key={u.id}>
                        <td><input type="checkbox" checked={selectedStudents.includes(u.id)}
                          onChange={e => setSelectedStudents(e.target.checked ? [...selectedStudents, u.id] : selectedStudents.filter(id => id !== u.id))} /></td>
                        <td className="mono text-muted">{u.uid || '—'}</td>
                        <td style={{ fontWeight: 500 }}>{u.name}</td>
                        <td>{u.class_name || '—'}</td>
                        <td>{u.syllabus ? <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#eef2ff', color:'#4338ca' }}>{u.syllabus}</span> : <span className="text-muted">—</span>}</td>
                        <td>
                          {u.mode ? (
                            <span className={`badge ${u.mode === 'online' ? 'badge-completed' : 'badge-scheduled'}`}>
                              {u.mode.charAt(0).toUpperCase() + u.mode.slice(1)}
                            </span>
                          ) : '—'}
                        </td>
                        <td>{statusBadge(u)}</td>
                        <td>
                          {tutorSubjects.length === 0 ? <span className="text-muted">—</span>
                            : tutorSubjects.map((ts, i) => (
                              <div key={i} style={{ fontSize: '12px', marginBottom: '2px' }}>
                                <span style={{ fontWeight: 500 }}>{ts.tutor}</span>
                                <span className="text-muted"> — {ts.subject}</span>
                              </div>
                            ))}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '13px', marginRight: 6 }} onClick={() => setViewUser(u)}>👁 View</button>
                          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '13px' }} onClick={() => openEdit(u)}>✏ Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Admin table */}
        {(roleFilter === 'all' || roleFilter === 'admin') && (
          <div className="card">
            <div className="card-title">Admins ({filtered.filter(u => u.role === 'admin').length})</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th></th></tr></thead>
                <tbody>
                  {filtered.filter(u => u.role === 'admin').length === 0 ? (
                    <tr><td colSpan="4"><div className="empty-state"><div className="empty-icon">🔑</div><p>No admins found</p></div></td></tr>
                  ) : filtered.filter(u => u.role === 'admin').map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 500 }}>{u.name}</td>
                      <td className="mono text-muted">{u.email}</td>
                      <td className="text-muted">{u.phone || '—'}</td>
                      <td><button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '13px' }} onClick={() => setViewUser(u)}>👁 View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* ── Edit User Modal ── */}
      {editUser && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Edit {editUser.role.charAt(0).toUpperCase() + editUser.role.slice(1)}: {editUser.name}</h3>
              <button className="modal-close" onClick={closeEdit}>×</button>
            </div>

            {editMsg && (
              <div className={`alert ${String(editMsg).startsWith('✓') ? 'alert-success' : 'alert-error'}`}
                style={{ margin: '0 0 12px' }} onClick={() => setEditMsg('')}>{editMsg}</div>
            )}

            {/* Status */}
            <div className="form-group">
              <label style={{ fontSize: '13px' }}>Status</label>
              <select className="form-control" value={editForm.is_active || 'active'}
                onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value }))}>
                <option value="active">Active</option>
                {editUser.role === 'student' && <option value="paused">Paused (exams / vacation)</option>}
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {editForm.is_active === 'paused' && (
              <div className="form-group">
                <label style={{ fontSize: '13px' }}>Pause Reason <span style={{ color:'#dc2626' }}>*</span></label>
                <textarea className="form-control" rows={2}
                  placeholder="e.g. Final exams until 15 Sep — resume after"
                  value={editForm.pause_reason || ''}
                  onChange={e => setEditForm(f => ({ ...f, pause_reason: e.target.value }))} />
                <div style={{ fontSize: 11, color:'#94a3b8', marginTop:4 }}>Paused students are hidden from the timetable and get no class reminders. Set back to Active to resume.</div>
              </div>
            )}

            {/* Personal */}
            <div style={{ fontWeight: 600, fontSize: '13px', margin: '12px 0 8px', color: 'var(--color-accent)' }}>Personal Information</div>
            <div className="grid-2">{ef('User ID', 'uid')}{ef('First Name', 'first_name')}</div>
            <div className="grid-2">{ef('Middle Name', 'middle_name')}{ef('Last Name', 'last_name')}</div>
            <div className="grid-2">{ef('Email', 'email', 'email')}{ef('Alternative Email', 'alt_email', 'email')}</div>
            <div className="grid-2">{ef('Phone', 'phone', 'tel')}{ef('Date of Birth', 'dob', 'date')}</div>
            <div className="grid-2">{ef('WhatsApp Number 1', 'whatsapp1', 'tel')}{editUser.role === 'student' && ef('WhatsApp Number 2', 'whatsapp2', 'tel')}</div>
            <div className="grid-2">
              <div className="form-group">
                <label style={{ fontSize: '13px' }}>Timezone</label>
                <select className="form-control" value={editForm.timezone || 'Asia/Kolkata'} onChange={e => setEditForm(f => ({ ...f, timezone: e.target.value }))}>
                  {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label style={{ fontSize: '13px' }}>Gender</label>
              <select className="form-control" value={editForm.gender || ''}
                onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}>
                <option value="">Select Gender</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </div>
            <div className="grid-2">{ef('Address', 'address')}{ef('City', 'city')}</div>
            <div className="grid-2">{ef('State', 'state')}{ef('Pincode', 'pincode')}</div>

            {/* Student-only fields */}
            {editUser.role === 'student' && (
              <>
                <div style={{ fontWeight: 600, fontSize: '13px', margin: '12px 0 8px', color: 'var(--color-accent)' }}>Academic Information</div>
                <div className="grid-2">
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Class</label>
                    <select className="form-control" value={editForm.class_name || ''}
                      onChange={e => setEditForm(f => ({ ...f, class_name: e.target.value }))}>
                      <option value="">Select Class</option>
                      {classes.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Mode</label>
                    <select className="form-control" value={editForm.mode || ''}
                      onChange={e => setEditForm(f => ({ ...f, mode: e.target.value }))}>
                      <option value="">Select Mode</option>
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                    </select>
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Pricing Plan</label>
                    <select className="form-control" value={editForm.pricing_plan || ''}
                      onChange={e => setEditForm(f => ({ ...f, pricing_plan: e.target.value }))}>
                      <option value="">Select Plan</option>
                      {PRICING_PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  {ef('Package', 'package')}
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Hours per Month</label>
                    <input className="form-control" type="number" min="1"
                      placeholder="e.g. 20"
                      value={editForm.hours_per_month || ''}
                      onChange={e => setEditForm(f => ({ ...f, hours_per_month: e.target.value ? parseInt(e.target.value) : null }))} />
                  </div>
                  {canViewProfit && (
                    <div className="form-group">
                      <label style={{ fontSize: '13px' }}>Profit / Hour — Online (₹) <span style={{ fontSize:10, color:'#94a3b8', fontWeight:500 }}>internal</span></label>
                      <input className="form-control" type="number" step="0.01" min="0"
                        placeholder="e.g. 250"
                        value={editForm.profit_per_hour_online == null ? '' : editForm.profit_per_hour_online}
                        onChange={e => setEditForm(f => ({ ...f, profit_per_hour_online: e.target.value === '' ? null : parseFloat(e.target.value) }))} />
                    </div>
                  )}
                  {canViewProfit && (
                    <div className="form-group">
                      <label style={{ fontSize: '13px' }}>Profit / Hour — Offline (₹) <span style={{ fontSize:10, color:'#94a3b8', fontWeight:500 }}>internal</span></label>
                      <input className="form-control" type="number" step="0.01" min="0"
                        placeholder="e.g. 300"
                        value={editForm.profit_per_hour_offline == null ? '' : editForm.profit_per_hour_offline}
                        onChange={e => setEditForm(f => ({ ...f, profit_per_hour_offline: e.target.value === '' ? null : parseFloat(e.target.value) }))} />
                    </div>
                  )}
                </div>
                <div className="grid-2">
                  <SyllabusPicker value={editForm.syllabus || ''} onChange={v => setEditForm(f => ({ ...f, syllabus: v }))} />
                </div>

                {/* Student Subjects */}
                <div style={{ fontWeight: 600, fontSize: '13px', margin: '12px 0 8px', color: 'var(--color-accent)' }}>Subjects (Enrolled)</div>
                {(() => {
                  const uniqueNames = [...new Set(allSubjects.map(s => s.name))];
                  editSelectedSubjects.forEach(n => { if (!uniqueNames.includes(n)) uniqueNames.push(n); });
                  return uniqueNames.length === 0
                    ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No subjects found. Add subjects first.</p>
                    : <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 0' }}>
                          {uniqueNames.map(name => {
                            const isSelected = editSelectedSubjects.includes(name);
                            return (
                              <span key={name}
                                onClick={() => setEditSelectedSubjects(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name])}
                                style={{ padding: '6px 16px', borderRadius: 20, border: `2px solid ${isSelected ? '#2554b8' : '#e2e8f6'}`, background: isSelected ? '#2554b8' : '#f8faff', color: isSelected ? '#fff' : '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none' }}>
                                {isSelected ? '✓ ' : ''}{name}
                              </span>
                            );
                          })}
                        </div>
                        {editSelectedSubjects.length > 0 && (
                          <p style={{ fontSize: 12, color: '#2554b8', marginTop: 4, fontWeight: 600 }}>
                            ✓ Selected: {editSelectedSubjects.join(', ')}
                          </p>
                        )}
                      </>;
                })()}
              </>
            )}

            {/* Tutor-only: Subjects */}
            {editUser.role === 'tutor' && (() => {
              // Deduplicate by name, then merge with any pre-selected names not in the list
              const uniqueNames = [...new Set(allSubjects.map(s => s.name))];
              editSelectedSubjects.forEach(n => { if (!uniqueNames.includes(n)) uniqueNames.push(n); });
              return (
                <>
                  <div style={{ fontWeight: 600, fontSize: '13px', margin: '12px 0 8px', color: 'var(--color-accent)' }}>Subjects (Teaching)</div>
                  {uniqueNames.length === 0
                    ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No subjects found. Add subjects first.</p>
                    : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 0' }}>
                        {uniqueNames.map(name => {
                          const isSelected = editSelectedSubjects.includes(name);
                          return (
                            <span key={name}
                              onClick={() => setEditSelectedSubjects(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name])}
                              style={{ padding: '6px 16px', borderRadius: 20, border: `2px solid ${isSelected ? '#2554b8' : '#e2e8f6'}`, background: isSelected ? '#2554b8' : '#f8faff', color: isSelected ? '#fff' : '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none' }}>
                              {isSelected ? '✓ ' : ''}{name}
                            </span>
                          );
                        })}
                      </div>
                  }
                  {editSelectedSubjects.length > 0 && (
                    <p style={{ fontSize: 12, color: '#2554b8', marginTop: 4, fontWeight: 600 }}>
                      ✓ Selected: {editSelectedSubjects.join(', ')}
                    </p>
                  )}
                </>
              );
            })()}

            {/* Password reset */}
            <div style={{ fontWeight: 600, fontSize: '13px', margin: '12px 0 8px', color: 'var(--color-accent)' }}>Reset Password</div>
            <div className="form-group">
              <label style={{ fontSize: '13px' }}>New Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank to keep current)</span></label>
              <input className="form-control" type="password" value={editForm.password || ''}
                onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min 8 characters" />
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeEdit}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editLoading}>
                {editLoading ? 'Saving...' : '✓ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View User Modal (read-only) ── */}
      {viewUser && (
        <div className="modal-overlay" onClick={() => setViewUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>{viewUser.role === 'tutor' ? 'Companion' : viewUser.role.charAt(0).toUpperCase() + viewUser.role.slice(1)}: {viewUser.name}</h3>
              <button className="modal-close" onClick={() => setViewUser(null)}>×</button>
            </div>

            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
              {statusBadge(viewUser)}
              {viewUser.uid && <span className="badge badge-scheduled">ID {viewUser.uid}</span>}
              {viewUser.mode && <span className={`badge ${viewUser.mode === 'online' ? 'badge-completed' : 'badge-scheduled'}`}>{viewUser.mode}</span>}
            </div>
            {viewUser.is_active === 'paused' && viewUser.pause_reason && (
              <div className="alert" style={{ background:'#fffbeb', border:'1px solid #fde68a', color:'#b45309', marginBottom:14 }}>
                ⏸ <strong>Paused:</strong> {viewUser.pause_reason}
              </div>
            )}

            {(() => {
              const rows = [
                ['Full Name', viewUser.name],
                ['Email', viewUser.email],
                ['Alt Email', viewUser.alt_email],
                ['Phone', viewUser.phone],
                ['WhatsApp 1', viewUser.whatsapp1],
                ['WhatsApp 2', viewUser.whatsapp2],
                ['Gender', viewUser.gender],
                ['Date of Birth', viewUser.dob],
                ['Timezone', viewUser.timezone],
                ['Address', [viewUser.address, viewUser.city, viewUser.state, viewUser.pincode].filter(Boolean).join(', ')],
              ];
              if (viewUser.role === 'student') {
                rows.push(
                  ['Class', viewUser.class_name],
                  ['Syllabus', viewUser.syllabus],
                  ['Pricing Plan', viewUser.pricing_plan],
                  ['Package', viewUser.package],
                  ['Hours / Month', viewUser.hours_per_month],
                );
              }
              if (viewUser.role === 'tutor') {
                rows.push(['Subjects', viewUser.selected_subjects]);
              }
              return (
                <div style={{ display:'grid', gridTemplateColumns:'150px 1fr', rowGap:8, columnGap:12, fontSize:13 }}>
                  {rows.map(([label, val], i) => (
                    <React.Fragment key={i}>
                      <div style={{ color:'#64748b', fontWeight:600 }}>{label}</div>
                      <div style={{ color:'#1e293b', wordBreak:'break-word' }}>{val || '—'}</div>
                    </React.Fragment>
                  ))}
                </div>
              );
            })()}

            {viewUser.role === 'student' && <StudentComments studentId={viewUser.id} />}

            <div className="modal-footer" style={{ marginTop:18 }}>
              <button className="btn btn-ghost" onClick={() => setViewUser(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { const u = viewUser; setViewUser(null); openEdit(u); }}>✏ Edit</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Admin comments about a student (visible to all admins) ─────────────────────
function StudentComments({ studentId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const myName = localStorage.getItem('name') || '';

  const load = useCallback(() => {
    API.get(`/student-notes/${studentId}`).then(r => setNotes(r.data)).catch(() => {});
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const r = await API.post(`/student-notes/${studentId}`, { note: text.trim() });
      setNotes(prev => [r.data, ...prev]);
      setText('');
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add comment'); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try { await API.delete(`/student-notes/${id}`); setNotes(prev => prev.filter(n => n.id !== id)); }
    catch (e) { alert('Failed to delete'); }
  };

  const fmt = (ts) => { try { return new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleString([], { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return ''; } };

  return (
    <div style={{ marginTop:20, borderTop:'1px solid #e8edf8', paddingTop:16 }}>
      <div style={{ fontWeight:800, fontSize:14, color:'#0f1f45', marginBottom:4 }}>🗒 Admin Comments</div>
      <div style={{ fontSize:11.5, color:'#94a3b8', marginBottom:10 }}>Internal notes visible to all admins — not shown to the student or companions.</div>
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <textarea className="form-control" rows={2} placeholder="Add a comment about this student…"
          value={text} onChange={e => setText(e.target.value)} style={{ flex:1, fontSize:13 }} />
        <button className="btn btn-primary" onClick={add} disabled={saving || !text.trim()} style={{ alignSelf:'flex-start', whiteSpace:'nowrap' }}>
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
      {notes.length === 0 ? (
        <div style={{ fontSize:13, color:'#94a3b8' }}>No comments yet.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {notes.map(n => (
            <div key={n.id} style={{ background:'#f8faff', border:'1px solid #eef2fb', borderRadius:10, padding:'10px 12px' }}>
              <div style={{ fontSize:13, color:'#1e293b', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{n.note}</div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:6 }}>
                <span style={{ fontSize:11, color:'#64748b', fontWeight:600 }}>{n.admin_name} · {fmt(n.created_at)}</span>
                <button onClick={() => remove(n.id)} title="Delete comment"
                  style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:700 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Classes ────────────────────────────────────────────────────────────────────
const CLASS_PRESETS = [
  'Grade 1','Grade 2','Grade 3','Grade 4','Grade 5','Grade 6',
  'Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12','Others',
];

function ClassManager() {
  const [classes, setClasses] = useState([]);
  const [preset, setPreset] = useState('');
  const [customName, setCustomName] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    API.get('/classes/').then(r => setClasses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = preset === 'Others' ? customName.trim() : preset;
    if (!name) { setMsg('Please select a class'); return; }
    try {
      await API.post('/classes/', { name });
      setMsg('✓ Class created!');
      setPreset(''); setCustomName('');
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error creating class');
    }
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Classes</h2><p>Grade 1 – Grade 12 are pre-loaded. Add more if needed.</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Add New Class</div>
            <div className="form-group">
              <label>Class</label>
              <select className="form-control" value={preset} onChange={e => { setPreset(e.target.value); setCustomName(''); }}>
                <option value="">— Select Class —</option>
                {CLASS_PRESETS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {preset === 'Others' && (
              <div className="form-group">
                <label>Custom Class Name</label>
                <input className="form-control" placeholder="e.g. O-Level, A-Level, Form 1" value={customName}
                  onChange={e => setCustomName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()} />
              </div>
            )}
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={create}>＋ Create Class</button>
          </div>

          <div className="card">
            <div className="card-title">All Classes ({classes.length})</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Class Name</th></tr></thead>
                <tbody>
                  {classes.length === 0
                    ? <tr><td colSpan="2"><div className="empty-state"><div className="empty-icon">🏫</div><p>No classes found</p></div></td></tr>
                    : classes.map((c, i) => (
                      <tr key={c.id}>
                        <td className="mono text-muted">{i + 1}</td>
                        <td>{c.name}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Subjects ───────────────────────────────────────────────────────────────────
function SubjectManager() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ name: '', class_id: '' });
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    API.get('/classes/').then(r => setClasses(r.data)).catch(() => {});
    API.get('/subjects/').then(r => setSubjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.class_id) { setMsg('Class and subject name(s) are required'); return; }
    try {
      const res = await API.post('/subjects/', { name: form.name.trim(), class_id: parseInt(form.class_id) });
      const count = res.data.length;
      setMsg(`${count} subject${count > 1 ? 's' : ''} added!`);
      setForm(f => ({ ...f, name: '' }));
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error creating subject');
    }
  };

  const deleteSubject = async (id, name) => {
    if (!window.confirm(`Delete subject "${name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/subjects/${id}`);
      load();
      setMsg(`"${name}" deleted.`);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error deleting subject');
    }
  };

  const filteredSubjects = form.class_id
    ? subjects.filter(s => s.class_id === parseInt(form.class_id))
    : subjects;

  return (
    <>
      <div className="page-header">
        <div><h2>Subjects</h2><p>Create subjects and link them to a class</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.includes('!') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Create New Subject</div>
            <div className="form-group">
              <label>Class</label>
              <select className="form-control" value={form.class_id} onChange={e => setForm({ ...form, class_id: e.target.value })}>
                <option value="">— Select Class —</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Subject Name(s)</label>
              <input
                className="form-control"
                placeholder="Malayalam, English, Hindi  (separate with commas)"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && create()}
              />
              <small style={{ color: '#888', fontSize: '0.78rem' }}>Separate multiple subjects with commas to add them all at once</small>
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={create}>＋ Add Subject(s)</button>
          </div>

          <div className="card">
            <div className="card-title">
              {form.class_id
                ? `Subjects in ${classes.find(c => c.id === parseInt(form.class_id))?.name || ''} (${filteredSubjects.length})`
                : `All Subjects (${subjects.length})`}
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Subject</th><th>Class</th><th></th></tr></thead>
                <tbody>
                  {filteredSubjects.length === 0
                    ? <tr><td colSpan="3"><div className="empty-state"><div className="empty-icon">📖</div><p>No subjects yet</p></div></td></tr>
                    : filteredSubjects.map(s => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td><span className="badge badge-scheduled">{s.class_name}</span></td>
                        <td>
                          <button onClick={() => deleteSubject(s.id, s.name)} className="btn btn-danger btn-sm" style={{ padding: '4px 10px', fontSize: 12 }}>Delete</button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Student Registration ───────────────────────────────────────────────────────
// ── Tutor Registration ─────────────────────────────────────────────────────────
const TUTOR_CSV_HEADERS = 'uid,first_name,middle_name,last_name,email,alt_email,password,whatsapp1,phone,gender,dob,address,city,state,pincode,bio,selected_subjects';
const TUTOR_CSV_EXAMPLE = 'T001,Rahul,,Sharma,rahul.sharma@email.com,rahul.alt@email.com,Pass@1234,919876543210,919876543210,Male,1990-05-15,123 Main St,Mumbai,Maharashtra,400001,Experienced Math tutor with 5 years,Mathematics,Science';

const STUDENT_CSV_HEADERS = 'uid,first_name,middle_name,last_name,email,alt_email,password,whatsapp1,whatsapp2,phone,gender,dob,address,city,state,pincode,class_name,package,pricing_plan,mode,hours_per_month,selected_subjects';
const STUDENT_CSV_EXAMPLE = 'S001,Priya,,Nair,priya.nair@email.com,,Pass@1234,919876543211,919876543212,919876543211,Female,2010-08-20,45 Park Ave,Chennai,Tamil Nadu,600001,Grade 10,Basic,Monthly,online,8,Mathematics,Science';

function downloadTemplate(headers, example, filename) {
  const blob = new Blob([headers + '\n' + example + '\n'], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function BulkUploadCard({ role }) {
  const [csvMsg, setCsvMsg] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResults, setCsvResults] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvLoading(true); setCsvMsg(''); setCsvResults(null);
    try {
      const text = await file.text();
      const res = await API.post(`/auth/bulk-register?role=${role}`, { csv_content: text });
      setCsvResults(res.data);
      setCsvMsg(`✓ Registered ${res.data.registered} ${role}(s)`);
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map(d => {
          const col = d.loc ? d.loc.filter(l => l !== 'body' && l !== '__root__').join(' → ') : '';
          return col ? `Column "${col}": ${d.msg}` : d.msg;
        });
        setCsvMsg('Upload failed — ' + msgs.join('; '));
      } else if (typeof detail === 'string') {
        setCsvMsg(detail);
      } else {
        setCsvMsg(ex.message || 'Upload failed — check your CSV format and try again');
      }
    }
    setCsvLoading(false);
    e.target.value = '';
  };

  return (
    <div className="card" style={{ marginTop: 28 }}>
      <div className="card-title">📋 Bulk Upload via CSV</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 14 }}>
        Upload a CSV file to register multiple {role}s at once. Download the template for the correct column format.
      </p>
      {csvMsg && <div className={`alert ${csvMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setCsvMsg('')}>{csvMsg}</div>}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" onClick={() => downloadTemplate(role === 'tutor' ? TUTOR_CSV_HEADERS : STUDENT_CSV_HEADERS, role === 'tutor' ? TUTOR_CSV_EXAMPLE : STUDENT_CSV_EXAMPLE, `${role}_template.csv`)}>
          ⬇ Download Template
        </button>
        <label className="btn btn-primary" style={{ cursor: 'pointer', marginBottom: 0 }}>
          {csvLoading ? 'Uploading…' : '⬆ Upload CSV'}
          <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleUpload} disabled={csvLoading} />
        </label>
      </div>
      {csvResults && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#16a34a' }}>✓ {csvResults.registered} account(s) created</div>
          {csvResults.errors && csvResults.errors.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 6, fontSize: 13 }}>⚠ {csvResults.errors.length} row(s) skipped:</div>
              {csvResults.errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 2 }}>{e}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TutorRegistration() {
  const emptyForm = {
    uid: '',
    first_name: '', middle_name: '', last_name: '',
    email: '', alt_email: '', password: '',
    gender: '', dob: '', phone: '',
    whatsapp1: '', timezone: 'Asia/Kolkata',
    address: '', city: '', state: '', pincode: '',
    bio: '',
  };

  const emptyAvail = DAYS.reduce((acc, d) => {
    acc[d] = { enabled: false, slots: [{ start_time: '09:00', end_time: '17:00' }] };
    return acc;
  }, {});

  const [form, setForm]                   = useState(emptyForm);
  const [allSubjects, setAllSubjects]     = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [avail, setAvail]                 = useState(emptyAvail);
  const [msg, setMsg]                     = useState('');
  const [loading, setLoading]             = useState(false);
  const [showPass, setShowPass]           = useState(false);

  // Edit-availability modal state
  const [editTutors, setEditTutors]       = useState([]);
  const [editTutorId, setEditTutorId]     = useState('');
  const [editAvail, setEditAvail]         = useState(emptyAvail);
  const [editMsg, setEditMsg]             = useState('');
  const [editLoading, setEditLoading]     = useState(false);

  useEffect(() => {
    // Load ALL subjects across all classes
    API.get('/subjects/').then(r => setAllSubjects(r.data)).catch(() => {});
    // Load tutors for edit section
    API.get('/auth/users/tutor?active=true').then(r => setEditTutors(r.data)).catch(() => {});
  }, []);

  const toggleSubject = (name) =>
    setSelectedSubjects(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const toggleDay = (day, setter) =>
    setter(a => ({ ...a, [day]: { ...a[day], enabled: !a[day].enabled } }));

  const updateSlot = (day, idx, key, val, setter) =>
    setter(a => {
      const slots = a[day].slots.map((s, i) => i === idx ? { ...s, [key]: val } : s);
      return { ...a, [day]: { ...a[day], slots } };
    });

  const addSlot = (day, setter) =>
    setter(a => ({
      ...a,
      [day]: { ...a[day], slots: [...a[day].slots, { start_time: '09:00', end_time: '17:00' }] }
    }));

  const removeSlot = (day, idx, setter) =>
    setter(a => {
      const slots = a[day].slots.filter((_, i) => i !== idx);
      return { ...a, [day]: { ...a[day], slots: slots.length ? slots : [{ start_time: '09:00', end_time: '17:00' }] } };
    });

  const buildSlots = (availState) =>
    DAYS.filter(d => availState[d].enabled).flatMap(d =>
      availState[d].slots.map(s => ({ day: d, start_time: s.start_time, end_time: s.end_time }))
    );

  const submit = async () => {
    if (!form.uid) { setMsg('User ID is required'); return; }
    if (!form.first_name || !form.last_name) { setMsg('First name and last name are required'); return; }
    if (!form.email)    { setMsg('Email is required'); return; }
    if (!form.password) { setMsg('Password is required'); return; }
    if (!form.whatsapp1) { setMsg('WhatsApp number is required'); return; }

    setLoading(true);
    try {
      const res = await API.post('/auth/register', {
        ...form,
        role: 'tutor',
        selected_subjects: selectedSubjects.join(','),
      });
      const tutorId = res.data.id;

      const slots = buildSlots(avail);
      if (slots.length > 0) {
        await API.post('/availability/', { tutor_id: tutorId, slots });
      }

      setMsg('✓ Companion registered successfully!');
      setForm(emptyForm);
      setSelectedSubjects([]);
      setAvail(emptyAvail);
      API.get('/auth/users/tutor?active=true').then(r => setEditTutors(r.data)).catch(() => {});
    } catch (e) {
      const detail = e.response?.data?.detail;
      setMsg(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Registration failed'));
    }
    setLoading(false);
  };

  // Edit availability for existing tutor
  const loadEditAvail = async (tutorId) => {
    setEditTutorId(tutorId);
    setEditMsg('');
    if (!tutorId) { setEditAvail(emptyAvail); return; }
    try {
      const res = await API.get(`/availability/${tutorId}`);
      const base = DAYS.reduce((acc, d) => {
        acc[d] = { enabled: false, slots: [{ start_time: '09:00', end_time: '17:00' }] };
        return acc;
      }, {});
      res.data.forEach(slot => {
        if (!base[slot.day].enabled) {
          base[slot.day] = { enabled: true, slots: [] };
        }
        base[slot.day].slots.push({
          start_time: slot.start_time.slice(0, 5),
          end_time:   slot.end_time.slice(0, 5),
        });
      });
      setEditAvail(base);
    } catch {
      setEditAvail(emptyAvail);
    }
  };

  const saveEditAvail = async () => {
    if (!editTutorId) { setEditMsg('Select a tutor first'); return; }
    setEditLoading(true);
    try {
      const slots = buildSlots(editAvail);
      await API.post('/availability/', { tutor_id: parseInt(editTutorId), slots });
      setEditMsg('✓ Availability updated!');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setEditMsg(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Update failed'));
    }
    setEditLoading(false);
  };

  const field = (label, key, type = 'text') => {
    const maxLen = key === 'uid' ? 10 : key === 'pincode' ? 6 : key === 'phone' ? 10 : (key === 'whatsapp1' || key === 'whatsapp2') ? 15 : undefined;
    return (
      <div className="form-group">
        <label>{label}</label>
        <input className="form-control" type={type} value={form[key]} maxLength={maxLen} onChange={e => set(key, e.target.value)} />
      </div>
    );
  };

  const AvailabilityGrid = ({ state, setter }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {DAYS.map(day => (
        <div key={day} style={{
          padding: '12px 14px',
          border: `1px solid ${state[day].enabled ? 'var(--color-accent)' : 'var(--border-color)'}`,
          borderRadius: '8px',
          background: state[day].enabled ? 'var(--color-accent)11' : 'transparent',
        }}>
          {/* Day checkbox row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: state[day].enabled ? '10px' : 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={state[day].enabled} onChange={() => toggleDay(day, setter)}
                style={{ accentColor: 'var(--color-accent)', width: '16px', height: '16px' }} />
              {day}
            </label>
            {state[day].enabled && (
              <button type="button" onClick={() => addSlot(day, setter)}
                style={{ background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>
                +
              </button>
            )}
          </div>

          {/* Time slots */}
          {state[day].enabled && state[day].slots.map((slot, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'flex-end', marginTop: idx > 0 ? '8px' : 0 }}>
              <div className="form-group" style={{ margin: 0 }}>
                {idx === 0 && <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Start Time</label>}
                <TimePicker12h className="form-control" value={slot.start_time}
                  onChange={v => updateSlot(day, idx, 'start_time', v, setter)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                {idx === 0 && <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>End Time</label>}
                <TimePicker12h className="form-control" value={slot.end_time}
                  onChange={v => updateSlot(day, idx, 'end_time', v, setter)} />
              </div>
              <button type="button"
                onClick={() => removeSlot(day, idx, setter)}
                disabled={state[day].slots.length === 1}
                style={{
                  background: 'transparent', border: '1px solid var(--border-color)',
                  borderRadius: '6px', padding: '6px 10px', cursor: state[day].slots.length === 1 ? 'not-allowed' : 'pointer',
                  color: 'var(--color-danger)', opacity: state[day].slots.length === 1 ? 0.3 : 1, fontSize: '16px',
                }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="page-header">
        <div><h2>Companion Registration</h2><p>Register a new Companion with subjects and weekly availability</p></div>
      </div>
      <div className="page-body">
        {msg && (
          <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>
            {msg}
          </div>
        )}

        {/* Personal Information */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-title">Personal Information</div>
          <div className="grid-2">
            {field('User ID *', 'uid')}
            {field('First Name *', 'first_name')}
          </div>
          <div className="grid-2">
            {field('Middle Name', 'middle_name')}
            {field('Last Name *', 'last_name')}
          </div>
          <div className="grid-2">
            {field('Mobile Number', 'phone', 'tel')}
            {field('Email *', 'email', 'email')}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>WhatsApp Number * <span style={{ fontSize: '11px', color: '#888' }}>(with country code, e.g. 919876543210)</span></label>
              <input className="form-control" type="tel" maxLength={15}
                value={form.whatsapp1} onChange={e => set('whatsapp1', e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 919876543210" required />
            </div>
            <div className="form-group">
              <label>Timezone</label>
              <select className="form-control" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid-2">
            {field('Alternative Email', 'alt_email', 'email')}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Password *</label>
              <div style={{ position: 'relative' }}>
                <input className="form-control" type={showPass ? 'text' : 'password'}
                  value={form.password} onChange={e => set('password', e.target.value)}
                  style={{ paddingRight: '40px' }} />
                <span onClick={() => setShowPass(p => !p)}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showPass ? '🙈' : '👁'}
                </span>
              </div>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Gender</label>
              <select className="form-control" value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="">Select Gender</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </div>
            {field('Date of Birth', 'dob', 'date')}
          </div>
          <div className="grid-2">
            {field('Address', 'address')}
            {field('City', 'city')}
          </div>
          <div className="grid-2">
            {field('State', 'state')}
            {field('Pincode', 'pincode')}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Role</label>
              <input className="form-control" value="Companion" readOnly style={{ opacity: 0.6 }} />
            </div>
          </div>
          <div className="form-group">
            <label>Bio / Teaching Background</label>
            <textarea className="form-control" rows={3}
              placeholder="Optional: Share teaching background and expertise"
              value={form.bio} onChange={e => set('bio', e.target.value)}
              style={{ resize: 'vertical' }} />
          </div>
        </div>

        {/* Subjects */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-title">Subjects <span style={{ color: 'var(--color-danger)' }}>*</span></div>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '12px' }}>
            Select all subjects this Companion can teach (across all grades)
          </p>
          {allSubjects.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No subjects found. Add subjects first in the Subjects module.</p>
          ) : (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px', border: '1px solid var(--border-color)',
              borderRadius: '8px', padding: '12px',
            }}>
              {[...new Map(allSubjects.map(s => [s.name, s])).values()].map(s => (
                <label key={s.name} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 10px', border: '1px solid',
                  borderColor: selectedSubjects.includes(s.name) ? 'var(--color-accent)' : 'var(--border-color)',
                  borderRadius: '6px', cursor: 'pointer',
                  background: selectedSubjects.includes(s.name) ? 'var(--color-accent)22' : 'transparent',
                }}>
                  <input type="checkbox" checked={selectedSubjects.includes(s.name)}
                    onChange={() => toggleSubject(s.name)}
                    style={{ accentColor: 'var(--color-accent)' }} />
                  <span style={{ fontSize: '14px' }}>{s.name}</span>
                </label>
              ))}
            </div>
          )}
          {selectedSubjects.length > 0 && (
            <small style={{ color: 'var(--text-muted)', marginTop: '6px', display: 'block' }}>
              {selectedSubjects.length} subject{selectedSubjects.length > 1 ? 's' : ''} selected
            </small>
          )}
        </div>

        {/* Availability */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-title">Weekly Availability</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '14px' }}>
            Check each day the Companion is available and set the working hours for that day
          </p>
          <AvailabilityGrid state={avail} setter={setAvail} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary"
            style={{ minWidth: '200px', padding: '12px 24px', fontSize: '15px' }}
            onClick={submit} disabled={loading}>
            {loading ? 'Registering...' : '✓ Submit Registration'}
          </button>
        </div>

        <BulkUploadCard role="tutor" />

        {/* Edit Availability for existing tutor */}
        <div className="card" style={{ marginTop: '28px' }}>
          <div className="card-title">Edit Companion Availability</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '14px' }}>
            Update the weekly schedule for an already registered Companion
          </p>
          {editMsg && (
            <div className={`alert ${editMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`}
              onClick={() => setEditMsg('')}>{editMsg}</div>
          )}
          <div className="form-group">
            <label>Select Companion</label>
            <select className="form-control" value={editTutorId}
              onChange={e => loadEditAvail(e.target.value)}>
              <option value="">— Select Companion —</option>
              {editTutors.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {editTutorId && (
            <>
              <AvailabilityGrid state={editAvail} setter={setEditAvail} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                <button className="btn btn-primary" onClick={saveEditAvail} disabled={editLoading}>
                  {editLoading ? 'Saving...' : '✓ Save Availability'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function getPackage(className) {
  if (!className) return '';
  const grade = parseInt(className.replace('Grade ', ''));
  return grade <= 9 ? 'Starter Scholar' : 'Excellence Accelerator';
}

function StudentRegistration({ canViewProfit = false }) {
  const emptyForm = {
    uid: '',
    first_name: '', middle_name: '', last_name: '',
    email: '', alt_email: '', password: '',
    gender: '', dob: '', phone: '',
    whatsapp1: '', whatsapp2: '', timezone: 'Asia/Kolkata',
    address: '', city: '', state: '', pincode: '',
    class_name: '', package: '', pricing_plan: '', mode: '', hours_per_month: '',
    syllabus: '',
    profit_per_hour: '', profit_per_hour_online: '', profit_per_hour_offline: '',
  };

  const [form, setForm] = useState(emptyForm);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    API.get('/classes/').then(r => setClasses(r.data)).catch(() => {});
    // One-shot prefill from an inquiry — populated by the Inquiries page before
    // it navigates here, then consumed and cleared so it does not stick.
    if (typeof window !== 'undefined' && window.__inquiryPrefill) {
      const p = window.__inquiryPrefill;
      window.__inquiryPrefill = null;
      const [fn, ...rest] = (p.student_name || '').trim().split(/\s+/);
      // Compose a single-line address from the structured address parts.
      const addressLine = [p.address_line, p.post_office, p.district]
        .filter(Boolean).join(', ');
      // Stash the parent-voice "about student" into the bio field so the admin
      // can see it on the form and decide what to keep.
      const bio = p.about_student || '';
      setForm(f => ({
        ...f,
        first_name: fn || '',
        last_name: rest.join(' '),
        phone: p.parent_phone || '',
        whatsapp1: p.parent_whatsapp || p.parent_phone || '',
        alt_email: p.parent_email || '',
        dob: p.student_dob || '',
        address: addressLine,
        city: p.city || '',
        state: p.district || '',  // closest existing slot for "district"
        pincode: p.pincode || '',
        class_name: p.student_class || '',
        syllabus: p.syllabus || '',
        package: p.student_class ? getPackage(p.student_class) : '',
        mode: p.mode || '',
        ...(bio ? { bio } : {}),
      }));
      // Pre-tick subjects of interest if they match the loaded subject list
      if (p.subjects_interested) {
        const wanted = p.subjects_interested.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
        // Will be re-applied once subjects load if class is set
        window.__inquirySubjectsWanted = wanted;
      }
      setMsg(`✓ Pre-filled from inquiry #${p.inquiry_id}. Please review, set email/password/UID, then save.`);
      if (p.student_class) {
        const cls = (classes || []).find(c => c.name === p.student_class);
        if (cls) API.get(`/subjects/?class_id=${cls.id}`).then(r => {
          setSubjects(r.data);
          const wanted = window.__inquirySubjectsWanted;
          if (wanted) {
            window.__inquirySubjectsWanted = null;
            const matched = (r.data || []).filter(s => wanted.includes((s.name || '').toLowerCase())).map(s => s.name);
            if (matched.length) setSelectedSubjects(matched);
          }
        }).catch(() => {});
      }
    }
  }, []); // eslint-disable-line

  const handleClassChange = (className) => {
    const pkg = getPackage(className);
    setForm(f => ({ ...f, class_name: className, package: pkg }));
    setSelectedSubjects([]);
    if (className) {
      const cls = classes.find(c => c.name === className);
      if (cls) {
        API.get(`/subjects/?class_id=${cls.id}`).then(r => setSubjects(r.data)).catch(() => {});
      }
    } else {
      setSubjects([]);
    }
  };

  const toggleSubject = (name) => {
    setSelectedSubjects(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const submit = async () => {
    if (!form.uid) { setMsg('User ID is required'); return; }
    if (form.uid.length > 10) { setMsg('User ID must not exceed 10 characters'); return; }
    if (!form.first_name || !form.last_name) { setMsg('First name and last name are required'); return; }
    if (!form.email) { setMsg('Email is required'); return; }
    if (!form.password) { setMsg('Password is required'); return; }
    if (!form.class_name) { setMsg('Please select a class'); return; }
    if (!form.pricing_plan) { setMsg('Please select a pricing plan'); return; }
    if (!form.whatsapp1) { setMsg('WhatsApp Number 1 is required'); return; }
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) { setMsg('Pincode must be exactly 6 digits'); return; }

    setLoading(true);
    try {
      const payload = { ...form, role: 'student', selected_subjects: selectedSubjects.join(',') };
      // Convert empty number fields to null so Pydantic accepts them
      if (payload.hours_per_month === '' || payload.hours_per_month === undefined) payload.hours_per_month = null;
      ['profit_per_hour', 'profit_per_hour_online', 'profit_per_hour_offline'].forEach(k => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
        else payload[k] = parseFloat(payload[k]);
      });
      // Strip profit fields entirely if this admin isn't permitted, just defence in depth
      if (!canViewProfit) { delete payload.profit_per_hour; delete payload.profit_per_hour_online; delete payload.profit_per_hour_offline; }
      await API.post('/auth/register', payload);
      setMsg('✓ Student registered successfully!');
      setForm(emptyForm);
      setSelectedSubjects([]);
      setSubjects([]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        setMsg(detail.map(d => d.msg).join(', '));
      } else {
        setMsg(detail || 'Registration failed');
      }
    }
    setLoading(false);
  };

  const field = (label, key, type = 'text', placeholder = '') => {
    const maxLen = key === 'uid' ? 10 : key === 'pincode' ? 6 : key === 'phone' ? 10 : (key === 'whatsapp1' || key === 'whatsapp2') ? 15 : undefined;
    return (
      <div className="form-group">
        <label>{label}</label>
        <input
          className="form-control"
          type={type}
          placeholder={placeholder}
          value={form[key]}
          maxLength={maxLen}
          onChange={e => set(key, e.target.value)}
        />
      </div>
    );
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Student Registration</h2><p>Register a new student with full details</p></div>
      </div>
      <div className="page-body">
        {msg && (
          <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>
            {msg}
          </div>
        )}

        {/* Personal Information */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-title">Personal Information</div>
          <div className="grid-2">
            {field('User ID *', 'uid')}
            {field('First Name *', 'first_name')}
          </div>
          <div className="grid-2">
            {field('Middle Name', 'middle_name')}
            {field('Last Name *', 'last_name')}
          </div>
          <div className="grid-2">
            {field('Mobile Number', 'phone', 'tel')}
            {field('Email *', 'email', 'email')}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>WhatsApp Number 1 * <span style={{ fontSize: '11px', color: '#888' }}>(with country code, e.g. 919876543210)</span></label>
              <input className="form-control" type="tel" maxLength={15}
                value={form.whatsapp1} onChange={e => set('whatsapp1', e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 919876543210" required />
            </div>
            <div className="form-group">
              <label>WhatsApp Number 2 <span style={{ fontSize: '11px', color: '#888' }}>(optional – father/mother/student)</span></label>
              <input className="form-control" type="tel" maxLength={15}
                value={form.whatsapp2} onChange={e => set('whatsapp2', e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 919876543210" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Timezone</label>
              <select className="form-control" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
            </div>
            {field('Alternative Email', 'alt_email', 'email')}
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  style={{ paddingRight: '40px' }}
                />
                <span
                  onClick={() => setShowPass(p => !p)}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  {showPass ? '🙈' : '👁'}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select className="form-control" value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="">Select Gender</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            {field('Date of Birth', 'dob', 'date')}
            {field('Address', 'address')}
          </div>
          <div className="grid-2">
            {field('City', 'city')}
            {field('State', 'state')}
          </div>
          <div className="grid-2">
            {field('Pincode', 'pincode')}
            <div className="form-group">
              <label>Role</label>
              <input className="form-control" value="Student" readOnly style={{ opacity: 0.6 }} />
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-title">Academic Information</div>
          <div className="grid-2">
            <div className="form-group">
              <label>Class Name *</label>
              <select className="form-control" value={form.class_name} onChange={e => handleClassChange(e.target.value)}>
                <option value="">Select Class</option>
                {classes.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              {!form.class_name && <small style={{ color: 'var(--text-muted)' }}>Please select a class first</small>}
            </div>
            <div className="form-group">
              <label>Pricing Plan *</label>
              <select className="form-control" value={form.pricing_plan} onChange={e => set('pricing_plan', e.target.value)}>
                <option value="">Select Pricing Plan</option>
                {PRICING_PLANS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid-2">
            <SyllabusPicker value={form.syllabus} onChange={v => set('syllabus', v)} />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Package</label>
              <input
                className="form-control"
                value={form.package}
                readOnly
                placeholder="Auto-selected based on class"
                style={{ opacity: form.package ? 1 : 0.5 }}
              />
              {form.package && (
                <small style={{ color: 'var(--color-accent)' }}>
                  {parseInt(form.class_name.replace('Grade ', '')) <= 9
                    ? 'Grades 1–9: Starter Scholar package'
                    : 'Grades 10–12: Excellence Accelerator package'}
                </small>
              )}
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Mode *</label>
              <select className="form-control" value={form.mode} onChange={e => set('mode', e.target.value)}>
                <option value="">Select Mode</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </div>
            <div className="form-group">
              <label>Hours per Month *</label>
              <input
                className="form-control"
                type="number"
                min="1"
                placeholder="e.g. 20"
                value={form.hours_per_month}
                onChange={e => set('hours_per_month', e.target.value ? parseInt(e.target.value) : '')}
              />
            </div>
            {canViewProfit && (
              <div className="form-group">
                <label>Profit / Hour — Online (₹) <span style={{ fontSize:11, color:'#94a3b8', fontWeight:500 }}>internal only</span></label>
                <input className="form-control" type="number" step="0.01" min="0" placeholder="e.g. 250"
                  value={form.profit_per_hour_online}
                  onChange={e => set('profit_per_hour_online', e.target.value)} />
              </div>
            )}
            {canViewProfit && (
              <div className="form-group">
                <label>Profit / Hour — Offline (₹) <span style={{ fontSize:11, color:'#94a3b8', fontWeight:500 }}>internal only</span></label>
                <input className="form-control" type="number" step="0.01" min="0" placeholder="e.g. 300"
                  value={form.profit_per_hour_offline}
                  onChange={e => set('profit_per_hour_offline', e.target.value)} />
              </div>
            )}
          </div>

          {/* Subjects */}
          <div className="form-group" style={{ marginTop: '8px' }}>
            <label style={{ color: 'var(--color-danger)', fontWeight: 600 }}>
              Subjects {subjects.length > 0 && '*'}
            </label>
            {!form.class_name ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Select a class to see available subjects</p>
            ) : subjects.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No subjects added for this class yet. Go to Subjects to add them.</p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '12px',
              }}>
                {subjects.map(s => (
                  <label
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: selectedSubjects.includes(s.name) ? 'var(--color-accent)22' : 'transparent',
                      borderColor: selectedSubjects.includes(s.name) ? 'var(--color-accent)' : 'var(--border-color)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSubjects.includes(s.name)}
                      onChange={() => toggleSubject(s.name)}
                      style={{ accentColor: 'var(--color-accent)' }}
                    />
                    <span style={{ fontSize: '14px' }}>{s.name}</span>
                  </label>
                ))}
              </div>
            )}
            {selectedSubjects.length > 0 && (
              <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                {selectedSubjects.length} subject{selectedSubjects.length > 1 ? 's' : ''} selected
              </small>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-primary"
            style={{ minWidth: '200px', padding: '12px 24px', fontSize: '15px' }}
            onClick={submit}
            disabled={loading}
          >
            {loading ? 'Registering...' : '✓ Submit Registration'}
          </button>
        </div>

        <BulkUploadCard role="student" />
      </div>
    </>
  );
}

// ── Enroll (Bulk) ──────────────────────────────────────────────────────────────
function JoiningLinkCell({ enrollment, onSaved }) {
  // Show the saved link as a clickable URL so admins can clearly see which
  // enrollments have a link configured. Click ✏️ to edit/replace it.
  const [editing, setEditing] = useState(!enrollment.joining_link);
  const [val, setVal] = useState(enrollment.joining_link || '');
  const [saving, setSaving] = useState(false);

  // Sync local state when the saved link changes externally (after onSaved -> load)
  useEffect(() => {
    setVal(enrollment.joining_link || '');
    setEditing(!enrollment.joining_link);
  }, [enrollment.joining_link, enrollment.id]);

  const save = async () => {
    const v = val.trim();
    if (v === (enrollment.joining_link || '')) { setEditing(!v); return; }
    setSaving(true);
    try {
      await API.patch(`/enrollment/${enrollment.id}`, { joining_link: v });
      if (onSaved) onSaved();
      // Don't flip mode here — wait for the parent reload to update
      // enrollment.joining_link, then the useEffect above handles the
      // transition. Otherwise we briefly render display mode with stale
      // (null) joining_link, which crashed the row.
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error saving link');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        <input type="url" placeholder="Paste Teams/Zoom/Meet link…" value={val}
          autoFocus={!!enrollment.joining_link}
          onChange={ev => setVal(ev.target.value)}
          onBlur={save}
          onKeyDown={ev => { if (ev.key === 'Enter') ev.currentTarget.blur(); if (ev.key === 'Escape') { setVal(enrollment.joining_link || ''); setEditing(!!enrollment.joining_link); } }}
          style={{ width:200, padding:'4px 8px', borderRadius:6, border:'1px solid #2554b8', fontSize:12, outline:'none' }} />
        {saving && <span style={{ fontSize:10, color:'#64748b' }}>…</span>}
      </div>
    );
  }
  // Compact display of saved link + edit affordance.
  // Defensive: if we end up here with no saved link (e.g. mid-reload race),
  // fall back to editing mode instead of crashing on .length.
  const saved = enrollment.joining_link || '';
  if (!saved) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        <input type="url" placeholder="Paste link…" value={val}
          onChange={ev => setVal(ev.target.value)}
          onBlur={save}
          style={{ width:200, padding:'4px 8px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:12, outline:'none' }} />
      </div>
    );
  }
  const short = saved.length > 32 ? saved.slice(0, 30) + '…' : saved;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, maxWidth:240 }}>
      <a href={saved} target="_blank" rel="noopener noreferrer"
        title={saved}
        style={{ color:'#2554b8', fontSize:12, fontWeight:600, textDecoration:'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
        🔗 {short}
      </a>
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Edit link"
        style={{ background:'none', border:'1px solid #cbd5e1', color:'#64748b', cursor:'pointer', borderRadius:6, padding:'2px 6px', fontSize:11 }}
      >✏️</button>
    </div>
  );
}


function Enroll() {
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentList, setShowStudentList] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rows, setRows] = useState([{ subject: '', tutor_id: '', mode: 'online', custom_rate: '' }]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollTutorSearch, setEnrollTutorSearch] = useState('');
  const [editSubjectModal, setEditSubjectModal] = useState(null); // { enrollment }
  const [editSubjectValue, setEditSubjectValue] = useState('');
  const [teamsOn, setTeamsOn] = useState(false);

  const load = useCallback(() => {
    API.get('/auth/users/student?active=true').then(r => setStudents(r.data)).catch(() => {});
    API.get('/auth/users/tutor?active=true').then(r => setTutors(r.data)).catch(() => {});
    API.get('/subjects/').then(r => setAllSubjects(r.data)).catch(() => {});
    API.get('/classes/').then(r => setClasses(r.data)).catch(() => {});
    API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {});
    API.get('/teams/status').then(r => setTeamsOn(!!r.data?.enabled)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Get subjects for selected student — filtered to their chosen subjects
  const selectedStudent = students.find(s => s.id === parseInt(studentId));
  const studentClass = selectedStudent?.class_name || '';
  const classObj = classes.find(c => c.name === studentClass);
  const studentSelectedSubjects = selectedStudent?.selected_subjects
    ? selectedStudent.selected_subjects.split(',').map(s => s.trim()).filter(Boolean)
    : null;
  // If student has selected subjects, always show those (regardless of class linkage)
  // Fall back to class-based subjects, then all subjects
  const subjects = studentSelectedSubjects
    ? [...new Map(allSubjects.filter(s => studentSelectedSubjects.includes(s.name)).map(s => [s.name, s])).values()]
    : classObj
    ? [...new Map(allSubjects.filter(s => s.class_id === classObj.id).map(s => [s.name, s])).values()]
    : [...new Map(allSubjects.map(s => [s.name, s])).values()];

  const addRow = () => setRows([...rows, { subject: '', tutor_id: '', mode: 'online', custom_rate: '' }]);

  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const updateRow = (i, field, value) => {
    const updated = [...rows];
    updated[i] = { ...updated[i], [field]: value };
    // Reset tutor when subject changes
    if (field === 'subject') updated[i].tutor_id = '';
    setRows(updated);
  };

  const submit = async () => {
    if (!studentId) { setMsg('Please select a student'); return; }
    if (!startDate || !endDate) { setMsg('Please set start and end dates'); return; }
    const invalid = rows.find(r => !r.subject || !r.tutor_id);
    if (invalid) { setMsg('Every row must have a subject and Companion selected'); return; }

    setLoading(true);
    const results = [];
    for (const row of rows) {
      try {
        await API.post('/enrollment/', {
          student_id: parseInt(studentId),
          tutor_id: parseInt(row.tutor_id),
          subject: row.subject,
          start_date: startDate,
          end_date: endDate,
          mode: row.mode || 'online',
          custom_rate: row.custom_rate ? parseFloat(row.custom_rate) : null,
        });
        results.push({ ok: true, subject: row.subject });
      } catch (e) {
        results.push({ ok: false, subject: row.subject, error: e.response?.data?.detail || 'Error' });
      }
    }
    setLoading(false);

    const ok = results.filter(r => r.ok).length;
    const failed = results.filter(r => !r.ok);
    if (failed.length === 0) {
      setMsg(`✓ All ${ok} enrollment${ok > 1 ? 's' : ''} created successfully!`);
      setStudentId('');
      setStartDate('');
      setEndDate('');
      setRows([{ subject: '', tutor_id: '', mode: 'online', custom_rate: '' }]);
    } else {
      setMsg(`${ok} created. Failed: ${failed.map(f => `${f.subject} (${f.error})`).join(', ')}`);
    }
    load();
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Enroll Student</h2><p>Assign multiple subjects and Companions in one go</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="grid-2">
          <div className="card">
            <div className="card-title">New Enrollment</div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>Student</label>
              <input
                className="form-control"
                placeholder="Type student name to search…"
                value={studentSearch}
                autoComplete="off"
                onChange={e => { setStudentSearch(e.target.value); setShowStudentList(true); if (!e.target.value) { setStudentId(''); setRows([{ subject: '', tutor_id: '' }]); } }}
                onFocus={() => setShowStudentList(true)}
                onBlur={() => setTimeout(() => setShowStudentList(false), 180)}
              />
              {showStudentList && studentSearch && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 220, overflowY: 'auto' }}>
                  {students.filter(s => s.name.toLowerCase().includes(studentSearch.toLowerCase())).length === 0
                    ? <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No students found</div>
                    : students.filter(s => s.name.toLowerCase().includes(studentSearch.toLowerCase())).map(s => (
                        <div key={s.id}
                          onMouseDown={() => { setStudentId(String(s.id)); setStudentSearch(`${s.name}${s.class_name ? ` (${s.class_name})` : ''}`); setShowStudentList(false); setRows([{ subject: '', tutor_id: '' }]); }}
                          style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f5ff'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                          <strong>{s.name}</strong>{s.class_name ? <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>({s.class_name})</span> : ''}
                        </div>
                      ))
                  }
                </div>
              )}
              {studentId && selectedStudent && (
                <small style={{ color: 'var(--text-muted)' }}>
                  Showing subjects for {studentClass}
                  {subjects.length === 0 ? ' — no subjects added yet' : ` (${subjects.length} subject${subjects.length > 1 ? 's' : ''})`}
                </small>
              )}
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Start Date</label>
                <input className="form-control" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input className="form-control" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>

            <div style={{ marginTop: '4px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Subjects &amp; Companions
              </label>
              {rows.map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <select
                    className="form-control"
                    value={row.subject}
                    onChange={e => updateRow(i, 'subject', e.target.value)}
                    style={{ flex: '1 1 160px', minWidth: 160 }}
                  >
                    <option value="">— Subject —</option>
                    {studentId
                      ? subjects.length === 0
                        ? <option disabled>No subjects for {studentClass}</option>
                        : subjects.map(s => <option key={s.name} value={s.name}>{s.name}</option>)
                      : <option disabled>Select a student first</option>
                    }

                  </select>
                  <div style={{ flex: '1 1 160px', minWidth: 160 }}>
                    {row.subject ? (
                      <SearchableSelect
                        options={tutors
                          .filter(t => t.is_active !== 'inactive' && t.selected_subjects?.split(',').map(s => s.trim()).includes(row.subject))
                          .map(t => ({ value: t.id, label: t.name }))}
                        value={row.tutor_id}
                        onChange={v => updateRow(i, 'tutor_id', v)}
                        placeholder="— Companion —"
                      />
                    ) : (
                      <select className="form-control" disabled>
                        <option>— Companion — (select a subject first)</option>
                      </select>
                    )}
                  </div>
                  <select
                    className="form-control"
                    value={row.mode || 'online'}
                    onChange={e => updateRow(i, 'mode', e.target.value)}
                    style={{ flex: '0 0 110px', minWidth: 110 }}
                  >
                    <option value="online">🌐 Online</option>
                    <option value="offline">🏫 Offline</option>
                  </select>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Custom rate (₹/hr)"
                    value={row.custom_rate || ''}
                    onChange={e => updateRow(i, 'custom_rate', e.target.value)}
                    style={{ flex: '0 0 150px', minWidth: 150 }}
                    min="0"
                    step="0.01"
                    title="Leave blank to use default class rate"
                  />
                  {rows.length > 1 && (
                    <button className="btn btn-ghost btn-sm" onClick={() => removeRow(i)} title="Remove row">×</button>
                  )}
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" style={{ marginTop: '4px' }} onClick={addRow}>
                ＋ Add Another Subject
              </button>
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '16px' }}
              onClick={submit}
              disabled={loading}
            >
              {loading ? 'Creating...' : `Create ${rows.length} Enrollment${rows.length > 1 ? 's' : ''}`}
            </button>
          </div>

          <div className="card">
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
              <div className="card-title" style={{ margin:0 }}>Active Enrollments ({enrollments.filter(e => e.status !== 'inactive').length})</div>
              <input
                className="form-control"
                style={{ width:220, fontSize:13, padding:'6px 12px' }}
                placeholder="Search by student or Companion…"
                value={enrollTutorSearch}
                onChange={e => setEnrollTutorSearch(e.target.value)}
              />
            </div>
            {teamsOn && (
              <div style={{ fontSize:12.5, color:'#4247a6', background:'#ecedfa', border:'1px solid #d5d7f2', borderRadius:10, padding:'9px 13px', marginBottom:12 }}>
                🎥 Online classes now run on Microsoft Teams — a recorded meeting is created automatically for each session, so no manual class link is needed.
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead><tr><th>Student</th><th>Subject</th><th>Companion</th><th>Mode</th><th>Rate (₹/hr)</th><th>Class Link</th><th>Action</th></tr></thead>
                <tbody>
                  {enrollments.filter(e => e.status !== 'inactive').length === 0
                    ? <tr><td colSpan="7" className="text-muted text-small" style={{ textAlign: 'center', padding: '20px' }}>No enrollments yet</td></tr>
                    : enrollments
                      .filter(e => e.status !== 'inactive')
                      .filter(e => !enrollTutorSearch || (e.tutor_name || '').toLowerCase().includes(enrollTutorSearch.toLowerCase()) || (e.student_name || '').toLowerCase().includes(enrollTutorSearch.toLowerCase()))
                      .map(e => (
                      <tr key={e.id}>
                        <td>{e.student_name || e.student_id}</td>
                        <td>{e.subject}</td>
                        <td>{e.tutor_name || e.tutor_id}</td>
                        <td>
                          <button
                            title="Click to switch Online/Offline — re-prices this enrollment's sessions in payment reports"
                            onClick={async () => {
                              const next = e.mode === 'online' ? 'offline' : 'online';
                              if (!window.confirm(`Change ${e.student_name}'s ${e.subject} to ${next.toUpperCase()}?\n\nThis re-prices ALL of this enrollment's sessions (past and future) at the ${next} rate in payment & profit reports.`)) return;
                              try {
                                await API.patch(`/enrollment/${e.id}`, { mode: next });
                                load();
                              } catch (err) { alert(err.response?.data?.detail || 'Error changing mode'); }
                            }}
                            style={{ fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:10, cursor:'pointer', border:`1px solid ${e.mode === 'online' ? '#93c5fd' : '#86efac'}`, background: e.mode === 'online' ? '#dbeafe' : '#dcfce7', color: e.mode === 'online' ? '#1e40af' : '#16a34a' }}>
                            {e.mode === 'online' ? '🌐 Online' : '🏫 Offline'} ⇄
                          </button>
                        </td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <input
                              type="number" min="0" step="0.01"
                              defaultValue={e.custom_rate || ''}
                              placeholder="Default"
                              title="Leave blank to use default class rate"
                              style={{ width:90, padding:'3px 7px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13, outline:'none' }}
                              onBlur={async (ev) => {
                                const val = ev.target.value;
                                try {
                                  await API.patch(`/enrollment/${e.id}`, { custom_rate: val ? parseFloat(val) : 0 });
                                  load();
                                } catch (err) { alert(err.response?.data?.detail || 'Error updating rate'); }
                              }}
                            />
                            {e.custom_rate ? <span style={{ fontSize:10, color:'#7c3aed', fontWeight:700 }}>★ Custom</span> : <span style={{ fontSize:10, color:'#94a3b8' }}>Default</span>}
                          </div>
                        </td>
                        <td>
                          {e.mode === 'online' ? (
                            teamsOn
                              ? <span title="A Teams meeting is created automatically for each class — no manual link needed." style={{ fontSize:11, fontWeight:700, color:'#5b5fc7', background:'#ecedfa', border:'1px solid #d5d7f2', borderRadius:8, padding:'3px 9px', display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>🎥 Teams · auto</span>
                              : <JoiningLinkCell enrollment={e} onSaved={load} />
                          ) : <span style={{ fontSize:11, color:'#94a3b8' }}>—</span>}
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <label title="Upload student plan (PDF/DOC/DOCX)"
                              style={{ background:'none', border:`1px solid ${e.has_plan ? '#86efac' : '#e2e8f0'}`, color: e.has_plan ? '#16a34a' : '#64748b', borderRadius:6, padding:'3px 10px', fontSize:12, cursor:'pointer', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}>
                              📄 {e.has_plan ? e.plan_file_name?.slice(0,12)+'…' : 'Upload Plan'}
                              <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                style={{ display:'none' }}
                                onChange={async (ev) => {
                                  const file = ev.target.files[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  try {
                                    await API.post(`/enrollment/${e.id}/upload-plan`, fd, { headers:{'Content-Type':'multipart/form-data'} });
                                    load();
                                  } catch(err) { alert(err?.response?.data?.detail || 'Upload failed'); }
                                  ev.target.value = '';
                                }} />
                            </label>
                            <button
                              className="btn"
                              style={{ background: 'none', border: '1px solid #93c5fd', color: '#2554b8', borderRadius: 6, padding: '3px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}
                              onClick={() => { setEditSubjectModal(e); setEditSubjectValue(e.subject); }}
                            >
                              Edit Subject
                            </button>
                            <button
                              className="btn"
                              style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 6, padding: '3px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}
                              onClick={async () => {
                                if (!window.confirm(`Remove enrollment for ${e.student_name || e.student_id} — ${e.subject}?`)) return;
                                try {
                                  await API.delete(`/enrollment/${e.id}`);
                                  load();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Error removing enrollment');
                                }
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            {/* Edit Subject Modal */}
            {editSubjectModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
                onClick={() => setEditSubjectModal(null)}>
                <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:380, boxShadow:'0 20px 60px rgba(0,0,0,0.18)' }}>
                  <h3 style={{ fontWeight:800, fontSize:16, marginBottom:16 }}>Edit Subject</h3>
                  <p style={{ fontSize:13, color:'#64748b', marginBottom:14 }}>Student: <strong>{editSubjectModal.student_name}</strong></p>
                  <div style={{ marginBottom:16 }}>
                    <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>New Subject</label>
                    <input className="form-control" value={editSubjectValue} onChange={e => setEditSubjectValue(e.target.value)} placeholder="e.g. Mathematics" />
                  </div>
                  <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                    <button onClick={() => setEditSubjectModal(null)} style={{ padding:'8px 18px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:700, fontSize:13 }}>Cancel</button>
                    <button onClick={async () => {
                      if (!editSubjectValue.trim()) return;
                      try {
                        await API.patch(`/enrollment/${editSubjectModal.id}`, { subject: editSubjectValue.trim() });
                        setEditSubjectModal(null);
                        load();
                      } catch (err) { alert(err.response?.data?.detail || 'Error updating subject'); }
                    }} style={{ padding:'8px 18px', borderRadius:8, border:'none', background:'#2554b8', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:13 }}>Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Schedule ───────────────────────────────────────────────────────────────────
const emptySlot = () => ({ enrollment_id: '', student_id: '', start_time: '', end_time: '', days: [] });

function ScheduleManager() {
  const [enrollments, setEnrollments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [dateRange, setDateRange] = useState({ start_date: '', end_date: '' });
  const [subjectSlots, setSubjectSlots] = useState([emptySlot()]);
  const [manualForm, setManualForm] = useState({ enrollment_id: '', student_id: '', date: '', start_time: '', end_time: '' });
  const [msg, setMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const [showSessionSuggestions, setShowSessionSuggestions] = useState(false);
  const [sessionFromDate, setSessionFromDate] = useState('');
  const [sessionToDate, setSessionToDate] = useState('');
  // Admin mark-completed modal
  const [adminCompleteModal, setAdminCompleteModal] = useState(null); // { schedule }
  const [adminCompleteForm, setAdminCompleteForm] = useState({ topics:'', homework:'', homework_due_date:'', remarks:'' });
  const [adminCompleteBusy, setAdminCompleteBusy] = useState(false);

  const openAdminCompleteModal = (s) => {
    setAdminCompleteForm({ topics:'', homework:'', homework_due_date:'', remarks:'' });
    setAdminCompleteModal({ schedule: s });
  };

  const submitAdminComplete = async () => {
    if (!adminCompleteModal) return;
    if (!window.confirm("Mark this session as Completed?\n\nThis will deduct hours from the student's cycle. The Companion will NOT be paid for this session unless you later click 'Add to Tutor Payment'.")) return;
    setAdminCompleteBusy(true);
    try {
      await API.post(`/class/admin/mark-completed/${adminCompleteModal.schedule.id}`, {
        topics: adminCompleteForm.topics,
        homework: adminCompleteForm.homework,
        homework_due_date: adminCompleteForm.homework_due_date || null,
        remarks: adminCompleteForm.remarks,
      });
      setMsg(`✓ Marked ${adminCompleteModal.schedule.student_name || 'session'} as completed (Companion pay pending approval).`);
      setAdminCompleteModal(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to mark completed');
    } finally {
      setAdminCompleteBusy(false);
    }
  };

  const matchSession = (s) => {
    if (sessionSearch) {
      const q = sessionSearch.toLowerCase();
      const hit = (s.student_name || '').toLowerCase().includes(q) || (s.tutor_name || '').toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (sessionFromDate && s.date < sessionFromDate) return false;
    if (sessionToDate && s.date > sessionToDate) return false;
    return true;
  };

  const load = useCallback(() => {
    API.get('/enrollment/').then(r => setEnrollments(r.data)).catch(() => {});
    API.get('/schedule/').then(r => setSchedules(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSlotDay = (idx, day) => {
    setSubjectSlots(prev => prev.map((s, i) => i === idx
      ? { ...s, days: s.days.includes(day) ? s.days.filter(d => d !== day) : [...s.days, day] }
      : s));
  };

  const updateSlot = (idx, field, value) => {
    setSubjectSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addSlot    = () => setSubjectSlots(prev => [...prev, emptySlot()]);
  const removeSlot = (idx) => setSubjectSlots(prev => prev.filter((_, i) => i !== idx));

  const generate = async () => {
    if (!dateRange.start_date || !dateRange.end_date) { setMsg('Please set start and end dates'); return; }
    const valid = subjectSlots.filter(s => s.enrollment_id && s.start_time && s.end_time && s.days.length > 0);
    if (valid.length === 0) { setMsg('Each subject needs an enrollment, times, and at least one day selected'); return; }

    // Validate max 2-hour duration for each slot
    for (let i = 0; i < valid.length; i++) {
      const slot = valid[i];
      if (slot.start_time && slot.end_time) {
        const [sh, sm] = slot.start_time.split(':').map(Number);
        const [eh, em] = slot.end_time.split(':').map(Number);
        const mins = (eh * 60 + em) - (sh * 60 + sm);
        if (mins > 120) { setMsg(`Subject ${i + 1}: Session duration cannot exceed 2 hours (currently ${Math.floor(mins/60)}h ${mins%60}m)`); return; }
      }
    }

    setGenerating(true);
    setMsg('');
    const results = [];
    for (const slot of valid) {
      try {
        const res = await API.post('/schedule/generate', {
          enrollment_id: parseInt(slot.enrollment_id),
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          start_time: slot.start_time,
          end_time: slot.end_time,
          days: slot.days,
        });
        const label = enrollments.find(e => e.id === parseInt(slot.enrollment_id));
        results.push(`✓ ${label?.subject || 'Enrollment'}: ${res.data.message}`);
      } catch (e) {
        const label = enrollments.find(e => e.id === parseInt(slot.enrollment_id));
        const det = e.response?.data?.detail;
        const errMsg = Array.isArray(det) ? det.map(d => d.msg || JSON.stringify(d)).join(', ') : (typeof det === 'string' ? det : JSON.stringify(det)) || 'Error';
        results.push(`✗ ${label?.subject || 'Enrollment'}: ${errMsg}`);
      }
    }
    setMsg(results.join(' | '));
    setSubjectSlots([emptySlot()]);
    setGenerating(false);
    load();
  };

  const addManual = async () => {
    if (!manualForm.enrollment_id) { setMsg('Please select an enrollment'); return; }
    if (manualForm.start_time && manualForm.end_time) {
      const [sh, sm] = manualForm.start_time.split(':').map(Number);
      const [eh, em] = manualForm.end_time.split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins > 120) { setMsg(`Session duration cannot exceed 2 hours (currently ${Math.floor(mins/60)}h ${mins%60}m)`); return; }
    }
    try {
      await API.post('/schedule/manual', { ...manualForm, enrollment_id: parseInt(manualForm.enrollment_id) });
      const label = enrollments.find(e => e.id === parseInt(manualForm.enrollment_id));
      setMsg(`✓ Session added for ${label?.subject || 'enrollment'} — you can add another below.`);
      setManualForm(f => ({ ...f, enrollment_id: '', date: '' }));
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      setMsg(Array.isArray(det) ? det.map(d => d.msg || JSON.stringify(d)).join(', ') : (typeof det === 'string' ? det : JSON.stringify(det)) || 'Error');
    }
  };

  const deleteSession = async (id) => {
    try {
      await API.delete(`/schedule/${id}`);
      load();
    } catch (e) {}
  };

  const togglePayable = async (id, newIsPayable) => {
    try {
      await API.patch(`/schedule/${id}/payable?is_payable=${newIsPayable}`);
      setSchedules(prev => prev.map(s => s.id === id ? { ...s, is_payable: newIsPayable } : s));
    } catch (e) {}
  };

  const joinClass = async (id) => {
    const w = window.open('', '_blank');
    try {
      const r = await API.get(`/teams/join/${id}`);
      const url = r.data?.join_url;
      if (url) { if (w) w.location.href = url; else window.location.href = url; }
      else { if (w) w.close(); alert('Class link is not ready yet. Please try again in a moment.'); }
    } catch (e) { if (w) w.close(); alert(e.response?.data?.detail || 'Class link is not ready yet. Please try again in a moment.'); }
  };

  // Admin Join button only shows for TODAY's online classes.
  const _pad = (n) => String(n).padStart(2, '0');
  const _now = new Date();
  const todayStr = `${_now.getFullYear()}-${_pad(_now.getMonth() + 1)}-${_pad(_now.getDate())}`;

  const badgeFor = (s) => {
    const map = { scheduled: 'badge-scheduled', completed: 'badge-completed', cancelled: 'badge-cancelled' };
    return map[s] || 'badge-pending';
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Timetable Manager</h2><p>Auto-generate or manually manage sessions — add multiple subjects without leaving this page</p></div>
      </div>
      <div className="page-body">
        {msg && (
          <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>
            {msg}
          </div>
        )}

        <div className="grid-2 mb-2">
          {/* Auto-generate */}
          <div className="card">
            <div className="card-title">🔄 Auto-Generate Schedule</div>

            {/* Shared date range */}
            <div className="grid-2">
              <div className="form-group">
                <label>Start Date</label>
                <input className="form-control" type="date" value={dateRange.start_date} onChange={e => setDateRange(d => ({ ...d, start_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input className="form-control" type="date" value={dateRange.end_date} onChange={e => setDateRange(d => ({ ...d, end_date: e.target.value }))} />
              </div>
            </div>

            {/* Per-subject slots */}
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <label style={{ margin: 0 }}>Subjects &amp; Timings</label>
                <button className="btn btn-ghost btn-sm" onClick={addSlot}>+ Add Subject</button>
              </div>

              {subjectSlots.map((slot, idx) => (
                <div key={idx} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>SUBJECT {idx + 1}</span>
                    {subjectSlots.length > 1 && (
                      <button onClick={() => removeSlot(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
                    )}
                  </div>
                  {/* Step 1: pick student */}
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Student</label>
                    <SearchableSelect
                      options={[...new Map(enrollments.filter(e => e.status !== 'inactive').map(e => [e.student_id, e])).values()].map(e => ({ value: e.student_id, label: e.student_name }))}
                      value={slot.student_id}
                      onChange={v => setSubjectSlots(prev => prev.map((s, i) => i === idx ? { ...s, student_id: v, enrollment_id: '' } : s))}
                      placeholder="— Select Student —"
                    />
                  </div>
                  {/* Step 2: pick subject (filtered by student) */}
                  {slot.student_id && (
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Subject</label>
                      <select className="form-control" value={slot.enrollment_id} onChange={e => updateSlot(idx, 'enrollment_id', e.target.value)}>
                        <option value="">— Select Subject —</option>
                        {enrollments.filter(e => e.status !== 'inactive' && String(e.student_id) === String(slot.student_id)).map(e => (
                          <option key={e.id} value={e.id}>{e.subject} (with {e.tutor_name})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Class Days</label>
                    <div className="checkbox-group">
                      {DAYS.map(day => (
                        <div key={day} className={`checkbox-pill ${slot.days.includes(day) ? 'checked' : ''}`} onClick={() => toggleSlotDay(idx, day)}>
                          {day.slice(0, 3)}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid-2" style={{ marginBottom: 0 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Start Time</label>
                      <TimePicker12h className="form-control" value={slot.start_time} onChange={v => updateSlot(idx, 'start_time', v)} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>End Time</label>
                      <TimePicker12h className="form-control" value={slot.end_time} onChange={v => updateSlot(idx, 'end_time', v)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="btn btn-primary" style={{ width: '100%' }} onClick={generate} disabled={generating}>
              {generating ? '⏳ Generating…' : `⚡ Generate Schedule (${subjectSlots.filter(s => s.enrollment_id).length} subject${subjectSlots.filter(s => s.enrollment_id).length !== 1 ? 's' : ''})`}
            </button>
          </div>

          {/* Manual add */}
          <div className="card">
            <div className="card-title">✏️ Add Single Session Manually</div>
            <div className="form-group">
              <label>Student</label>
              <SearchableSelect
                options={[...new Map(enrollments.filter(e => e.status !== 'inactive').map(e => [e.student_id, e])).values()].map(e => ({ value: e.student_id, label: e.student_name }))}
                value={manualForm.student_id}
                onChange={v => setManualForm({ ...manualForm, student_id: v, enrollment_id: '' })}
                placeholder="— Select Student —"
              />
            </div>
            {manualForm.student_id && (
              <div className="form-group">
                <label>Subject</label>
                <select className="form-control" value={manualForm.enrollment_id} onChange={e => setManualForm({ ...manualForm, enrollment_id: e.target.value })}>
                  <option value="">— Select Subject —</option>
                  {enrollments.filter(e => e.status !== 'inactive' && String(e.student_id) === String(manualForm.student_id)).map(e => (
                    <option key={e.id} value={e.id}>{e.subject} (with {e.tutor_name})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Date</label>
              <input className="form-control" type="date" value={manualForm.date} onChange={e => setManualForm({ ...manualForm, date: e.target.value })} />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label>Start Time</label>
                <TimePicker12h className="form-control" value={manualForm.start_time} onChange={v => setManualForm({ ...manualForm, start_time: v })} />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <TimePicker12h className="form-control" value={manualForm.end_time} onChange={v => setManualForm({ ...manualForm, end_time: v })} />
              </div>
            </div>
            <button className="btn btn-success" style={{ width: '100%', marginTop: '8px' }} onClick={addManual}>＋ Add Session</button>
          </div>
        </div>

        {/* Schedule list */}
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:12 }}>
            <div className="card-title" style={{ margin:0 }}>
              All Sessions ({schedules.filter(matchSession).length}{(sessionSearch || sessionFromDate || sessionToDate) ? ` of ${schedules.length}` : ''})
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                <label style={{ fontSize:11, color:'#64748b', fontWeight:700 }}>From</label>
                <input type="date" value={sessionFromDate} onChange={e => setSessionFromDate(e.target.value)}
                  style={{ padding:'6px 8px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:12, outline:'none' }} />
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                <label style={{ fontSize:11, color:'#64748b', fontWeight:700 }}>To</label>
                <input type="date" value={sessionToDate} onChange={e => setSessionToDate(e.target.value)}
                  style={{ padding:'6px 8px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:12, outline:'none' }} />
              </div>
              {(sessionFromDate || sessionToDate) && (
                <button onClick={() => { setSessionFromDate(''); setSessionToDate(''); }}
                  style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #cbd5e1', background:'#fff', color:'#64748b', fontSize:11, fontWeight:700, cursor:'pointer' }}
                  title="Clear date filter">✕ Dates</button>
              )}
            <div style={{ position:'relative', maxWidth:240 }}>
              <input
                className="form-control"
                style={{ width:'100%', margin:0 }}
                placeholder="🔍 Search by student or companion name…"
                value={sessionSearch}
                autoComplete="off"
                onChange={e => { setSessionSearch(e.target.value); setShowSessionSuggestions(true); }}
                onFocus={() => setShowSessionSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSessionSuggestions(false), 180)}
              />
              {showSessionSuggestions && sessionSearch && (() => {
                const q = sessionSearch.toLowerCase();
                const suggestions = new Map();
                for (const s of schedules) {
                  if (s.student_name && s.student_name.toLowerCase().includes(q)) {
                    suggestions.set(`s:${s.student_name}`, { name: s.student_name, role: 'Student' });
                  }
                  if (s.tutor_name && s.tutor_name.toLowerCase().includes(q)) {
                    suggestions.set(`t:${s.tutor_name}`, { name: s.tutor_name, role: 'Companion' });
                  }
                }
                const list = [...suggestions.values()].sort((a, b) => a.name.localeCompare(b.name));
                if (!list.length) return null;
                return (
                  <div style={{ position:'absolute', top:'100%', right:0, left:0, background:'#fff', border:'1px solid #dbe1f0', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:200, maxHeight:220, overflowY:'auto' }}>
                    {list.map(item => (
                      <div key={`${item.role}:${item.name}`} onMouseDown={() => { setSessionSearch(item.name); setShowSessionSuggestions(false); }}
                        style={{ padding:'9px 14px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                        onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                        <strong>{item.name}</strong>
                        <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background: item.role === 'Student' ? '#dcfce7' : '#dbeafe', color: item.role === 'Student' ? '#15803d' : '#1e40af' }}>{item.role}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Day</th><th>Subject</th><th>Student</th><th>Companion</th><th>Time</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {schedules.length === 0
                  ? <tr><td colSpan="8"><div className="empty-state"><div className="empty-icon">📅</div><p>No sessions yet</p></div></td></tr>
                  : schedules.filter(matchSession).length === 0
                  ? <tr><td colSpan="8"><div className="empty-state"><div className="empty-icon">🔍</div><p>No sessions match your filters</p></div></td></tr>
                  : schedules.filter(matchSession).map(s => (
                    <tr key={s.id}>
                      <td className="mono">{s.date}</td>
                      <td style={{ fontSize:12, color:'var(--text-muted)', fontWeight:600 }}>{s.date ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(s.date + 'T00:00:00').getDay()] : '—'}</td>
                      <td>{s.subject}</td>
                      <td>{s.student_name || '—'}</td>
                      <td>{s.tutor_name || '—'}</td>
                      <td className="mono text-muted">{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</td>
                      <td>
                        {s.status === 'cancelled' && s.cancel_reason === 'tutor_leave'
                          ? <span className="badge" style={{ background:'rgba(245,158,11,0.1)', color:'#b45309', border:'1px solid rgba(245,158,11,0.3)' }}>🏖 Tutor Leave</span>
                          : s.status === 'cancelled' && s.cancel_reason === 'student_leave'
                          ? <span className="badge" style={{ background:'rgba(124,58,237,0.1)', color:'#7c3aed', border:'1px solid rgba(124,58,237,0.3)' }}>📝 Student Leave</span>
                          : s.status === 'cancelled' && s.cancel_reason === 'student_absent'
                          ? <span className="badge" style={{ background:'rgba(37,84,184,0.1)', color:'#2554b8', border:'1px solid rgba(37,84,184,0.3)' }}>👤 Student Absent</span>
                          : s.status === 'cancelled' && s.cancel_reason === 'emergency'
                          ? <span className="badge" style={{ background:'rgba(220,38,38,0.1)', color:'#dc2626', border:'1px solid rgba(220,38,38,0.3)' }}>🚨 Emergency</span>
                          : <span className={`badge ${badgeFor(s.status)}`}>{s.status}</span>
                        }
                        {s.is_payable === false && (
                          <span style={{ display:'inline-block', marginLeft:4, padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, background:'#dc262618', color:'#dc2626', border:'1px solid #dc262630' }}>NOT PAYABLE</span>
                        )}
                      </td>
                      <td style={{ whiteSpace:'nowrap', display:'flex', gap:6, alignItems:'center' }}>
                        {s.status === 'scheduled' && (
                          <>
                            {s.mode === 'online' && s.date === todayStr && (
                              <button
                                onClick={() => joinClass(s.id)}
                                title="Join this class as admin (today's classes)"
                                style={{ padding:'5px 10px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:11, boxShadow:'0 2px 6px rgba(37,84,184,0.25)' }}>
                                🔗 Join
                              </button>
                            )}
                            <button
                              onClick={() => openAdminCompleteModal(s)}
                              title="Mark this session as completed on behalf of the Companion"
                              style={{ padding:'5px 10px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:11, boxShadow:'0 2px 6px rgba(22,163,74,0.25)' }}>
                              ✓ Mark Completed
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => deleteSession(s.id)}>🗑</button>
                          </>
                        )}
                        {(s.status === 'completed' || s.status === 'cancelled') && (
                          <button className="btn btn-ghost btn-sm"
                            style={{ color: s.is_payable === false ? '#16a34a' : '#dc2626', fontSize:11, fontWeight:700 }}
                            onClick={() => togglePayable(s.id, s.is_payable === false)}>
                            {s.is_payable === false ? '✓ Mark Payable' : '✕ Not Payable'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Admin Mark Completed modal */}
      {adminCompleteModal && (
        <div onClick={() => !adminCompleteBusy && setAdminCompleteModal(null)}
             style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200, padding:16 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background:'#fff', borderRadius:16, padding:24, maxWidth:560, width:'100%', boxShadow:'0 20px 50px rgba(15,31,69,0.25)' }}>
            <h3 style={{ margin:0, fontSize:17, fontWeight:800, color:'#0f1f45' }}>✓ Mark Session Completed</h3>
            <p style={{ fontSize:12, color:'#64748b', marginTop:4, marginBottom:14 }}>
              {adminCompleteModal.schedule.student_name} · {adminCompleteModal.schedule.subject} · {adminCompleteModal.schedule.date} · {adminCompleteModal.schedule.tutor_name}
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', display:'block', marginBottom:4 }}>Topics covered (optional)</label>
                <textarea className="form-control" rows={2} value={adminCompleteForm.topics}
                  onChange={e => setAdminCompleteForm(f => ({ ...f, topics: e.target.value }))}
                  placeholder="Leave blank if unknown" />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', display:'block', marginBottom:4 }}>Homework (optional)</label>
                  <input className="form-control" value={adminCompleteForm.homework}
                    onChange={e => setAdminCompleteForm(f => ({ ...f, homework: e.target.value }))}
                    placeholder="Leave blank if unknown" />
                </div>
                <div style={{ flex:'0 0 150px' }}>
                  <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', display:'block', marginBottom:4 }}>Due date</label>
                  <input className="form-control" type="date" value={adminCompleteForm.homework_due_date}
                    onChange={e => setAdminCompleteForm(f => ({ ...f, homework_due_date: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', display:'block', marginBottom:4 }}>Remarks (optional)</label>
                <input className="form-control" value={adminCompleteForm.remarks}
                  onChange={e => setAdminCompleteForm(f => ({ ...f, remarks: e.target.value }))}
                  placeholder='e.g. "Marked by admin — companion forgot to log"' />
              </div>
            </div>
            <div style={{ padding:'10px 14px', marginTop:14, borderRadius:10, background:'rgba(217,119,6,0.08)', border:'1px solid rgba(217,119,6,0.25)', color:'#b45309', fontSize:12, lineHeight:1.5 }}>
              ⚠ This session will deduct from the student's cycle. <b>The Companion will NOT be paid</b> unless you later click <b>Add to Tutor Payment</b> in the Class Logs page.
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16 }}>
              <button onClick={() => setAdminCompleteModal(null)} disabled={adminCompleteBusy} className="btn btn-ghost">Cancel</button>
              <button onClick={submitAdminComplete} disabled={adminCompleteBusy}
                style={{ padding:'9px 18px', borderRadius:10, border:'none', background: adminCompleteBusy ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:700, cursor: adminCompleteBusy ? 'wait' : 'pointer', fontSize:13 }}>
                {adminCompleteBusy ? '⏳ Saving…' : '✓ Mark Completed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Class Logs ─────────────────────────────────────────────────────────────────
function ClassLogs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [approving, setApproving] = useState(null);

  // ── Student progress PDF: state ──
  const [students, setStudents] = useState([]);
  const [reportStudentId, setReportStudentId] = useState('');
  const [reportStudentSearch, setReportStudentSearch] = useState('');
  const [reportStudentListOpen, setReportStudentListOpen] = useState(false);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg] = useState('');

  const reloadLogs = useCallback(() => {
    API.get('/class/logs').then(r => setLogs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    reloadLogs();
    API.get('/auth/users/student?active=true').then(r => setStudents(r.data || [])).catch(() => {});
  }, [reloadLogs]);

  const approveTutorPayment = async (log) => {
    if (log.id < 0) return; // synthetic row, no real log
    if (!window.confirm(`Add this session to ${log.tutor_name}'s payment?\n\nThis session will be included in their next payment report.`)) return;
    setApproving(log.id);
    try {
      await API.post(`/class/admin/approve-tutor-payment/${log.id}`);
      reloadLogs();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to approve'); }
    finally { setApproving(null); }
  };

  const unapproveTutorPayment = async (log) => {
    if (log.id < 0) return;
    if (!window.confirm("Undo Companion payment for this session?\n\nOnly possible within 24 hours of approval.")) return;
    setApproving(log.id);
    try {
      await API.post(`/class/admin/unapprove-tutor-payment/${log.id}`);
      reloadLogs();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to undo'); }
    finally { setApproving(null); }
  };

  const isUndoOpen = (log) => {
    if (!log.tutor_payment_approved || !log.tutor_payment_approved_at) return false;
    const approvedAt = new Date(log.tutor_payment_approved_at + (log.tutor_payment_approved_at.endsWith('Z') ? '' : 'Z'));
    return (Date.now() - approvedAt.getTime()) < 24 * 60 * 60 * 1000;
  };

  const filteredStudents = students.filter(s => {
    const q = reportStudentSearch.toLowerCase().trim();
    if (!q) return true;
    return (s.name || '').toLowerCase().includes(q) || String(s.id).includes(q);
  });
  const selectedStudent = students.find(s => s.id === Number(reportStudentId));

  const downloadStudentReport = async () => {
    if (!reportStudentId) { setReportMsg('Pick a student first'); return; }
    setReportBusy(true);
    setReportMsg('');
    try {
      const sid = Number(reportStudentId);
      // Pull everything in parallel — class logs is admin-wide so we filter here;
      // tests/for-student and tutor-feedback are scoped server-side.
      const [allLogsR, gradesR, feedbackR, hoursR] = await Promise.all([
        API.get('/class/logs'),
        API.get(`/tests/for-student/${sid}`).catch(() => ({ data: [] })),
        API.get('/tutor-feedback/mine').catch(() => ({ data: [] })),
        API.get('/bills/overview').catch(() => ({ data: [] })),
      ]);

      const studentName = selectedStudent?.name || '';
      const studentLogs = (allLogsR.data || []).filter(l => l.student_name === studentName);
      const studentFeedback = (feedbackR.data || []).filter(f => f.student_id === sid);

      // /tests/for-student returns tests with my-grade fields; map into the
      // shape generateStudentPDF expects (grades list with marks_obtained etc.).
      const grades = (gradesR.data || []).map(t => ({
        test_name: t.test_name || t.name || '—',
        subject: t.subject || '—',
        test_date: t.test_date || t.date || '',
        marks_obtained: t.marks_obtained ?? null,
        max_marks: t.max_marks ?? t.total_marks ?? 0,
        remarks: t.remarks || '',
      }));

      const hoursRow = (hoursR.data || []).find(h => h.student_id === sid) || null;
      // Same shape as the student's own /payments/my-hours, so the PDF reads both.
      const hours = hoursRow ? {
        billing_type: hoursRow.billing_type,
        this_month_hours: hoursRow.this_month_hours,
        month_label: new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        hours_used: hoursRow.current_bill?.used ?? 0,
        hours_per_month: hoursRow.current_bill?.hours ?? 0,
        cycle_start: hoursRow.current_bill?.since ?? null,
      } : null;

      const profile = selectedStudent ? {
        name: selectedStudent.name,
        class_name: selectedStudent.class_name,
        syllabus: selectedStudent.syllabus,
        package: selectedStudent.package,
        selected_subjects: selectedStudent.selected_subjects,
      } : { name: studentName };

      const filteredLogs = reportFrom || reportTo
        ? studentLogs.filter(l => {
            const d = l.schedule_date || l.updated_at?.slice(0, 10) || '';
            return (!reportFrom || d >= reportFrom) && (!reportTo || d <= reportTo);
          })
        : studentLogs;
      const filteredGrades = reportFrom || reportTo
        ? grades.filter(g => {
            const d = g.test_date || '';
            return (!reportFrom || d >= reportFrom) && (!reportTo || d <= reportTo);
          })
        : grades;
      const filteredFeedback = reportFrom || reportTo
        ? studentFeedback.filter(f => (!reportFrom || f.month >= reportFrom.slice(0,7)) && (!reportTo || f.month <= reportTo.slice(0,7)))
        : studentFeedback;

      await generateStudentPDF({
        profile,
        logs: filteredLogs,
        grades: filteredGrades,
        hours,
        feedback: filteredFeedback,
        dateRange: reportFrom || reportTo ? `${reportFrom || 'start'} to ${reportTo || 'today'}` : null,
      });
      setReportMsg('✓ Report downloaded');
    } catch (e) {
      console.error(e);
      setReportMsg('Error generating report');
    }
    setReportBusy(false);
  };

  const filtered = logs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q || (l.student_name || '').toLowerCase().includes(q) || (l.tutor_name || '').toLowerCase().includes(q) || (l.subject || '').toLowerCase().includes(q);
    const logDate = l.schedule_date || l.updated_at?.slice(0, 10);
    const matchFrom = !dateFrom || (logDate && logDate >= dateFrom);
    const matchTo = !dateTo || (logDate && logDate <= dateTo);
    return matchSearch && matchFrom && matchTo;
  });

    // Session length from start → end time, e.g. "1h", "1h 30m".
  const csvDuration = (l) => {
    const toMin = (t) => {
      if (!t) return null;
      const [h, m] = String(t).split(':');
      const hh = parseInt(h, 10), mm = parseInt(m, 10);
      return (Number.isNaN(hh) || Number.isNaN(mm)) ? null : hh * 60 + mm;
    };
    const s = toMin(l.start_time), e = toMin(l.end_time);
    if (s === null || e === null || e <= s) return '';
    const mins = e - s, h = Math.floor(mins / 60), m = mins % 60;
    return (h ? `${h}h` : '') + (m ? `${h ? ' ' : ''}${m}m` : (h ? '' : `${mins}m`));
  };

  const downloadCSV = () => {
    const headers = ['Session ID','Student','Tutor','Subject','Status','Date','Duration','Topics','Homework','Remarks'];
    const rows = filtered.map(l => [
      l.schedule_id,
      l.student_name || '',
      l.tutor_name || '',
      l.subject || '',
      l.status,
      l.schedule_date || l.updated_at?.slice(0,10) || '',
      csvDuration(l),
      (l.topics || '').replace(/,/g,'|'),
      (l.homework || '').replace(/,/g,'|'),
      (l.remarks || '').replace(/,/g,'|'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'class_logs.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Class Logs</h2><p>Class completion records submitted by Companions</p></div>
      </div>
      <div className="page-body">
        {/* ── Download Student Progress Report ────────────────────────── */}
        <div className="card" style={{ marginBottom: 16, background:'linear-gradient(135deg, #eef3ff 0%, #f5f3ff 100%)', border:'1px solid #c7d4f5' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ fontSize:22 }}>📥</div>
            <div>
              <div style={{ fontWeight:800, color:'#0f1f45', fontSize:15 }}>Download Student Progress Report</div>
              <div style={{ fontSize:12, color:'#64748b' }}>Same template students see in their dashboard — pick a student and date range.</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div style={{ flex:'1 1 240px', minWidth:200, position:'relative' }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Student</label>
              <input
                className="form-control"
                placeholder="Type student name…"
                value={selectedStudent ? selectedStudent.name : reportStudentSearch}
                onChange={e => { setReportStudentSearch(e.target.value); setReportStudentId(''); setReportStudentListOpen(true); }}
                onFocus={() => setReportStudentListOpen(true)}
                onBlur={() => setTimeout(() => setReportStudentListOpen(false), 180)}
              />
              {reportStudentListOpen && filteredStudents.length > 0 && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, marginTop:4, maxHeight:220, overflowY:'auto', zIndex:20, boxShadow:'0 8px 24px rgba(15,31,69,0.12)' }}>
                  {filteredStudents.slice(0, 50).map(s => (
                    <div key={s.id}
                         onMouseDown={() => { setReportStudentId(String(s.id)); setReportStudentSearch(s.name); setReportStudentListOpen(false); }}
                         style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', fontSize:13 }}
                         onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                         onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                      <div style={{ fontWeight:600, color:'#0f1f45' }}>{s.name}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{s.class_name || ''} {s.syllabus ? `· ${s.syllabus}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flex:'0 0 150px' }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>From</label>
              <input className="form-control" type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} />
            </div>
            <div style={{ flex:'0 0 150px' }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>To</label>
              <input className="form-control" type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" disabled={reportBusy || !reportStudentId} onClick={downloadStudentReport}>
              {reportBusy ? '⏳ Generating…' : '📥 Download PDF'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setReportStudentId(''); setReportStudentSearch(''); setReportFrom(''); setReportTo(''); setReportMsg(''); }}>× Clear</button>
            {reportMsg && <span style={{ marginLeft:8, fontSize:12, color: reportMsg.startsWith('✓') ? '#16a34a' : '#dc2626', alignSelf:'center', fontWeight:600 }}>{reportMsg}</span>}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div style={{ flex:'1 1 200px', minWidth:160 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Search by Name / Subject</label>
              <input className="form-control" placeholder="Student, Companion or subject…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div style={{ flex:'0 0 150px' }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>From Date</label>
              <input className="form-control" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div style={{ flex:'0 0 150px' }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>To Date</label>
              <input className="form-control" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}>× Clear</button>
            <button className="btn btn-primary btn-sm" onClick={downloadCSV}>⬇ Download CSV</button>
            <span style={{ marginLeft:'auto', fontSize:12, color:'#64748b', alignSelf:'center' }}>{filtered.length} log{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>Student</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Topics Covered</th>
                  <th>Homework</th>
                  <th>Remarks</th>
                  <th>Companion</th>
                  <th>Companion Pay</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0
                  ? <tr><td colSpan="10"><div className="empty-state"><div className="empty-icon">📝</div><p>No class logs found</p></div></td></tr>
                  : filtered.map(l => (
                    <tr key={l.id}>
                      <td className="mono text-muted">#{l.schedule_id}</td>
                      <td>{l.student_name || '—'}</td>
                      <td>{l.subject || '—'}</td>
                      <td>{(() => {
                        if (l.status === 'completed') return (
                          <span style={{ display:'inline-flex', flexDirection:'column', gap:3, alignItems:'flex-start' }}>
                            <span className="badge badge-completed">Completed</span>
                            {l.marked_by_admin && (
                              <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:10, background:'rgba(124,58,237,0.1)', color:'#7c3aed', border:'1px solid rgba(124,58,237,0.25)' }}>👤 Marked by Admin</span>
                            )}
                          </span>
                        );
                        if (l.cancel_reason === 'tutor_leave') return <span className="badge badge-cancelled" style={{ background:'rgba(245,158,11,0.1)', color:'#b45309', border:'1px solid rgba(245,158,11,0.3)' }}>🏖 Tutor Leave</span>;
                        if (l.cancel_reason === 'student_leave') return <span className="badge badge-cancelled" style={{ background:'rgba(124,58,237,0.1)', color:'#7c3aed', border:'1px solid rgba(124,58,237,0.3)' }}>📝 Student Leave</span>;
                        if (l.cancel_reason === 'student_absent') return <span className="badge badge-cancelled" style={{ background:'rgba(37,84,184,0.1)', color:'#2554b8', border:'1px solid rgba(37,84,184,0.3)' }}>👤 Student Absent</span>;
                        if (l.cancel_reason === 'emergency') return <span className="badge badge-cancelled" style={{ background:'rgba(220,38,38,0.1)', color:'#dc2626', border:'1px solid rgba(220,38,38,0.3)' }}>🚨 Emergency</span>;
                        return <span className="badge badge-cancelled">Cancelled</span>;
                      })()}</td>
                      <td className="mono text-muted text-small">{l.schedule_date || l.updated_at?.slice(0,10) || '—'}</td>
                      <td style={{ maxWidth:240, whiteSpace:'pre-wrap', wordBreak:'break-word', verticalAlign:'top', fontSize:13, color:'#475569', lineHeight:1.45 }}>{l.topics || '—'}</td>
                      <td style={{ maxWidth:220, whiteSpace:'pre-wrap', wordBreak:'break-word', verticalAlign:'top', fontSize:13, color:'#475569', lineHeight:1.45 }}>{l.homework || '—'}</td>
                      <td style={{ maxWidth:220, whiteSpace:'pre-wrap', wordBreak:'break-word', verticalAlign:'top', fontSize:13, color:'#475569', lineHeight:1.45 }}>{l.remarks || '—'}</td>
                      <td>{l.tutor_name || '—'}</td>
                      <td style={{ whiteSpace:'nowrap', verticalAlign:'top' }}>{(() => {
                        if (l.status !== 'completed' || !l.marked_by_admin) {
                          return <span style={{ fontSize:11, color:'#94a3b8' }}>—</span>;
                        }
                        if (!l.tutor_payment_approved) {
                          return (
                            <button onClick={() => approveTutorPayment(l)} disabled={approving === l.id}
                              title="Include this session in the Companion's payment"
                              style={{ padding:'5px 10px', borderRadius:8, border:'none', background: approving === l.id ? '#94a3b8' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:11, cursor: approving === l.id ? 'wait' : 'pointer' }}>
                              {approving === l.id ? '⏳' : '💰 Add to Tutor Payment'}
                            </button>
                          );
                        }
                        return (
                          <span style={{ display:'inline-flex', flexDirection:'column', gap:3, alignItems:'flex-start' }}>
                            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(22,163,74,0.1)', color:'#16a34a', border:'1px solid rgba(22,163,74,0.25)' }}>✓ Added to pay</span>
                            {isUndoOpen(l) && (
                              <button onClick={() => unapproveTutorPayment(l)} disabled={approving === l.id}
                                title="Undo within 24h of approval"
                                style={{ padding:'2px 8px', borderRadius:6, border:'none', background:'transparent', color:'#dc2626', fontWeight:700, fontSize:10, cursor: approving === l.id ? 'wait' : 'pointer', textDecoration:'underline' }}>
                                ↶ Undo
                              </button>
                            )}
                          </span>
                        );
                      })()}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Payments & Hours ──────────────────────────────────────────────────────────
function ProfitReportTab() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const run = async () => {
    if (!start || !end) { setErr('Pick both dates'); return; }
    setLoading(true); setErr('');
    try {
      const r = await API.get(`/payments/profit-report?start_date=${start}&end_date=${end}`);
      setReport(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-title">💹 Profit Report</div>
      <p style={{ fontSize:13, color:'#64748b', marginBottom:14 }}>
        Profit = completed hours × per-student profit/hour. Pick a date range to see the totals.
      </p>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end', marginBottom:14 }}>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', display:'block', marginBottom:4 }}>From</label>
          <input className="form-control" type="date" value={start} onChange={e => setStart(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', display:'block', marginBottom:4 }}>To</label>
          <input className="form-control" type="date" value={end} onChange={e => setEnd(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={run} disabled={loading}>{loading ? 'Loading…' : '🔍 Calculate'}</button>
      </div>
      {err && <div className="alert alert-error">{err}</div>}
      {report && (
        <>
          <div style={{ display:'flex', gap:18, marginBottom:14, flexWrap:'wrap' }}>
            <div style={{ padding:'10px 16px', borderRadius:10, background:'#eef2ff', border:'1px solid #c7d2fe' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#4338ca', textTransform:'uppercase' }}>Total Hours</div>
              <div style={{ fontSize:22, fontWeight:800, color:'#3730a3' }}>{report.total_hours}h</div>
            </div>
            <div style={{ padding:'10px 16px', borderRadius:10, background:'#dcfce7', border:'1px solid #bbf7d0' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#15803d', textTransform:'uppercase' }}>Total Profit</div>
              <div style={{ fontSize:22, fontWeight:800, color:'#14532d' }}>₹{Number(report.total_profit).toLocaleString()}</div>
            </div>
            <div style={{ padding:'10px 16px', borderRadius:10, background:'#f1f5f9', border:'1px solid #e2e8f0' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase' }}>Students</div>
              <div style={{ fontSize:22, fontWeight:800, color:'#0f1f45' }}>{report.rows.length}</div>
            </div>
          </div>
          {report.rows.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">📊</div><p>No completed hours in this range.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Online (h × ₹)</th>
                    <th>Offline (h × ₹)</th>
                    <th>Total Hours</th>
                    <th>Total Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map(r => (
                    <tr key={r.student_id}>
                      <td style={{ fontWeight:600 }}>{r.student_name}</td>
                      <td className="text-muted text-small">{r.class_name}</td>
                      <td className="text-small">
                        {r.online_hours > 0
                          ? <>{r.online_hours}h × ₹{Number(r.online_rate).toFixed(0)} = <b>₹{Number(r.online_profit).toLocaleString()}</b></>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-small">
                        {r.offline_hours > 0
                          ? <>{r.offline_hours}h × ₹{Number(r.offline_rate).toFixed(0)} = <b>₹{Number(r.offline_profit).toLocaleString()}</b></>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>{r.completed_hours}h</td>
                      <td style={{ fontWeight:800, color:'#15803d' }}>₹{Number(r.total_profit).toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ background:'#f8fafc', fontWeight:800 }}>
                    <td colSpan={4} style={{ textAlign:'right' }}>Grand Total</td>
                    <td>{report.total_hours}h</td>
                    <td style={{ color:'#14532d' }}>₹{Number(report.total_profit).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PaymentsModule({ canViewProfit = false }) {
  const [tab, setTab] = useState('students');
  // classRates: flat array of { class_name, mode, rate_per_hour }
  const [classRates, setClassRates] = useState([]);
  const [classRateEdits, setClassRateEdits] = useState({}); // key: "Grade5_online"
  const [ratesSaving, setRatesSaving] = useState(false);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSearch, setReportSearch] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [payHistory, setPayHistory] = useState({});
  const [historyOpen, setHistoryOpen] = useState(null);
  const [paying, setPaying] = useState({});  // tutor_id → true while request in flight
  const [concerns, setConcerns] = useState([]);
  const [concernReply, setConcernReply] = useState({});  // id → response text
  const [msg, setMsg] = useState('');
  const [studPayments, setStudPayments] = useState([]);
  const [studPayForm, setStudPayForm] = useState({ student_id: '', amount: '', hours: '', invoice_number: '', payment_date: '', notes: '' });
  const [studPayExisting, setStudPayExisting] = useState(null); // {count, total_amount, total_hours, payments[]}
  const [studPayReminderBusy, setStudPayReminderBusy] = useState(null);
  const [studPayReminderMsg, setStudPayReminderMsg] = useState('');
  const [students, setStudents] = useState([]);
  const [studPayMsg, setStudPayMsg] = useState('');
  const [studPaySearch, setStudPaySearch] = useState('');
  const [showStudPayList, setShowStudPayList] = useState(false);
  const [studPayStudentName, setStudPayStudentName] = useState('');
  const [adjustmentsModal, setAdjustmentsModal] = useState(null); // { tutor_id, tutor_name }
  const [adjustmentsList, setAdjustmentsList] = useState([]);
  const [adjForm, setAdjForm] = useState({ amount: '', category: 'bonus', applied_month: '', reason: '' });
  const [adjMsg, setAdjMsg] = useState('');
  const [adjBusy, setAdjBusy] = useState(false);

  const loadClassRates = useCallback(() => {
    API.get('/payments/class-rates').then(r => {
      setClassRates(r.data);
      const edits = {};
      r.data.forEach(row => { edits[`${row.class_name}_${row.mode}`] = row.rate_per_hour; });
      setClassRateEdits(edits);
    }).catch(() => {});
  }, []);

  const loadStudentPayments = useCallback(() => {
    API.get('/payments/student-payments').then(r => setStudPayments(r.data || [])).catch(() => {});
  }, []);

  // Fetch existing payments in the same month as soon as a student is picked
  // (or the payment date changes). Result drives the yellow info banner that
  // tells the admin "this student already has X payments this month".
  useEffect(() => {
    if (!studPayForm.student_id) { setStudPayExisting(null); return; }
    const params = new URLSearchParams({ student_id: String(studPayForm.student_id) });
    if (studPayForm.payment_date) params.set('on_date', studPayForm.payment_date);
    API.get(`/payments/student-payments/check?${params.toString()}`)
      .then(r => setStudPayExisting(r.data))
      .catch(() => setStudPayExisting(null));
  }, [studPayForm.student_id, studPayForm.payment_date]);

  const sendStudentPaymentReminder = async (record) => {
    if (!window.confirm(`Send WhatsApp payment reminder to ${record.student_name}?`)) return;
    setStudPayReminderBusy(record.id);
    setStudPayReminderMsg('');
    try {
      const r = await API.post(`/payments/student-payments/${record.id}/remind`);
      setStudPayReminderMsg(r.data?.message || (r.data?.sent ? `✓ Reminder sent to ${record.student_name}` : 'Reminder not sent'));
    } catch (e) {
      setStudPayReminderMsg(e?.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setStudPayReminderBusy(null);
      setTimeout(() => setStudPayReminderMsg(''), 5000);
    }
  };

  useEffect(() => {
    if (tab === 'rates') loadClassRates();
    if (tab === 'concerns') API.get('/payments/concerns/all').then(r => setConcerns(r.data)).catch(() => {});
    if (tab === 'studpay') { loadStudentPayments(); API.get('/auth/users/student?active=true').then(r => setStudents(r.data)).catch(() => {}); }
  }, [tab, loadClassRates, loadStudentPayments]);

  const saveAllRates = async () => {
    setRatesSaving(true);
    try {
      const payload = classRates.map(row => ({
        class_name: row.class_name,
        mode: row.mode,
        rate_per_hour: parseFloat(classRateEdits[`${row.class_name}_${row.mode}`]) || 0,
      }));
      await API.put('/payments/class-rates', payload);
      setMsg('✓ All rates saved');
      loadClassRates();
    } catch { setMsg('Error saving rates'); }
    setRatesSaving(false);
  };

  const runReport = async () => {
    if (!reportStart || !reportEnd) { setMsg('Select date range'); return; }
    setReportLoading(true); setReport(null);
    try {
      const r = await API.get(`/payments/tutor-report?start_date=${reportStart}&end_date=${reportEnd}`);
      setReport(r.data);
    } catch { setMsg('Error loading report'); }
    setReportLoading(false);
  };

  const downloadReportPDF = async () => {
    if (!report || report.tutors.length === 0) return;
    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`Companion Payment Report`, 14, 16);
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text(`Period: ${reportStart}  to  ${reportEnd}`, 14, 23);
      doc.setTextColor(0);
      let y = 32;
      report.tutors.forEach(tutor => {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text(`${tutor.tutor_name}`, 14, y);
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        const headerAmount = (tutor.net_amount ?? tutor.total_amount).toFixed(2);
        doc.text(`Total: Rs.${headerAmount}  (${tutor.total_hours}h)`, 120, y);
        y += 6;
        tutor.breakdown.forEach(b => {
          const rows = b.sessions.map(s => [s.date, `${s.start} - ${s.end}`, `${s.hours}h`, `Rs.${s.amount.toFixed(2)}`]);
          autoTable(doc, {
            startY: y,
            head: [[`${b.student_name} | ${b.subject} | ${b.mode} | Rs.${b.rate_per_hour}/hr`, 'Time', 'Hours', 'Amount']],
            body: rows,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [37, 84, 184], fontSize: 9 },
            columnStyles: { 0: { cellWidth: 90 } },
            margin: { left: 14, right: 14 },
          });
          y = doc.lastAutoTable.finalY + 5;
          if (y > 260) { doc.addPage(); y = 20; }
        });
        // Adjustments block — only printed when there are any for this tutor in the window
        const adj = tutor.adjustments || [];
        if (adj.length > 0) {
          if (y > 250) { doc.addPage(); y = 20; }
          const adjRows = adj.map(a => {
            const sign = a.amount >= 0 ? '+' : '-';
            return [`${a.category}`, a.applied_month, a.reason, `${sign}Rs.${Math.abs(a.amount).toFixed(2)}`];
          });
          const adjTotal = tutor.adjustments_total ?? adj.reduce((s, a) => s + Number(a.amount || 0), 0);
          const adjSign = adjTotal >= 0 ? '+' : '-';
          const net = tutor.net_amount ?? (Number(tutor.total_amount) + Number(adjTotal));
          autoTable(doc, {
            startY: y,
            head: [['Adjustment', 'Month', 'Reason', 'Amount']],
            body: [
              ...adjRows,
              ['', '', 'Gross', `Rs.${Number(tutor.total_amount).toFixed(2)}`],
              ['', '', 'Adjustments total', `${adjSign}Rs.${Math.abs(adjTotal).toFixed(2)}`],
              ['', '', 'NET PAYABLE', `Rs.${Number(net).toFixed(2)}`],
            ],
            styles: { fontSize: 9 },
            headStyles: { fillColor: [124, 58, 237], fontSize: 9 },
            margin: { left: 14, right: 14 },
          });
          y = doc.lastAutoTable.finalY + 5;
        }
        y += 6;
      });
      // ── Grand total across all companions ──
      const gGross = report.tutors.reduce((s, t) => s + Number(t.total_amount || 0), 0);
      const gAdj = report.tutors.reduce((s, t) => s + Number(t.adjustments_total || 0), 0);
      const gNet = report.tutors.reduce((s, t) => s + Number(t.net_amount ?? t.total_amount ?? 0), 0);
      const gHours = report.tutors.reduce((s, t) => s + Number(t.total_hours || 0), 0);
      if (y > 250) { doc.addPage(); y = 20; }
      autoTable(doc, {
        startY: y,
        head: [['GRAND TOTAL (all Companions)', 'Total Hours', 'Amount']],
        body: [
          ['Gross', `${gHours.toFixed(2)}h`, `Rs.${gGross.toFixed(2)}`],
          ...(gAdj !== 0 ? [['Adjustments total', '', `${gAdj >= 0 ? '+' : '-'}Rs.${Math.abs(gAdj).toFixed(2)}`]] : []),
          ['NET PAYABLE', `${gHours.toFixed(2)}h`, `Rs.${gNet.toFixed(2)}`],
        ],
        styles: { fontSize: 10, fontStyle: 'bold' },
        headStyles: { fillColor: [22, 101, 52], fontSize: 10 },
        margin: { left: 14, right: 14 },
      });
      doc.save(`payment_report_${reportStart}_${reportEnd}.pdf`);
    } catch (e) {
      setMsg('Error generating PDF');
    }
  };

  const downloadReportCSV = () => {
    if (!report || report.tutors.length === 0) return;
    const rows = [['Companion', 'Student', 'Class', 'Subject', 'Mode', 'Rate/hr', 'Sessions', 'Total Hours', 'Total Amount']];
    report.tutors.forEach(tutor => {
      tutor.breakdown.forEach(b => {
        rows.push([tutor.tutor_name, b.student_name, b.student_class, b.subject, b.mode, b.rate_per_hour, b.sessions.length, b.hours, b.amount.toFixed(2)]);
      });
      // Gross row
      rows.push(['', '', '', '', '', '', 'Gross', tutor.total_hours, tutor.total_amount.toFixed(2)]);
      // Adjustments — one row per entry so the audit trail survives in the CSV
      const adj = tutor.adjustments || [];
      if (adj.length > 0) {
        adj.forEach(a => {
          const sign = a.amount >= 0 ? '+' : '-';
          rows.push(['', '', '', '', '', '', `Adjustment (${a.category} · ${a.applied_month}) — ${a.reason}`, '', `${sign}${Math.abs(a.amount).toFixed(2)}`]);
        });
        const adjTotal = tutor.adjustments_total ?? adj.reduce((s, a) => s + Number(a.amount || 0), 0);
        const adjSign = adjTotal >= 0 ? '+' : '-';
        rows.push(['', '', '', '', '', '', 'Adjustments total', '', `${adjSign}${Math.abs(adjTotal).toFixed(2)}`]);
        const net = tutor.net_amount ?? (Number(tutor.total_amount) + Number(adjTotal));
        rows.push(['', '', '', '', '', '', 'NET PAYABLE', tutor.total_hours, Number(net).toFixed(2)]);
      }
      rows.push([]);
    });
    // ── Grand total across all companions ──
    const gGross = report.tutors.reduce((s, t) => s + Number(t.total_amount || 0), 0);
    const gAdj = report.tutors.reduce((s, t) => s + Number(t.adjustments_total || 0), 0);
    const gNet = report.tutors.reduce((s, t) => s + Number(t.net_amount ?? t.total_amount ?? 0), 0);
    const gHours = report.tutors.reduce((s, t) => s + Number(t.total_hours || 0), 0);
    rows.push(['GRAND TOTAL', '', '', '', '', '', 'Gross', gHours.toFixed(2), gGross.toFixed(2)]);
    if (gAdj !== 0) rows.push(['', '', '', '', '', '', 'Adjustments total', '', `${gAdj >= 0 ? '+' : '-'}${Math.abs(gAdj).toFixed(2)}`]);
    rows.push(['', '', '', '', '', '', 'NET PAYABLE', gHours.toFixed(2), gNet.toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment_report_${reportStart}_${reportEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const markPaid = async (tutor) => {
    if (paying[tutor.tutor_id]) return;
    setPaying(p => ({ ...p, [tutor.tutor_id]: true }));
    try {
      const finalAmount = tutor.net_amount ?? tutor.total_amount;
      await API.post('/payments/tutor-report/mark-paid', {
        tutor_id: tutor.tutor_id,
        start_date: reportStart,
        end_date: reportEnd,
        total_hours: tutor.total_hours,
        total_amount: finalAmount,
      });
      setMsg(`✓ Payment of ₹${finalAmount.toFixed(2)} for ${tutor.tutor_name} recorded`);
      // Auto-open history so user sees the new entry immediately
      const r = await API.get(`/payments/tutor-payment-history/${tutor.tutor_id}`);
      setPayHistory(p => ({ ...p, [tutor.tutor_id]: r.data }));
      setHistoryOpen(tutor.tutor_id);
    } catch {
      setMsg('Error recording payment');
    }
    setPaying(p => ({ ...p, [tutor.tutor_id]: false }));
  };

  const loadHistory = async (tutor_id) => {
    if (historyOpen === tutor_id) { setHistoryOpen(null); return; }
    const r = await API.get(`/payments/tutor-payment-history/${tutor_id}`);
    setPayHistory(p => ({ ...p, [tutor_id]: r.data }));
    setHistoryOpen(tutor_id);
  };

  const openAdjustments = async (tutor) => {
    setAdjustmentsModal({ tutor_id: tutor.tutor_id, tutor_name: tutor.tutor_name });
    setAdjForm({
      amount: '',
      category: 'bonus',
      applied_month: (reportStart || new Date().toISOString().slice(0, 10)).slice(0, 7),
      reason: '',
    });
    setAdjMsg('');
    setAdjustmentsList([]);
    try {
      const r = await API.get(`/tutor-adjustments/?tutor_id=${tutor.tutor_id}`);
      setAdjustmentsList(r.data || []);
    } catch {
      setAdjMsg('Failed to load adjustments');
    }
  };

  const submitAdjustment = async () => {
    const amt = parseFloat(adjForm.amount);
    if (!amt || Number.isNaN(amt)) { setAdjMsg('Amount must be a non-zero number'); return; }
    if (!adjForm.reason.trim()) { setAdjMsg('Reason is required'); return; }
    if (!/^\d{4}-\d{2}$/.test(adjForm.applied_month)) { setAdjMsg('Applied month must be YYYY-MM'); return; }
    setAdjBusy(true);
    try {
      await API.post('/tutor-adjustments/', {
        tutor_id: adjustmentsModal.tutor_id,
        amount: amt,
        category: adjForm.category,
        applied_month: adjForm.applied_month,
        reason: adjForm.reason.trim(),
      });
      const r = await API.get(`/tutor-adjustments/?tutor_id=${adjustmentsModal.tutor_id}`);
      setAdjustmentsList(r.data || []);
      setAdjForm(f => ({ ...f, amount: '', reason: '' }));
      setAdjMsg('✓ Adjustment saved');
      // Refresh the report so the net total reflects the new adjustment
      if (reportStart && reportEnd) {
        try {
          const rep = await API.get(`/payments/tutor-report?start_date=${reportStart}&end_date=${reportEnd}`);
          setReport(rep.data);
        } catch {}
      }
    } catch (e) {
      setAdjMsg(e?.response?.data?.detail || 'Failed to save adjustment');
    } finally { setAdjBusy(false); }
  };

  const deleteAdjustment = async (id) => {
    if (!window.confirm('Delete this adjustment? (Only allowed within 24h of creation.)')) return;
    try {
      await API.delete(`/tutor-adjustments/${id}`);
      const r = await API.get(`/tutor-adjustments/?tutor_id=${adjustmentsModal.tutor_id}`);
      setAdjustmentsList(r.data || []);
      if (reportStart && reportEnd) {
        const rep = await API.get(`/payments/tutor-report?start_date=${reportStart}&end_date=${reportEnd}`);
        setReport(rep.data);
      }
    } catch (e) {
      setAdjMsg(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const adjCategoryLabel = (c) => ({
    deposit_hold: 'Deposit Hold', deposit_release: 'Deposit Release',
    bonus: 'Bonus', deduction: 'Deduction', correction: 'Correction', other: 'Other',
  })[c] || c;


  const tabStyle = (t) => ({
    padding: '9px 22px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13,
    cursor: 'pointer', transition: 'all 0.18s',
    background: tab === t ? '#2554b8' : 'transparent',
    color: tab === t ? '#ffffff' : '#64748b',
    boxShadow: tab === t ? '0 3px 10px rgba(37,84,184,0.35)' : 'none',
  });

  return (
    <>
      <div className="page-header">
        <div><h2>Payments & Hours</h2><p>Bill student hours, track payments and calculate Companion payments</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, background: '#f1f5f9', padding: 6, borderRadius: 12, width: 'fit-content' }}>
          <button style={tabStyle('students')} onClick={() => setTab('students')}>👨‍🎓 Student Hours</button>
          <button style={tabStyle('rates')} onClick={() => setTab('rates')}>⚙️ Class Rates</button>
          <button style={tabStyle('report')} onClick={() => setTab('report')}>📊 Payment Report</button>
          <button style={tabStyle('concerns')} onClick={() => setTab('concerns')}>🚨 Concerns</button>
          <button style={tabStyle('studpay')} onClick={() => setTab('studpay')}>💳 Student Payments</button>
          <button style={tabStyle('billing')} onClick={() => setTab('billing')}>🧾 Bills</button>
          {canViewProfit && (
            <button style={tabStyle('profit')} onClick={() => setTab('profit')}>💹 Profit Report</button>
          )}
        </div>

        {tab === 'profit' && canViewProfit && <ProfitReportTab />}

        {/* ── Tab: Student Hours (bills overview — see StudentBilling.js) ── */}
        {tab === 'students' && <StudentBillingOverview />}

        {/* ── Tab: Class Rates ────────────────────────────────────────────── */}
        {tab === 'rates' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>Class Payment Rates</div>
              <button className="btn btn-primary" disabled={ratesSaving} onClick={saveAllRates} style={{ fontSize: 13 }}>
                {ratesSaving ? 'Saving…' : '💾 Save All Rates'}
              </button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              Set per-hour rates by class and mode. Companion payment is calculated using the student's class and enrollment mode.
            </p>
            {classRates.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">💰</div><p>No classes found</p></div>
            ) : (() => {
              // Group rows by class_name for the grid
              const classNames = [...new Set(classRates.map(r => r.class_name))];
              return (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Class</th>
                        <th>🌐 Online Rate (₹/hr)</th>
                        <th>🏫 Offline Rate (₹/hr)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classNames.map(cls => (
                        <tr key={cls}>
                          <td><strong>{cls}</strong></td>
                          <td>
                            <input
                              type="number" min="0" step="50"
                              className="form-control"
                              style={{ maxWidth: 130, padding: '6px 10px' }}
                              value={classRateEdits[`${cls}_online`] ?? 0}
                              onChange={e => setClassRateEdits(p => ({ ...p, [`${cls}_online`]: e.target.value }))}
                            />
                          </td>
                          <td>
                            <input
                              type="number" min="0" step="50"
                              className="form-control"
                              style={{ maxWidth: 130, padding: '6px 10px' }}
                              value={classRateEdits[`${cls}_offline`] ?? 0}
                              onChange={e => setClassRateEdits(p => ({ ...p, [`${cls}_offline`]: e.target.value }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Tab: Payment Report ─────────────────────────────────────────── */}
        {tab === 'report' && (
          <div>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-title">Generate Companion Payment Report</div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0, flex: '0 0 180px' }}>
                  <label>From Date</label>
                  <input className="form-control" type="date" value={reportStart} onChange={e => setReportStart(e.target.value)} />
                </div>
                <div className="form-group" style={{ margin: 0, flex: '0 0 180px' }}>
                  <label>To Date</label>
                  <input className="form-control" type="date" value={reportEnd} onChange={e => setReportEnd(e.target.value)} />
                </div>
                <button className="btn btn-primary" onClick={runReport} disabled={reportLoading}>
                  {reportLoading ? 'Calculating…' : '📊 Calculate'}
                </button>
                {report && report.tutors.length > 0 && (
                  <>
                    <div className="form-group" style={{ margin: 0, flex: '1 1 220px', minWidth: 180 }}>
                      <label>Search Companion</label>
                      <input className="form-control" placeholder="🔍 Companion name…" value={reportSearch}
                        onChange={e => setReportSearch(e.target.value)} />
                    </div>
                    <button className="btn btn-ghost" onClick={downloadReportPDF} title="Download PDF">
                      📄 PDF
                    </button>
                    <button className="btn btn-ghost" onClick={downloadReportCSV} title="Download CSV/Excel">
                      📊 CSV
                    </button>
                  </>
                )}
              </div>
            </div>

            {report && (() => {
              const q = reportSearch.trim().toLowerCase();
              const shownTutors = q
                ? report.tutors.filter(t => (t.tutor_name || '').toLowerCase().includes(q))
                : report.tutors;
              return report.tutors.length === 0 ? (
                <div className="card"><div className="empty-state"><div className="empty-icon">📊</div><p>No completed classes in this period</p></div></div>
              ) : shownTutors.length === 0 ? (
                <div className="card"><div className="empty-state"><div className="empty-icon">🔍</div><p>No companion matches “{reportSearch}”</p></div></div>
              ) : (
                shownTutors.map(tutor => (
                  <div className="card" key={tutor.tutor_id} style={{ marginBottom: 16 }}>
                    {/* Tutor header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-dark)' }}>👨‍🏫 {tutor.tutor_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                          Rate based on student class &amp; mode
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {tutor.adjustments_total && tutor.adjustments_total !== 0 ? (
                          <>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Gross ₹{tutor.total_amount.toFixed(2)}</div>
                            <div style={{ fontSize: 11, color: tutor.adjustments_total >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                              {tutor.adjustments_total >= 0 ? '+' : '−'}₹{Math.abs(tutor.adjustments_total).toFixed(2)} adj
                            </div>
                            <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--color-accent)', marginTop: 2 }}>₹{tutor.net_amount.toFixed(2)}</div>
                          </>
                        ) : (
                          <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--color-accent)' }}>₹{tutor.total_amount.toFixed(2)}</div>
                        )}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tutor.total_hours}h total</div>
                      </div>
                    </div>

                    {/* Breakdown by student */}
                    <div style={{ marginBottom: 12 }}>
                      {tutor.breakdown.map((b, bi) => (
                        <div key={bi} style={{ border: '1px solid var(--border-color)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                          <div
                            style={{ padding: '10px 16px', background: '#f8faff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                            onClick={() => setExpandedStudent(expandedStudent === `${tutor.tutor_id}-${bi}` ? null : `${tutor.tutor_id}-${bi}`)}
                          >
                            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700 }}>👤 {b.student_name}</span>
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.student_class}</span>
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.subject}</span>
                              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700, background: b.mode === 'online' ? 'rgba(37,84,184,0.1)' : 'rgba(22,163,74,0.1)', color: b.mode === 'online' ? '#2554b8' : '#16a34a' }}>
                                {b.mode === 'online' ? '🌐' : '🏫'} {b.mode}
                              </span>
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.sessions.length} sessions · {b.hours}h</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <strong style={{ color: 'var(--color-accent)' }}>₹{b.amount.toFixed(2)}</strong>
                              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>@ ₹{b.rate_per_hour}/hr ({b.student_class} {b.mode})</span>
                              <span style={{ fontSize: 13, marginLeft: 10 }}>{expandedStudent === `${tutor.tutor_id}-${bi}` ? '▲' : '▼'}</span>
                            </div>
                          </div>
                          {expandedStudent === `${tutor.tutor_id}-${bi}` && (
                            <div style={{ padding: '0 16px 12px' }}>
                              <table style={{ width: '100%', marginTop: 8 }}>
                                <thead><tr style={{ fontSize: 11, color: 'var(--text-muted)' }}><th style={{ padding: '6px 0', textAlign: 'left' }}>Date</th><th>Time</th><th>Hours</th><th>Amount</th></tr></thead>
                                <tbody>
                                  {b.sessions.map((s, si) => (
                                    <tr key={si} style={{ fontSize: 13, borderTop: '1px solid #f1f5f9' }}>
                                      <td style={{ padding: '6px 0' }}>{s.date}</td>
                                      <td style={{ color: 'var(--text-muted)' }}>{s.start} – {s.end}</td>
                                      <td>{s.hours}h</td>
                                      <td style={{ color: 'var(--color-accent)', fontWeight: 700 }}>₹{s.amount.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Action row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: 12, gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => loadHistory(tutor.tutor_id)} style={{ fontSize: 12 }}>
                          📋 {historyOpen === tutor.tutor_id ? 'Hide' : 'View'} Payment History
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openAdjustments(tutor)} style={{ fontSize: 12 }}>
                          🔧 Adjustments{tutor.adjustments && tutor.adjustments.length > 0 ? ` (${tutor.adjustments.length})` : ''}
                        </button>
                      </div>
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: 13, opacity: paying[tutor.tutor_id] ? 0.6 : 1 }}
                        disabled={!!paying[tutor.tutor_id]}
                        onClick={() => markPaid(tutor)}
                      >
                        {paying[tutor.tutor_id] ? 'Saving…' : `✅ Mark ₹${(tutor.net_amount ?? tutor.total_amount).toFixed(2)} as Paid`}
                      </button>
                    </div>

                    {historyOpen === tutor.tutor_id && payHistory[tutor.tutor_id] && (
                      <div style={{ marginTop: 12, padding: '12px', background: '#f8faff', borderRadius: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>Payment History</div>
                        {payHistory[tutor.tutor_id].length === 0
                          ? <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No payments recorded yet</span>
                          : payHistory[tutor.tutor_id].map(r => (
                            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 }}>
                              <span style={{ flex: 1 }}>{r.start_date} → {r.end_date}</span>
                              <span style={{ color: 'var(--text-muted)' }}>{r.total_hours}h</span>
                              <strong style={{ color: '#16a34a' }}>₹{r.total_amount.toFixed(2)}</strong>
                              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Paid {r.paid_at}</span>
                              <button
                                style={{ marginLeft: 8, background: 'none', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 6, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}
                                onClick={async () => {
                                  if (!window.confirm('Delete this payment record?')) return;
                                  await API.delete(`/payments/tutor-payment-history/${r.id}`);
                                  const updated = await API.get(`/payments/tutor-payment-history/${tutor.tutor_id}`);
                                  setPayHistory(p => ({ ...p, [tutor.tutor_id]: updated.data }));
                                }}
                              >🗑 Delete</button>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                ))
              );
            })()}
          </div>
        )}

        {/* ── Tab: Student Payments ───────────────────────────────────────── */}
        {tab === 'studpay' && (
          <div>
            <div className="grid-2" style={{ marginBottom: 24 }}>
              <div className="card">
                <div className="card-title">Record Student Payment</div>
                {studPayMsg && <div className={`alert ${studPayMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setStudPayMsg('')}>{studPayMsg}</div>}
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Student</label>
                  <input
                    className="form-control"
                    placeholder="Search student by name…"
                    value={studPaySearch}
                    autoComplete="off"
                    onChange={e => { setStudPaySearch(e.target.value); setShowStudPayList(true); setStudPayForm(f => ({ ...f, student_id: '' })); setStudPayStudentName(''); }}
                    onFocus={() => setShowStudPayList(true)}
                    onBlur={() => setTimeout(() => setShowStudPayList(false), 180)}
                    style={{ borderColor: studPayForm.student_id ? '#16a34a' : undefined }}
                  />
                  {studPayForm.student_id && <small style={{ color: '#16a34a', fontWeight: 700 }}>✓ {studPayStudentName}</small>}
                  {showStudPayList && studPaySearch && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #cbd5e1', borderRadius: 10, zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                      {students.filter(s => s.name.toLowerCase().includes(studPaySearch.toLowerCase())).length === 0
                        ? <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>No students found</div>
                        : students.filter(s => s.name.toLowerCase().includes(studPaySearch.toLowerCase())).map(s => (
                          <div key={s.id}
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f1f5f9' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                            onMouseDown={() => { setStudPayForm(f => ({ ...f, student_id: s.id })); setStudPaySearch(s.name + (s.class_name ? ` (${s.class_name})` : '')); setStudPayStudentName(s.name); setShowStudPayList(false); }}>
                            <span style={{ fontWeight: 700 }}>{s.name}</span>
                            {s.class_name && <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>{s.class_name}</span>}
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
                {studPayExisting && studPayExisting.count > 0 && (() => {
                  const hasSameMonth = (studPayExisting.same_month_count || 0) > 0;
                  // Bright yellow when there's already a payment in the picked month;
                  // softer blue informational tint when only older payments exist.
                  const bg     = hasSameMonth ? 'rgba(217,119,6,0.10)' : 'rgba(37,84,184,0.06)';
                  const border = hasSameMonth ? 'rgba(217,119,6,0.30)' : 'rgba(37,84,184,0.25)';
                  const colour = hasSameMonth ? '#92400e' : '#1e3a8a';
                  const title  = hasSameMonth
                    ? `⚠ Already paid this month (${studPayExisting.month})`
                    : `ℹ Previous payments on record`;
                  return (
                    <div style={{ padding:'10px 14px', marginBottom:12, borderRadius:10, background:bg, border:`1px solid ${border}`, color:colour, fontSize:13 }}>
                      <div style={{ fontWeight:800, marginBottom:4 }}>{title}</div>
                      <div style={{ fontSize:12.5, lineHeight:1.5 }}>
                        {hasSameMonth
                          ? <>{studPayExisting.student_name} already has {studPayExisting.same_month_count} payment{studPayExisting.same_month_count === 1 ? '' : 's'} for <b>{studPayExisting.month}</b> totalling <b>₹{Number(studPayExisting.same_month_amount).toLocaleString()}</b>{studPayExisting.same_month_hours != null ? ` (${studPayExisting.same_month_hours}h)` : ''}.</>
                          : <>{studPayExisting.student_name} has {studPayExisting.prev_month_count} payment{studPayExisting.prev_month_count === 1 ? '' : 's'} in <b>{studPayExisting.prev_month}</b> totalling <b>₹{Number(studPayExisting.prev_month_amount).toLocaleString()}</b>.</>
                        }
                      </div>
                      <div style={{ marginTop:6, fontSize:12, maxHeight:130, overflowY:'auto' }}>
                        {studPayExisting.payments.slice(0, 8).map(p => (
                          <div key={p.id} style={{ color: p.same_month ? '#78350f' : '#475569', fontWeight: p.same_month ? 700 : 400 }}>
                            {p.same_month ? '★ ' : '• '}{p.payment_date} — ₹{Number(p.amount).toLocaleString()}{p.hours != null ? ` · ${p.hours}h` : ''}{p.invoice_number ? ` · ${p.invoice_number}` : ''}
                          </div>
                        ))}
                        {studPayExisting.payments.length > 8 && (
                          <div style={{ color:'#94a3b8', fontStyle:'italic' }}>… and {studPayExisting.payments.length - 8} older</div>
                        )}
                      </div>
                      <div style={{ marginTop:6, fontSize:11.5, opacity:0.85 }}>
                        {hasSameMonth ? 'You can still add another entry — split payments are allowed.' : 'No restriction. This is just a heads-up.'}
                      </div>
                    </div>
                  );
                })()}
                <div className="grid-2">
                  <div className="form-group">
                    <label>Amount (₹)</label>
                    <input className="form-control" type="number" placeholder="0.00" value={studPayForm.amount} onChange={e => setStudPayForm(f => ({ ...f, amount: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Hours covered</label>
                    <input className="form-control" type="number" step="0.5" placeholder="e.g. 8" value={studPayForm.hours} onChange={e => setStudPayForm(f => ({ ...f, hours: e.target.value }))} />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Invoice Number</label>
                    {(() => {
                      // Indian financial year: April–March. e.g. May 2026 -> 2026-27
                      const d = studPayForm.payment_date ? new Date(studPayForm.payment_date + 'T00:00:00') : new Date();
                      const m = d.getMonth() + 1;
                      const y = d.getFullYear();
                      const fyStart = m >= 4 ? y : y - 1;
                      const prefix = `SAP/${fyStart}-${String(fyStart + 1).slice(-2)}/`;
                      // Strip any prefix the user kept around when switching dates
                      const suffix = (studPayForm.invoice_number || '').replace(/^SAP\/\d{4}-\d{2}\//, '');
                      return (
                        <div style={{ display:'flex', alignItems:'stretch', border:'1.5px solid #e2e8f0', borderRadius:8, overflow:'hidden', background:'#fff' }}>
                          <span style={{ padding:'9px 12px', background:'#f8faff', color:'#475569', fontSize:13, fontWeight:700, borderRight:'1px solid #e2e8f0', whiteSpace:'nowrap' }}>{prefix}</span>
                          <input
                            type="text"
                            placeholder="e.g. 24"
                            value={suffix}
                            onChange={e => {
                              const cleaned = e.target.value.replace(/^SAP\/\d{4}-\d{2}\//, '');
                              setStudPayForm(f => ({ ...f, invoice_number: cleaned ? prefix + cleaned : '' }));
                            }}
                            style={{ flex:1, padding:'9px 12px', border:'none', fontSize:13, outline:'none', background:'#fff', minWidth:0 }}
                          />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="form-group">
                    <label>Payment Date</label>
                    <input className="form-control" type="date" value={studPayForm.payment_date} onChange={e => setStudPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes (optional)</label>
                  <textarea className="form-control" rows={2} placeholder="e.g. Monthly fee for March" value={studPayForm.notes} onChange={e => setStudPayForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={async () => {
                  if (!studPayForm.student_id || !studPayForm.amount || !studPayForm.payment_date) { setStudPayMsg('Student, amount and date are required'); return; }
                  try {
                    await API.post('/payments/student-payments', {
                      student_id: parseInt(studPayForm.student_id),
                      amount: parseFloat(studPayForm.amount),
                      hours: studPayForm.hours ? parseFloat(studPayForm.hours) : null,
                      invoice_number: studPayForm.invoice_number || null,
                      payment_date: studPayForm.payment_date,
                      notes: studPayForm.notes || null,
                    });
                    setStudPayMsg('✓ Payment recorded successfully!');
                    setStudPayForm({ student_id: '', amount: '', hours: '', invoice_number: '', payment_date: '', notes: '' });
                    setStudPaySearch(''); setStudPayStudentName('');
                    setStudPayExisting(null);
                    loadStudentPayments();
                  } catch (e) { setStudPayMsg(e.response?.data?.detail || 'Error recording payment'); }
                }}>＋ Record Payment</button>
              </div>

              <div className="card">
                <div className="card-title">Payment Summary</div>
                {studPayReminderMsg && (
                  <div style={{ padding:'8px 12px', marginBottom:10, borderRadius:8, background: studPayReminderMsg.includes('sent to') ? 'rgba(22,163,74,0.10)' : 'rgba(217,119,6,0.10)', border:'1px solid', borderColor: studPayReminderMsg.includes('sent to') ? 'rgba(22,163,74,0.30)' : 'rgba(217,119,6,0.30)', color: studPayReminderMsg.includes('sent to') ? '#15803d' : '#92400e', fontSize:12.5, fontWeight:600 }}>
                    {studPayReminderMsg}
                  </div>
                )}
                <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <table>
                    <thead><tr><th>Student</th><th>Amount</th><th>Hours</th><th>Invoice</th><th>Date</th><th>Month Total</th><th></th></tr></thead>
                    <tbody>
                      {studPayments.length === 0
                        ? <tr><td colSpan="7" style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>No payments recorded yet</td></tr>
                        : (() => {
                          // Group by student + month to know how many entries share the same month.
                          const groupCount = {};
                          for (const x of studPayments) {
                            const key = `${x.student_id}_${(x.payment_date || '').slice(0,7)}`;
                            groupCount[key] = (groupCount[key] || 0) + 1;
                          }
                          return studPayments.map(p => {
                            const key = `${p.student_id}_${(p.payment_date || '').slice(0,7)}`;
                            const splitCount = groupCount[key] || 1;
                            const isSplit = splitCount > 1;
                            return (
                          <tr key={p.id} style={isSplit ? { background:'#fffbeb' } : undefined}>
                            <td style={{ fontWeight: 600 }}>
                              {p.student_name}
                              {isSplit && (
                                <span style={{ marginLeft:6, fontSize:10, fontWeight:800, padding:'2px 7px', borderRadius:10, background:'#f59e0b', color:'#fff', verticalAlign:'middle' }}>
                                  {splitCount} entries
                                </span>
                              )}
                            </td>
                            <td style={{ fontWeight: 700, color: '#16a34a', whiteSpace:'nowrap' }}>₹{Number(p.amount).toLocaleString()}</td>
                            <td className="text-muted text-small" style={{ whiteSpace:'nowrap' }}>{p.hours != null ? `${p.hours}h` : '—'}</td>
                            <td className="mono text-muted text-small">{p.invoice_number || '—'}</td>
                            <td className="text-muted text-small" style={{ whiteSpace:'nowrap' }}>{p.payment_date}</td>
                            <td style={{ whiteSpace:'nowrap' }}>
                              {p.month_total != null ? (
                                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background: isSplit ? '#fef3c7' : '#eff6ff', color: isSplit ? '#b45309' : '#1e40af' }}>
                                  ₹{Number(p.month_total).toLocaleString()}
                                </span>
                              ) : <span className="text-muted text-small">—</span>}
                            </td>
                            <td style={{ whiteSpace:'nowrap' }}>
                              <button
                                onClick={() => sendStudentPaymentReminder(p)}
                                disabled={studPayReminderBusy === p.id}
                                title={`Send WhatsApp payment reminder to ${p.student_name}`}
                                style={{ padding:'4px 8px', marginRight:4, borderRadius:6, border:'none', background: studPayReminderBusy === p.id ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:700, fontSize:11, cursor: studPayReminderBusy === p.id ? 'wait' : 'pointer' }}>
                                {studPayReminderBusy === p.id ? '⏳' : '💬 Remind'}
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ color: '#dc2626', fontSize: 11 }}
                                onClick={async () => {
                                  if (!window.confirm('Delete this payment record?')) return;
                                  await API.delete(`/payments/student-payments/${p.id}`);
                                  loadStudentPayments();
                                }}>Delete</button>
                            </td>
                          </tr>
                            );
                          });
                        })()
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Concerns ───────────────────────────────────────────── */}
        {tab === 'concerns' && (
          <div>
            {concerns.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-icon">🚨</div><p>No concerns raised yet</p></div></div>
            ) : concerns.map(c => (
              <div className="card" key={c.id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-dark)', marginBottom: 3 }}>👨‍🏫 {c.tutor_name} — {c.subject}</div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.created_at}</span>
                  </div>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: c.status === 'resolved' ? 'rgba(22,163,74,0.1)' : 'rgba(245,158,11,0.1)', color: c.status === 'resolved' ? '#16a34a' : '#d97706', border: `1px solid ${c.status === 'resolved' ? 'rgba(22,163,74,0.2)' : 'rgba(245,158,11,0.2)'}` }}>
                    {c.status === 'resolved' ? '✓ Resolved' : '⏳ Pending'}
                  </span>
                </div>
                <p style={{ fontSize: 14, color: 'var(--text-dark)', marginBottom: 12, lineHeight: 1.6 }}>{c.message}</p>
                {c.admin_response && (
                  <div style={{ background: 'rgba(37,84,184,0.06)', borderLeft: '3px solid var(--color-accent)', padding: '10px 14px', borderRadius: '0 8px 8px 0', marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-accent)', marginBottom: 4 }}>YOUR RESPONSE</div>
                    <p style={{ fontSize: 13, color: 'var(--text-dark)' }}>{c.admin_response}</p>
                  </div>
                )}
                {c.status !== 'resolved' && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <textarea
                      placeholder="Type your response…"
                      value={concernReply[c.id] || ''}
                      onChange={e => setConcernReply(p => ({ ...p, [c.id]: e.target.value }))}
                      rows={2}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-color)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                    <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }}
                      onClick={async () => {
                        const resp = concernReply[c.id];
                        if (!resp?.trim()) return;
                        await API.patch(`/payments/concerns/${c.id}`, { admin_response: resp, status: 'resolved' });
                        const updated = await API.get('/payments/concerns/all');
                        setConcerns(updated.data);
                        setConcernReply(p => ({ ...p, [c.id]: '' }));
                        setMsg('✓ Response sent');
                      }}>
                      Send & Resolve
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'billing' && <BillsList />}

        {/* Tutor payment adjustments modal */}
        {adjustmentsModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={() => setAdjustmentsModal(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:24, width:'100%', maxWidth:680, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <h3 style={{ fontWeight:800, fontSize:17, color:'#0f1f45' }}>🔧 Payment Adjustments — {adjustmentsModal.tutor_name}</h3>
                <button onClick={() => setAdjustmentsModal(null)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#64748b' }}>×</button>
              </div>
              <p style={{ fontSize:12, color:'#64748b', marginBottom:14 }}>
                Add bonuses, deductions, or deposit holds for any month. Positive amounts are added to the payout, negative amounts are deducted. Entries can be deleted within 24 hours; after that, add a reversing entry instead.
              </p>
              {adjMsg && <div className={`alert ${adjMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setAdjMsg('')}>{adjMsg}</div>}

              {/* Add form */}
              <div style={{ background:'#f8faff', border:'1px solid #dbeafe', borderRadius:12, padding:14, marginBottom:18 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#0f1f45', marginBottom:10 }}>Add new adjustment</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:11 }}>Category</label>
                    <select className="form-control" value={adjForm.category} onChange={e => setAdjForm(f => ({ ...f, category: e.target.value }))}>
                      <option value="bonus">Bonus</option>
                      <option value="deduction">Deduction</option>
                      <option value="deposit_hold">Deposit Hold</option>
                      <option value="deposit_release">Deposit Release</option>
                      <option value="correction">Correction</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:11 }}>Amount (₹) — negative for deduction/hold</label>
                    <input className="form-control" type="number" step="0.01" value={adjForm.amount}
                      placeholder="e.g. -500 or 1000"
                      onChange={e => setAdjForm(f => ({ ...f, amount: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:10, marginTop:10 }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:11 }}>Applied Month</label>
                    <input className="form-control" type="month" value={adjForm.applied_month}
                      onChange={e => setAdjForm(f => ({ ...f, applied_month: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label style={{ fontSize:11 }}>Reason (visible to tutor)</label>
                    <input className="form-control" maxLength={500} value={adjForm.reason}
                      placeholder="e.g. Initial joining deposit hold"
                      onChange={e => setAdjForm(f => ({ ...f, reason: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
                  <button className="btn btn-primary" onClick={submitAdjustment} disabled={adjBusy} style={{ fontSize:13 }}>
                    {adjBusy ? 'Saving…' : 'Add Adjustment'}
                  </button>
                </div>
              </div>

              {/* History list */}
              <div style={{ fontWeight:700, fontSize:13, color:'#0f1f45', marginBottom:8 }}>History ({adjustmentsList.length})</div>
              {adjustmentsList.length === 0 ? (
                <p style={{ fontSize:12, color:'#94a3b8', padding:'8px 0' }}>No adjustments yet for this tutor.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {adjustmentsList.map(a => (
                    <div key={a.id} style={{ border:'1px solid #e2e8f0', borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#eef2ff', color:'#4338ca' }}>{adjCategoryLabel(a.category)}</span>
                          <span style={{ fontSize:11, color:'#64748b' }}>📅 {a.applied_month}</span>
                          <span style={{ fontSize:11, color:'#94a3b8' }}>by {a.created_by_name || 'admin'} · {a.created_at ? new Date(a.created_at + 'Z').toLocaleString() : ''}</span>
                        </div>
                        <div style={{ fontSize:13, color:'#1e293b' }}>{a.reason}</div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:800, fontSize:16, color: a.amount >= 0 ? '#16a34a' : '#dc2626' }}>
                          {a.amount >= 0 ? '+' : '−'}₹{Math.abs(a.amount).toFixed(2)}
                        </div>
                        {a.deletable && (
                          <button onClick={() => deleteAdjustment(a.id)} style={{ marginTop:4, background:'none', border:'1px solid #fca5a5', color:'#dc2626', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700, cursor:'pointer' }} title="Delete (allowed within 24h of creation)">
                            🗑 Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Chat Rooms Admin ────────────────────────────────────────────────────────────
function ChatRoomsAdmin({ unreadRooms = {}, onRoomRead }) {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isMobile] = useState(() => window.innerWidth <= 768);
  const [showChat, setShowChat] = useState(false);
  const [roomSearch, setRoomSearch] = useState('');

  useEffect(() => {
    const fetchRooms = () => {
      if (document.hidden) return;
      API.get('/chat/rooms').then(r => setRooms(r.data || [])).catch(() => {});
    };
    fetchRooms();
    // Poll so a freshly-arrived message re-sorts the list to the top without
    // needing a manual reload.
    const iv = setInterval(fetchRooms, 15000);
    window.addEventListener('focus', fetchRooms);
    return () => { clearInterval(iv); window.removeEventListener('focus', fetchRooms); };
  }, []);

  // Most recent conversation first. last_message_id is monotonic (higher =
  // newer); rooms with no messages sink to the bottom. Filtered by the search.
  const q = roomSearch.trim().toLowerCase();
  const sortedRooms = [...rooms]
    .filter(r => !q || (r.student_name || '').toLowerCase().includes(q))
    .sort((a, b) => (b.last_message_id || 0) - (a.last_message_id || 0));

  const selectRoom = (room) => {
    setSelectedRoom(room);
    if (room.last_message_id) {
      localStorage.setItem(`chat_seen_${room.room_id}`, String(room.last_message_id));
    }
    onRoomRead && onRoomRead();
    if (isMobile) setShowChat(true);
  };

  const RoomList = () => (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf8', boxShadow: '0 4px 16px rgba(37,84,184,0.07)', overflowY: 'auto', display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 160px)' : '100%' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: '#0f1f45', marginBottom: 10 }}>
          Conversations ({sortedRooms.length}{q ? ` of ${rooms.length}` : ''})
        </div>
        <div style={{ position: 'relative' }}>
          <input
            value={roomSearch}
            onChange={e => setRoomSearch(e.target.value)}
            placeholder="🔍 Search student name…"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
          {roomSearch && (
            <button onClick={() => setRoomSearch('')}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, borderRadius: 6, border: 'none', background: '#f1f5f9', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
          )}
        </div>
      </div>
      {sortedRooms.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          {q ? `No student matches “${roomSearch}”` : 'No chat rooms yet'}
        </div>
      ) : sortedRooms.map(room => {
        const hasUnread = unreadRooms[room.room_id];
        return (
          <div key={room.room_id} onClick={() => selectRoom(room)}
            style={{ padding: '13px 18px', cursor: 'pointer', borderBottom: '1px solid #f8faff',
              background: selectedRoom?.room_id === room.room_id ? 'linear-gradient(135deg,#eef3ff,#e8eeff)' : hasUnread ? '#fffbf0' : '#fff',
              borderLeft: selectedRoom?.room_id === room.room_id ? '3px solid #2554b8' : hasUnread ? '3px solid #f59e0b' : '3px solid transparent',
              transition: 'all 0.18s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
              <div style={{ fontWeight: hasUnread ? 800 : 700, fontSize: 13, color: '#0f1f45' }}>{room.student_name}</div>
              {hasUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />}
            </div>
            {room.last_message && <div style={{ fontSize: 11, color: hasUnread ? '#92400e' : '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: hasUnread ? 600 : 400 }}>{room.last_message}</div>}
            {room.last_message_at && <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 2 }}>{formatDateTime(room.last_message_at)}</div>}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="page-header">
        <div><h2>Chat Rooms</h2><p>Message students and monitor conversations</p></div>
      </div>
      <div className="page-body">
        {isMobile ? (
          showChat && selectedRoom ? (
            <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 160px)' }}>
              <button onClick={() => setShowChat(false)}
                style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0', background:'none', border:'none', cursor:'pointer', color:'#2554b8', fontWeight:700, fontSize:14, marginBottom:10 }}>
                ← Back to Conversations
              </button>
              <div style={{ flex:1, minHeight:0 }}>
                <ChatWindow roomId={selectedRoom.room_id} studentName={selectedRoom.student_name} />
              </div>
            </div>
          ) : RoomList()
        ) : (
          <div style={{ display: 'flex', gap: 18, height: 600 }}>
            <div style={{ width: 260, flexShrink: 0 }}>{RoomList()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ChatWindow roomId={selectedRoom?.room_id || null} studentName={selectedRoom?.student_name} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Class Requests Admin ────────────────────────────────────────────────────────
function ClassRequestsAdmin() {
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    API.get('/class-request/all').then(r => setRequests(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await API.patch(`/class-request/${id}`, { status });
      setMsg(`✓ Request ${status}`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error updating request');
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending:  { background: 'rgba(217,119,6,0.1)',   color: '#d97706', border: '1px solid rgba(217,119,6,0.25)'  },
      approved: { background: 'rgba(22,163,74,0.1)',   color: '#16a34a', border: '1px solid rgba(22,163,74,0.25)'  },
      rejected: { background: 'rgba(220,38,38,0.1)',   color: '#dc2626', border: '1px solid rgba(220,38,38,0.25)'  },
    };
    return map[s] || { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Class Requests</h2><p>Review and action student class requests</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Student</th><th>Companion</th><th>Subject</th><th>Date</th><th>Time</th><th>Reason</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>No class requests yet</td></tr>
                ) : requests.map(r => (
                  <tr key={r.id}>
                    <td>{r.student_name || r.student_id}</td>
                    <td>{r.tutor_name || r.tutor_id || '—'}</td>
                    <td>{r.subject}</td>
                    <td>{r.preferred_date}</td>
                    <td>{r.preferred_time}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
                    <td>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, ...statusBadge(r.status) }}>
                        {r.status}
                      </span>
                    </td>
                    <td>
                      {r.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn" style={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => updateStatus(r.id, 'approved')}>Approve</button>
                          <button className="btn" style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 7, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => updateStatus(r.id, 'rejected')}>Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}


// ── Admin Bulk Leave ────────────────────────────────────────────────────────────
// ── Leave Requests (admin: view / cancel / restore sessions) ──────────────────
function AdminLeaveRequests() {
  const [leaves, setLeaves] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [msg, setMsg] = useState('');
  const [restoreFor, setRestoreFor] = useState(null); // leave object
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    API.get('/leave/').then(r => setLeaves(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelLeave = async (l) => {
    if (!window.confirm(`Cancel ${l.tutor_name}'s leave (${l.start_date} → ${l.end_date})?\n\nThe cancelled classes are NOT added back automatically — use "Restore sessions" afterward to re-add the ones you want.`)) return;
    setBusy(true); setMsg('');
    try {
      await API.post(`/leave/${l.id}/cancel`);
      setMsg(`✓ Leave cancelled for ${l.tutor_name}. Use "Restore sessions" to re-add classes.`);
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed to cancel leave'); }
    setBusy(false);
  };

  const openRestore = async (l) => {
    setRestoreFor(l); setSessions([]); setSelected([]); setLoadingSessions(true); setMsg('');
    try {
      const r = await API.get(`/leave/${l.id}/cancelled-sessions`);
      setSessions(r.data || []);
      setSelected((r.data || []).map(s => s.schedule_id)); // default: all selected
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed to load sessions'); }
    setLoadingSessions(false);
  };

  const toggleOne = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allSel = sessions.length > 0 && selected.length === sessions.length;
  const toggleAll = () => setSelected(allSel ? [] : sessions.map(s => s.schedule_id));

  const submitRestore = async () => {
    if (selected.length === 0) { setMsg('Select at least one session to restore'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await API.post(`/leave/${restoreFor.id}/restore`, { schedule_ids: selected });
      let m = `✓ Restored ${r.data.restored} session(s).`;
      if (r.data.conflicts && r.data.conflicts.length) m += ` Skipped ${r.data.conflicts.length} (time clash): ${r.data.conflicts.join(', ')}`;
      setMsg(m);
      setRestoreFor(null); setSessions([]); setSelected([]);
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed to restore sessions'); }
    setBusy(false);
  };

  const statusBadge = (st) => {
    const m = { approved:{bg:'#e5f4ec',c:'#2b9c69'}, pending:{bg:'#f8efdd',c:'#b3781a'}, cancelled:{bg:'#f1f5f9',c:'#64748b'}, rejected:{bg:'#fbe8ec',c:'#cf4a5c'} }[st] || {bg:'#f1f5f9',c:'#64748b'};
    return <span className="badge" style={{ background:m.bg, color:m.c, textTransform:'capitalize' }}>{st}</span>;
  };

  const shown = leaves.filter(l => roleFilter === 'all' || (l.user_role || 'tutor') === roleFilter);

  return (
    <>
      <div className="page-header"><div><h2>Leave Requests</h2><p>Cancel a leave and re-add the classes it cancelled</p></div></div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        <div style={{ display:'flex', gap:6, marginBottom:14 }}>
          {[['all','All'],['tutor','Companions'],['student','Students']].map(([v, lbl]) => (
            <button key={v} onClick={() => setRoleFilter(v)}
              style={{ padding:'5px 14px', borderRadius:20, border:'1.5px solid', fontSize:12.5, fontWeight:700, cursor:'pointer',
                borderColor: roleFilter === v ? '#2554b8' : '#e2e8f0', background: roleFilter === v ? '#2554b8' : '#fff', color: roleFilter === v ? '#fff' : '#64748b' }}>{lbl}</button>
          ))}
        </div>

        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Applicant</th><th>Type</th><th>Dates</th><th>Reason</th><th>Classes cancelled</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {shown.length === 0 ? <tr><td colSpan="7" className="text-muted" style={{ padding:14 }}>No leave requests.</td></tr> :
                  shown.map(l => (
                    <tr key={l.id}>
                      <td>{l.tutor_name || '—'} <span className="badge badge-scheduled" style={{ marginLeft:4, fontSize:10 }}>{l.user_role === 'tutor' ? 'Companion' : (l.user_role || '—')}</span></td>
                      <td>{l.leave_type}</td>
                      <td className="mono">{l.start_date}{l.end_date !== l.start_date ? ` → ${l.end_date}` : ''}</td>
                      <td style={{ maxWidth:200, fontSize:12.5, color:'#475569' }}>{l.reason || '—'}</td>
                      <td>{l.cancelled_count || 0}</td>
                      <td>{statusBadge(l.status)}</td>
                      <td style={{ whiteSpace:'nowrap' }}>
                        {l.status !== 'cancelled' && l.status !== 'rejected' && (
                          <button className="btn btn-ghost" style={{ padding:'4px 10px', fontSize:12, color:'#dc2626', marginRight:6 }} disabled={busy} onClick={() => cancelLeave(l)}>Cancel Leave</button>
                        )}
                        {(l.cancelled_count || 0) > 0 && (
                          <button className="btn btn-ghost" style={{ padding:'4px 10px', fontSize:12, color:'#2554b8' }} onClick={() => openRestore(l)}>Restore sessions</button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Restore modal */}
      {restoreFor && (
        <div className="modal-overlay" onClick={() => setRestoreFor(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:560, maxHeight:'88vh', overflowY:'auto' }}>
            <div className="modal-header">
              <h3>Restore classes — {restoreFor.tutor_name}</h3>
              <button className="modal-close" onClick={() => setRestoreFor(null)}>×</button>
            </div>
            <p style={{ fontSize:13, color:'#64748b', margin:'0 0 12px' }}>Pick which cancelled classes to add back. Slots that now clash with another booked class are skipped automatically.</p>
            {loadingSessions ? <div className="text-muted" style={{ padding:12 }}>Loading…</div>
              : sessions.length === 0 ? <div className="text-muted" style={{ padding:12 }}>No cancelled sessions found for this leave.</div>
              : (
                <>
                  <label style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 0', fontWeight:700, fontSize:13, borderBottom:'1px solid #eef2fb' }}>
                    <input type="checkbox" checked={allSel} onChange={toggleAll} /> Select all ({sessions.length})
                  </label>
                  {sessions.map(s => (
                    <label key={s.schedule_id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #f5f7fc', fontSize:13 }}>
                      <input type="checkbox" checked={selected.includes(s.schedule_id)} onChange={() => toggleOne(s.schedule_id)} />
                      <span><strong>{s.date}</strong> · {s.start_time}–{s.end_time} · {s.subject || '—'}{s.student_name ? ` · ${s.student_name}` : ''}</span>
                    </label>
                  ))}
                  <button className="btn btn-primary" style={{ marginTop:14 }} disabled={busy || selected.length === 0} onClick={submitRestore}>
                    {busy ? 'Restoring…' : `Restore ${selected.length} class(es)`}
                  </button>
                </>
              )}
          </div>
        </div>
      )}
    </>
  );
}

function AdminBulkLeave() {
  const [tutors, setTutors] = useState([]);
  const [form, setForm] = useState({ tutor_id: '', start_date: '', end_date: '', reason: '' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('range'); // 'range' | 'sessions'
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    API.get('/auth/users/tutor?active=true').then(r => setTutors(r.data || [])).catch(() => {});
  }, []);

  const loadSessions = async () => {
    if (!form.tutor_id || !form.start_date || !form.end_date) { setMsg('Select tutor and date range first'); return; }
    if (form.start_date > form.end_date) { setMsg('Start date must be before end date'); return; }
    setLoadingSessions(true); setMsg(''); setSessions([]); setSelected([]);
    try {
      const r = await API.get(`/leave/admin-tutor-sessions?tutor_id=${form.tutor_id}&start_date=${form.start_date}&end_date=${form.end_date}`);
      setSessions(r.data || []);
      if ((r.data || []).length === 0) setMsg('No scheduled sessions for this tutor in that range');
    } catch (e) { setMsg(e.response?.data?.detail || 'Error loading sessions'); }
    setLoadingSessions(false);
  };

  const submitRange = async () => {
    if (!form.tutor_id) { setMsg('Please select a tutor'); return; }
    if (!form.start_date || !form.end_date) { setMsg('Please select a date range'); return; }
    if (form.start_date > form.end_date) { setMsg('Start date must be before end date'); return; }
    const tutor = tutors.find(t => String(t.id) === String(form.tutor_id));
    if (!window.confirm(`Grant bulk leave for ${tutor?.name} from ${form.start_date} to ${form.end_date}?\n\nAll their scheduled sessions in this range will be cancelled.`)) return;
    setLoading(true);
    try {
      const r = await API.post('/leave/admin-bulk', {
        tutor_id: parseInt(form.tutor_id),
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason || null,
      });
      setMsg(`✓ Bulk leave granted — ${r.data.cancelled_sessions ?? r.data.cancelled_count} session(s) cancelled`);
      setForm({ tutor_id: '', start_date: '', end_date: '', reason: '' });
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error granting leave');
    }
    setLoading(false);
  };

  const submitSessions = async () => {
    if (selected.length === 0) { setMsg('Select at least one session'); return; }
    const tutor = tutors.find(t => String(t.id) === String(form.tutor_id));
    if (!window.confirm(`Cancel ${selected.length} selected session(s) for ${tutor?.name}?`)) return;
    setLoading(true);
    try {
      const r = await API.post('/leave/admin-bulk-sessions', {
        tutor_id: parseInt(form.tutor_id),
        schedule_ids: selected,
        reason: form.reason || null,
      });
      setMsg(`✓ ${r.data.cancelled_sessions ?? r.data.cancelled_count} session(s) cancelled`);
      setSessions([]); setSelected([]);
      setForm({ tutor_id: '', start_date: '', end_date: '', reason: '' });
    } catch (e) { setMsg(e.response?.data?.detail || 'Error cancelling sessions'); }
    setLoading(false);
  };

  const allSelected = sessions.length > 0 && selected.length === sessions.length;
  const toggleAll = () => setSelected(allSelected ? [] : sessions.map(s => s.schedule_id));
  const toggleOne = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <>
      <div className="page-header">
        <div><h2>Admin Bulk Leave</h2><p>Cancel a tutor's sessions — the whole date range, or only specific classes on those days</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="card" style={{ maxWidth: 640 }}>
          <div className="card-title">📅 Grant Tutor Bulk Leave</div>

          {/* Mode toggle */}
          <div style={{ display:'flex', gap:6, marginBottom:16, background:'#f1f5f9', padding:4, borderRadius:10, width:'fit-content' }}>
            {[['range','Whole date range'],['sessions','Specific sessions']].map(([m,label]) => (
              <button key={m} onClick={() => { setMode(m); setSessions([]); setSelected([]); setMsg(''); }}
                style={{ padding:'7px 16px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
                  background: mode===m ? '#fff' : 'transparent', color: mode===m ? '#2554b8' : '#64748b',
                  boxShadow: mode===m ? '0 2px 8px rgba(37,84,184,0.12)' : 'none' }}>{label}</button>
            ))}
          </div>

          <div className="form-group">
            <label>Tutor</label>
            <SearchableSelect
              options={tutors.map(t => ({ value: t.id, label: t.name }))}
              value={form.tutor_id}
              onChange={v => { setForm(f => ({ ...f, tutor_id: v })); setSessions([]); setSelected([]); }}
              placeholder="— Select Tutor —"
            />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Start Date</label>
              <input className="form-control" type="date" value={form.start_date} onChange={e => { setForm(f => ({ ...f, start_date: e.target.value })); setSessions([]); setSelected([]); }} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input className="form-control" type="date" value={form.end_date} onChange={e => { setForm(f => ({ ...f, end_date: e.target.value })); setSessions([]); setSelected([]); }} />
            </div>
          </div>
          <div className="form-group">
            <label>Reason <span style={{ color:'#94a3b8', fontWeight:400 }}>(optional)</span></label>
            <input className="form-control" placeholder="e.g. Medical leave, Family emergency…" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </div>

          {mode === 'range' ? (
            <button className="btn btn-primary" style={{ width:'100%' }} onClick={submitRange} disabled={loading}>
              {loading ? 'Granting Leave…' : '📅 Cancel ALL Sessions in Range'}
            </button>
          ) : (
            <>
              <button className="btn btn-ghost" style={{ width:'100%', marginBottom:12 }} onClick={loadSessions} disabled={loadingSessions}>
                {loadingSessions ? 'Loading…' : '🔍 Load Sessions'}
              </button>
              {sessions.length > 0 && (
                <>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Select all ({sessions.length})
                    </label>
                    <span style={{ fontSize:12, color:'#64748b' }}>{selected.length} selected</span>
                  </div>
                  <div style={{ maxHeight:280, overflowY:'auto', border:'1px solid #e2e8f0', borderRadius:10, marginBottom:14 }}>
                    {sessions.map(s => (
                      <label key={s.schedule_id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderBottom:'1px solid #f1f5f9', cursor:'pointer', background: selected.includes(s.schedule_id) ? '#f0f5ff' : '#fff' }}>
                        <input type="checkbox" checked={selected.includes(s.schedule_id)} onChange={() => toggleOne(s.schedule_id)} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:'#0f1f45' }}>{s.subject} · {s.student_name}</div>
                          <div style={{ fontSize:12, color:'#64748b' }}>{s.date} · {s.start_time}–{s.end_time}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <button className="btn btn-primary" style={{ width:'100%' }} onClick={submitSessions} disabled={loading || selected.length === 0}>
                    {loading ? 'Cancelling…' : `❌ Cancel ${selected.length} Selected Session${selected.length === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Admin Student Bulk Leave ───────────────────────────────────────────────────
function AdminStudentBulkLeave() {
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ student_id: '', start_date: '', end_date: '', reason: '' });
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/auth/users/student?active=true').then(r => setStudents(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.student_id) { setMsg('Please select a student'); return; }
    if (!form.start_date || !form.end_date) { setMsg('Please select a date range'); return; }
    if (form.start_date > form.end_date) { setMsg('Start date must be before end date'); return; }
    const student = students.find(s => String(s.id) === String(form.student_id));
    if (!window.confirm(`Grant bulk leave for ${student?.name} from ${form.start_date} to ${form.end_date}?\n\nAll their scheduled sessions in this range will be cancelled.`)) return;
    setLoading(true);
    try {
      const r = await API.post('/leave/admin-bulk-student', {
        student_id: parseInt(form.student_id),
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason || null,
      });
      setMsg(`✓ Bulk leave granted — ${r.data.cancelled_sessions ?? r.data.cancelled_count} session(s) cancelled`);
      setForm({ student_id: '', start_date: '', end_date: '', reason: '' });
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error granting leave');
    }
    setLoading(false);
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Student Bulk Leave</h2><p>Grant leave for a student over a date range — automatically cancels all their scheduled sessions</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="card" style={{ maxWidth: 520 }}>
          <div className="card-title">🎓 Grant Student Bulk Leave</div>
          <div className="form-group">
            <label>Student</label>
            <SearchableSelect
              options={students.map(s => ({ value: s.id, label: s.name }))}
              value={form.student_id}
              onChange={v => setForm(f => ({ ...f, student_id: v }))}
              placeholder="— Select Student —"
            />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Start Date</label>
              <input className="form-control" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input className="form-control" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label>Reason <span style={{ color:'#94a3b8', fontWeight:400 }}>(optional)</span></label>
            <input className="form-control" placeholder="e.g. Medical leave, Family trip…" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </div>
          <button className="btn btn-primary" style={{ width:'100%' }} onClick={submit} disabled={loading}>
            {loading ? 'Granting Leave…' : '🎓 Grant Bulk Leave & Cancel Sessions'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Emergency Admin ────────────────────────────────────────────────────────────
function EmergencyAdmin() {
  const [records, setRecords] = useState([]);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    API.get('/emergency/all').then(r => setRecords(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = async (id, action, userName) => {
    if (action === 'reject' && !window.confirm(`Reject emergency and reinstate class for ${userName}?`)) return;
    try {
      await API.patch(`/emergency/${id}`, { action });
      setMsg(action === 'accept' ? `✓ Emergency accepted for ${userName}` : `✓ Rejected — class reinstated for ${userName}`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error');
    }
  };

  const reEnable = async (userId, userName) => {
    if (!window.confirm(`Re-enable emergency leave for ${userName}?`)) return;
    try {
      await API.post(`/emergency/enable/${userId}`);
      setMsg(`✓ Emergency re-enabled for ${userName}`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error');
    }
  };

  const disableEmergency = async (userId, userName) => {
    if (!window.confirm(`Disable emergency leave for ${userName}?`)) return;
    try {
      await API.post(`/emergency/disable/${userId}`);
      setMsg(`✓ Emergency disabled for ${userName}`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error');
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending:  { bg:'rgba(217,119,6,0.1)',  color:'#d97706', border:'rgba(217,119,6,0.25)' },
      accepted: { bg:'rgba(22,163,74,0.1)',  color:'#16a34a', border:'rgba(22,163,74,0.25)' },
      rejected: { bg:'rgba(220,38,38,0.1)',  color:'#dc2626', border:'rgba(220,38,38,0.25)' },
    };
    return map[s] || { bg:'#f1f5f9', color:'#64748b', border:'#e2e8f0' };
  };

  return (
    <>
      <div className="page-header">
        <div><h2>🚨 Emergency Leaves</h2><p>All emergency cancellations — accept, reject or re-enable</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Role</th><th>Subject</th><th>Class Date</th><th>Reason</th><th>Status</th><th>Emergency</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {records.length === 0
                  ? <tr><td colSpan="8" style={{ textAlign:'center', padding:24, color:'#94a3b8' }}>No emergency leaves recorded</td></tr>
                  : records.map(r => {
                    const badge = statusBadge(r.status || 'pending');
                    const isPending = !r.status || r.status === 'pending';
                    return (
                      <tr key={r.id}>
                        <td style={{ fontWeight:700 }}>{r.user_name}</td>
                        <td style={{ textTransform:'capitalize' }}>{r.role}</td>
                        <td>{r.subject || '—'}</td>
                        <td>{r.schedule_date ? `${r.schedule_date} ${r.schedule_start_time ? fmtTime(r.schedule_start_time) : ''}` : '—'}</td>
                        <td style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.reason || '—'}</td>
                        <td>
                          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:badge.bg, color:badge.color, border:`1px solid ${badge.border}` }}>
                            {r.status || 'pending'}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background: r.emergency_enabled ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)', color: r.emergency_enabled ? '#16a34a' : '#dc2626', border:`1px solid ${r.emergency_enabled ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.25)'}` }}>
                            {r.emergency_enabled ? 'enabled' : 'used'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                            {isPending && (
                              <>
                                <button className="btn" style={{ background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', border:'none', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                                  onClick={() => doAction(r.id, 'accept', r.user_name)}>✓ Accept</button>
                                <button className="btn" style={{ background:'none', border:'1px solid #fca5a5', color:'#dc2626', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                                  onClick={() => doAction(r.id, 'reject', r.user_name)}>✗ Reject</button>
                              </>
                            )}
                            {!r.emergency_enabled && (
                              <button className="btn" style={{ background:'rgba(37,84,184,0.1)', color:'#2554b8', border:'1px solid rgba(37,84,184,0.2)', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                                onClick={() => reEnable(r.user_id, r.user_name)}>⟳ Re-enable</button>
                            )}
                            {r.emergency_enabled && (
                              <button className="btn" style={{ background:'rgba(220,38,38,0.08)', color:'#dc2626', border:'1px solid rgba(220,38,38,0.2)', borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                                onClick={() => disableEmergency(r.user_id, r.user_name)}>✕ Disable</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Feedback Admin ─────────────────────────────────────────────────────────────
function FeedbackAdmin() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    API.get('/feedback/all').then(r => setRows(r.data || [])).catch(() => {});
  }, []);

  const stars = n => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : '—';

  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    return !q || (r.student_name || '').toLowerCase().includes(q) || (r.subject || '').toLowerCase().includes(q) || (r.tutor_name || '').toLowerCase().includes(q);
  });

  return (
    <>
      <div className="page-header">
        <div><h2>Student Feedback</h2><p>Feedback submitted by students after sessions</p></div>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom:16 }}>
          <input className="form-control" placeholder="Search by student, Companion or subject…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth:320 }} />
        </div>
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Student</th><th>Companion</th><th>Subject</th><th>Week</th>
                  <th>Understanding</th><th>Difficulty</th><th>Teacher Support</th><th>Overall</th>
                  <th>Parent Feedback</th><th>Notes</th><th>Type</th><th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0
                  ? <tr><td colSpan="12"><div className="empty-state"><div className="empty-icon">💬</div><p>No feedback yet</p></div></td></tr>
                  : filtered.map(r => (
                    <tr key={r.id}>
                      <td>{r.student_name}</td>
                      <td>{r.tutor_name || '—'}</td>
                      <td>{r.subject || '—'}</td>
                      <td className="mono text-muted text-small">{r.feedback_week || '—'}</td>
                      <td style={{ color:'#f59e0b', fontSize:12 }}>{stars(r.topic_understanding)}</td>
                      <td style={{ color:'#f59e0b', fontSize:12 }}>{stars(r.difficulty_level)}</td>
                      <td style={{ color:'#f59e0b', fontSize:12 }}>{stars(r.teacher_support)}</td>
                      <td style={{ color:'#f59e0b', fontSize:12 }}>{stars(r.overall_experience)}</td>
                      <td style={{ maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.parent_feedback}>{r.parent_feedback || '—'}</td>
                      <td style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.additional_notes}>{r.additional_notes || '—'}</td>
                      <td><span className={`badge badge-${r.feedback_type === 'weekly' ? 'scheduled' : 'completed'}`}>{r.feedback_type}</span></td>
                      <td className="mono text-muted text-small">{r.submitted_at?.slice(0,10) || '—'}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Activity Log ───────────────────────────────────────────────────────────────
function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    API.get('/admin-log/').then(r => setLogs(r.data)).catch(() => {});
  }, []);

  const ACTION_META = {
    USER_CREATED:    { label: 'User Created',    bg: 'rgba(22,163,74,0.12)',  color: '#16a34a', border: 'rgba(22,163,74,0.25)' },
    USER_UPDATED:    { label: 'User Updated',    bg: 'rgba(37,84,184,0.10)',  color: '#2554b8', border: 'rgba(37,84,184,0.2)'  },
    PASSWORD_CHANGED:{ label: 'Password Changed',bg: 'rgba(234,179,8,0.12)',  color: '#b45309', border: 'rgba(234,179,8,0.3)'  },
    ENROLLED:        { label: 'Enrolled',        bg: 'rgba(124,58,237,0.10)', color: '#7c3aed', border: 'rgba(124,58,237,0.2)' },
    DISENROLLED:     { label: 'Disenrolled',     bg: 'rgba(220,38,38,0.10)',  color: '#dc2626', border: 'rgba(220,38,38,0.2)'  },
  };

  const actions = ['all', ...Object.keys(ACTION_META)];

  const filtered = logs.filter(l => {
    const matchAction = actionFilter === 'all' || l.action === actionFilter;
    const q = search.trim().toLowerCase();
    const matchSearch = !q ||
      (l.admin_name || '').toLowerCase().includes(q) ||
      (l.target_name || '').toLowerCase().includes(q) ||
      (l.details || '').toLowerCase().includes(q);
    return matchAction && matchSearch;
  });

  return (
    <div style={{ padding: '28px 24px' }}>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>🔍 Admin Activity Log</div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search admin, user, details…"
              style={{ paddingLeft: 32, padding: '8px 12px 8px 32px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, width: 240, fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {actions.map(a => {
              const meta = ACTION_META[a];
              const active = actionFilter === a;
              return (
                <button key={a} onClick={() => setActionFilter(a)}
                  style={{ padding: '5px 13px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: `1.5px solid ${active && meta ? meta.border : active ? '#2554b8' : '#e2e8f0'}`,
                    background: active && meta ? meta.bg : active ? 'rgba(37,84,184,0.08)' : '#fff',
                    color: active && meta ? meta.color : active ? '#2554b8' : '#64748b' }}>
                  {meta ? meta.label : 'All'} <span style={{ opacity: 0.6, fontSize: 11 }}>
                    ({a === 'all' ? logs.length : logs.filter(l => l.action === a).length})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ fontSize: 12, color: '#94a3b8' }}>Showing {filtered.length} of {logs.length} entries (latest 500)</div>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <p style={{ color: '#64748b' }}>No activity logs found</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#f8faff' }}>
                  {['Timestamp', 'Admin', 'Action', 'Target User', 'Details'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => {
                  const meta = ACTION_META[l.action] || { label: l.action, bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
                  return (
                    <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbff' }}>
                      <td style={{ padding: '11px 16px', fontFamily: 'monospace', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatDateTime(l.created_at)}</td>
                      <td style={{ padding: '11px 16px', fontWeight: 700, color: '#0f1f45', whiteSpace: 'nowrap' }}>{l.admin_name}</td>
                      <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ padding: '11px 16px', color: '#334155', whiteSpace: 'nowrap' }}>
                        {l.target_name || '—'}
                        {l.target_id && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>#{l.target_id}</span>}
                      </td>
                      <td style={{ padding: '11px 16px', color: '#475569', fontSize: 12.5 }}>{l.details || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── WhatsApp Settings ──────────────────────────────────────────────────────────
const ALL_TEMPLATES = [
  'enrollment_confirmed','class_cancelled','homework_due_reminder',
  'test_reminder','test_result','leave_request_update',
  'payment_reminder','emergency_leave_alert','class_request_update',
];
const TEMPLATE_LABELS = {
  enrollment_confirmed:  'Enrollment Confirmed',
  class_cancelled:       'Class Cancelled',
  homework_due_reminder: 'Homework Due Reminder',
  test_reminder:         'Test Reminder',
  test_result:           'Test Result',
  leave_request_update:  'Emergency Leave Update',
  payment_reminder:      'Payment Reminder',
  emergency_leave_alert: 'Emergency Leave Alert',
  class_request_update:  'Class Request Update',
};

function WhatsAppSettings() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [settings, setSettings] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [globalOn, setGlobalOn] = useState(true);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [globalMsg, setGlobalMsg] = useState('');

  useEffect(() => {
    Promise.all([
      API.get('/auth/users/student?active=true'),
      API.get('/auth/users/tutor?active=true'),
    ]).then(([s, t]) => {
      // Exclude inactive students and companions — no point sending WhatsApp
      // to someone who's no longer active.
      const active = [...s.data, ...t.data].filter(u => u.is_active !== 'inactive');
      setUsers(active);
    }).catch(() => {});
    API.get('/whatsapp-settings/global').then(r => setGlobalOn(r.data.enabled)).catch(() => {});
  }, []);

  const toggleGlobal = async () => {
    setGlobalSaving(true);
    setGlobalMsg('');
    try {
      const r = await API.patch('/whatsapp-settings/global', { enabled: !globalOn });
      setGlobalOn(r.data.enabled);
      setGlobalMsg(r.data.enabled ? '✓ WhatsApp messages are ON for everyone' : '✓ All WhatsApp messages are now OFF');
    } catch {
      setGlobalMsg('Failed to update the master switch.');
    }
    setGlobalSaving(false);
  };

  const selectUser = async (user) => {
    setSelectedUser(user);
    setMsg('');
    try {
      const res = await API.get(`/whatsapp-settings/${user.id}`);
      setSettings(res.data.settings);
    } catch {
      setSettings(ALL_TEMPLATES.map(t => ({ template_name: t, enabled: true })));
    }
  };

  const toggle = (templateName) => {
    setSettings(prev => prev.map(s =>
      s.template_name === templateName ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const save = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await API.patch(`/whatsapp-settings/${selectedUser.id}`, settings);
      setMsg('✓ Settings saved');
    } catch {
      setMsg('Failed to save');
    }
    setLoading(false);
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="page-header">
        <div><h2>WhatsApp Settings</h2><p>Enable or disable WhatsApp notifications per user</p></div>
      </div>
      <div className="page-body">
        {/* Master kill-switch */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, background: globalOn ? '#fff' : '#fef2f2', border: `1.5px solid ${globalOn ? '#e2e8f0' : '#fecaca'}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#0f1f45', marginBottom: 4 }}>📵 Master WhatsApp Switch</div>
            <div style={{ fontSize: 12.5, color: '#64748b' }}>When OFF, <strong>no WhatsApp message is sent to anyone</strong> — this overrides every per-user setting below.</div>
            {globalMsg && <div className={`alert ${globalMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} style={{ marginTop: 10, marginBottom: 0 }}>{globalMsg}</div>}
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button onClick={toggleGlobal} disabled={globalSaving}
              style={{ width: 56, height: 28, borderRadius: 14, border: 'none', cursor: globalSaving ? 'not-allowed' : 'pointer', background: globalOn ? '#16a34a' : '#dc2626', position: 'relative', transition: 'background 0.2s' }}>
              <span style={{ position: 'absolute', top: 4, left: globalOn ? 32 : 4, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
            <span style={{ fontSize: 11, fontWeight: 700, color: globalOn ? '#16a34a' : '#dc2626' }}>{globalOn ? 'ON' : 'OFF'}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {/* User list */}
          <div className="card" style={{ flex: '0 0 280px', minWidth: 240 }}>
            <div className="card-title">Select User</div>
            <input className="form-control" placeholder="Search name or email..." value={search}
              onChange={e => setSearch(e.target.value)} style={{ marginBottom: 10 }} />
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {filtered.map(u => (
                <div key={u.id} onClick={() => selectUser(u)}
                  style={{
                    padding: '8px 10px', cursor: 'pointer', borderRadius: 8,
                    background: selectedUser?.id === u.id ? '#2554b8' : 'transparent',
                    marginBottom: 2,
                  }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: selectedUser?.id === u.id ? '#fff' : '#0f1f45' }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: selectedUser?.id === u.id ? '#bfdbfe' : '#64748b' }}>{u.role} · {u.email}</div>
                </div>
              ))}
              {filtered.length === 0 && <p className="text-muted" style={{ fontSize: 13 }}>No users found</p>}
            </div>
          </div>

          {/* Template toggles */}
          {selectedUser && (
            <div className="card" style={{ flex: 1, minWidth: 280 }}>
              <div className="card-title">Notifications for {selectedUser.name}</div>
              {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 12 }}>{msg}</div>}
              {settings.map(s => (
                <div key={s.template_name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{TEMPLATE_LABELS[s.template_name] || s.template_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.template_name}</div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={s.enabled} onChange={() => toggle(s.template_name)} style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: 13, color: s.enabled ? 'var(--color-success)' : 'var(--text-muted)' }}>
                      {s.enabled ? 'ON' : 'OFF'}
                    </span>
                  </label>
                </div>
              ))}
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={save} disabled={loading}>
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── PushNotificationSettings ───────────────────────────────────────────────────
function PushNotificationSettings() {
  const [settings, setSettings] = useState({ student: true, tutor: true, admin: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    API.get('/push/global-settings')
      .then(r => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (role) => {
    setSaving(role);
    setMsg('');
    try {
      const r = await API.patch(`/push/global-settings/${role}`);
      setSettings(prev => ({ ...prev, [role]: r.data.push_enabled }));
      setMsg(`✓ Push notifications for ${role}s ${r.data.push_enabled ? 'enabled' : 'disabled'}`);
    } catch {
      setMsg('Failed to update setting.');
    }
    setSaving('');
  };

  const roleConfig = [
    { key: 'student', label: 'Students', icon: '🎓', desc: 'Class reminders, exam updates, results, chat messages, class request updates' },
    { key: 'tutor',   label: 'Tutors',   icon: '👨‍🏫', desc: 'Class reminders, chat messages, class requests, cancellation alerts' },
    { key: 'admin',   label: 'Admins',   icon: '🛡️',  desc: 'Emergency leave alerts, chat messages' },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Push Notification Settings</h2>
          <p>Enable or disable push notifications for each user group</p>
        </div>
      </div>
      <div className="page-body">
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
            {msg && (
              <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`}>{msg}</div>
            )}
            {roleConfig.map(({ key, label, icon, desc }) => (
              <div key={key} className="card" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0f1f45', marginBottom: 4 }}>
                    {icon} {label}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>{desc}</div>
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={() => toggle(key)}
                    disabled={saving === key}
                    style={{
                      width: 56,
                      height: 28,
                      borderRadius: 14,
                      border: 'none',
                      cursor: saving === key ? 'not-allowed' : 'pointer',
                      background: settings[key] ? '#16a34a' : '#94a3b8',
                      position: 'relative',
                      transition: 'background 0.2s',
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: 4,
                      left: settings[key] ? 32 : 4,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#fff',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </button>
                  <span style={{ fontSize: 11, fontWeight: 600, color: settings[key] ? '#16a34a' : '#94a3b8' }}>
                    {settings[key] ? 'ON' : 'OFF'}
                  </span>
                </div>
              </div>
            ))}
            <div className="card" style={{ padding: '16px 20px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <p style={{ fontSize: 12.5, color: '#64748b', margin: 0 }}>
                <strong>Note:</strong> Turning off push for a group stops all push notifications for every user in that group immediately.
                Individual browser subscriptions are kept — re-enabling restores notifications without users needing to re-subscribe.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── ExamManagement ─────────────────────────────────────────────────────────────
function ExamManagement() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create / upload an exam paper (same capability tutors have)
  const [form, setForm] = useState({ title: '', subject: '', secret_code: '', duration_minutes: 60, exam_date: '' });
  const [studentIds, setStudentIds] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const fileRef = useRef(null);

  const loadPapers = useCallback(() => {
    API.get('/exams/all')
      .then(r => setPapers(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadPapers(); }, [loadPapers]);

  const autoCode = () => {
    setForm(f => ({ ...f, secret_code: 'EXAM-' + Math.random().toString(36).substr(2, 8).toUpperCase() }));
  };

  const handleCreate = async () => {
    if (!form.title || !form.secret_code || !form.exam_date || !pdfFile) { setUploadMsg('Fill title, exam date, secret code and select a PDF.'); return; }
    setUploading(true); setUploadMsg('Saving exam paper…');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('subject', form.subject || '');
      fd.append('secret_code', form.secret_code);
      fd.append('duration_minutes', String(parseInt(form.duration_minutes) || 60));
      fd.append('exam_date', form.exam_date);
      fd.append('student_ids', studentIds.join(','));
      fd.append('pdf_file', pdfFile);
      await API.post('/exams/', fd);
      setUploadMsg('✓ Exam paper created successfully!');
      setForm({ title: '', subject: '', secret_code: '', duration_minutes: 60, exam_date: '' });
      setStudentIds([]);
      setPdfFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadPapers();
    } catch (e) { setUploadMsg(e.response?.data?.detail || e.message || 'Failed to create exam paper.'); }
    finally { setUploading(false); }
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Loading exam papers…</div>;

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f1f45', margin: 0 }}>Exam Papers (All Companions)</h2>
        <button className="btn btn-primary" onClick={() => { setShowCreate(v => !v); setUploadMsg(''); }}>
          {showCreate ? '× Close' : '＋ Create Exam Paper'}
        </button>
      </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 22, background: 'linear-gradient(135deg,#eef3ff,#f5f3ff)', border: '1px solid #c7d4f5' }}>
          <div className="card-title">✏️ Create Exam Paper</div>
          <p style={{ fontSize: 12.5, color: '#64748b', marginBottom: 14 }}>
            Upload a question paper PDF and share the secret code with students. The paper is created under your admin account.
          </p>
          {uploadMsg && (
            <div className={`alert ${uploadMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setUploadMsg('')}>{uploadMsg}</div>
          )}
          <div className="grid-2">
            <div className="form-group">
              <label>Title *</label>
              <input className="form-control" placeholder="e.g. Chapter 3 — Genetics" value={form.title} maxLength={200}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input className="form-control" placeholder="e.g. Biology" value={form.subject} maxLength={100}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Secret Code *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-control" placeholder="EXAM-XXXXXXXX" value={form.secret_code} maxLength={40}
                  onChange={e => setForm(f => ({ ...f, secret_code: e.target.value.toUpperCase() }))} />
                <button className="btn btn-ghost btn-sm" onClick={autoCode} style={{ whiteSpace: 'nowrap' }}>🎲 Generate</button>
              </div>
            </div>
            <div className="form-group">
              <label>Duration (minutes)</label>
              <input className="form-control" type="number" min="1" max="600" value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Exam Date *</label>
              <input className="form-control" type="date" value={form.exam_date}
                onChange={e => setForm(f => ({ ...f, exam_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Question Paper PDF *</label>
              <input ref={fileRef} className="form-control" type="file" accept="application/pdf"
                onChange={e => setPdfFile(e.target.files?.[0] || null)} />
              <small style={{ color: '#94a3b8' }}>PDF only, max 20 MB.</small>
            </div>
          </div>
          <div className="form-group">
            <label>Students this paper is for</label>
            <StudentPicker value={studentIds} onChange={setStudentIds} />
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleCreate} disabled={uploading}>
            {uploading ? 'Uploading…' : '＋ Create Exam Paper'}
          </button>
        </div>
      )}

      <ExamPaperBrowser papers={papers} showTutor onChanged={loadPapers} />
    </div>
  );
}

// ── VR Learning (admin) ───────────────────────────────────────────────────────
// ── Teams meeting host pool ────────────────────────────────────────
function TeamsAdmin() {
  const [status, setStatus] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [form, setForm] = useState({ upn: '', display_name: '' });
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    API.get('/teams/status').then(r => setStatus(r.data)).catch(() => setStatus(null));
    API.get('/teams/hosts').then(r => setHosts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addHost = async () => {
    if (!form.upn.trim()) { setMsg('Enter the host account email'); return; }
    setAdding(true); setMsg('');
    try {
      await API.post('/teams/hosts', { upn: form.upn.trim(), display_name: form.display_name.trim() || null });
      setMsg('✓ Host added to the pool');
      setForm({ upn: '', display_name: '' });
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not add host'); }
    setAdding(false);
  };

  const toggleHost = async (h) => {
    try { await API.patch(`/teams/hosts/${h.id}`, { is_active: !h.is_active }); load(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
  };
  const removeHost = async (h) => {
    if (!window.confirm(`Remove ${h.upn} from the pool?`)) return;
    try { await API.delete(`/teams/hosts/${h.id}`); load(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Teams Meetings</h2><p>Host account pool for online classes</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        {/* Status */}
        <div className="card" style={{ marginBottom: 16, display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
          <div><div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>Integration</div>
            <div style={{ fontWeight:700, color: status?.enabled ? '#16a34a' : '#dc2626' }}>{status?.enabled ? 'ON' : 'OFF (set TEAMS_ENABLED=true)'}</div></div>
          <div><div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>MS credentials</div>
            <div style={{ fontWeight:700, color: status?.credentials_ok ? '#16a34a' : '#dc2626' }}>{status?.credentials_ok ? 'Connected' : 'Not connected'}</div></div>
          <div><div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>Host pool</div>
            <div style={{ fontWeight:700, color:'#0f1f45' }}>{status?.hosts_active ?? 0} active / {status?.hosts_total ?? 0} total</div></div>
        </div>
        {status && !status.credentials_ok && status.diagnostic && (
          <div className="alert alert-error" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>Microsoft connection problem</div>
            <div style={{ fontSize:12.5, marginBottom:6 }}>
              MS_TENANT_ID: {status.diagnostic.tenant_set ? 'set ✓' : 'MISSING ✗'} &nbsp;·&nbsp;
              MS_CLIENT_ID: {status.diagnostic.client_set ? 'set ✓' : 'MISSING ✗'} &nbsp;·&nbsp;
              MS_CLIENT_SECRET: {status.diagnostic.secret_set ? 'set ✓' : 'MISSING ✗'}
            </div>
            {status.diagnostic.error && <div className="mono" style={{ fontSize:12, wordBreak:'break-word' }}>{status.diagnostic.error}</div>}
          </div>
        )}

        <div className="alert" style={{ background:'#ecedfa', border:'1px solid #d5d7f2', color:'#4247a6', marginBottom:16, fontSize:13 }}>
          🎥 Classes record automatically. Recordings are kept in the host account&rsquo;s Microsoft OneDrive and managed by Microsoft &mdash; the LMS does not store copies.
        </div>

        {/* Host pool */}
        <div className="card">
          <div className="card-title">Host account pool</div>
          <p style={{ fontSize:12.5, color:'#64748b', margin:'0 0 12px' }}>Each licensed Microsoft 365 account can run one class at a time. Add one per concurrent class you need.</p>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
            <input className="form-control" placeholder="class-host-01@yourdomain.com" value={form.upn} onChange={e => setForm(f => ({ ...f, upn: e.target.value }))} style={{ maxWidth:280 }} />
            <input className="form-control" placeholder="Label (optional)" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} style={{ maxWidth:180 }} />
            <button className="btn btn-primary" onClick={addHost} disabled={adding}>{adding ? 'Adding…' : '+ Add host'}</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Account</th><th>Label</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {hosts.length === 0 ? <tr><td colSpan="4" className="text-muted" style={{ padding:14 }}>No host accounts yet.</td></tr> :
                  hosts.map(h => (
                    <tr key={h.id}>
                      <td className="mono">{h.upn}</td>
                      <td>{h.display_name || '—'}</td>
                      <td><span className={`badge ${h.is_active ? 'badge-completed' : 'badge-cancelled'}`}>{h.is_active ? 'Active' : 'Paused'}</span></td>
                      <td style={{ whiteSpace:'nowrap' }}>
                        <button className="btn btn-ghost" style={{ padding:'4px 10px', fontSize:12, marginRight:6 }} onClick={() => toggleHost(h)}>{h.is_active ? 'Pause' : 'Activate'}</button>
                        <button className="btn btn-ghost" style={{ padding:'4px 10px', fontSize:12, color:'#dc2626' }} onClick={() => removeHost(h)}>Remove</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function VRAdmin() {
  const [contents, setContents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [upForm, setUpForm] = useState({ title: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [reply, setReply] = useState({});

  const load = useCallback(() => {
    API.get('/vr/content').then(r => setContents(r.data)).catch(() => {});
    API.get('/vr/requests').then(r => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!upForm.title.trim()) { setMsg('Please enter a title'); return; }
    if (!upForm.file) { setMsg('Please choose a PDF file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', upForm.title.trim());
      fd.append('file', upForm.file);
      await API.post('/vr/content/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg('✓ VR content uploaded!');
      setUpForm({ title: '', file: null });
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const viewPdf = async (c) => {
    try {
      const response = await API.get(`/vr/content/${c.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { setMsg('Could not open PDF'); }
  };

  const deleteContent = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try { await API.delete(`/vr/content/${c.id}`); load(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Delete failed'); }
  };

  const act = async (r, status) => {
    try {
      await API.patch(`/vr/requests/${r.id}`, { status, admin_reply: (reply[r.id] || '').trim() || null });
      setMsg(`✓ Request marked ${status}`);
      setReply(x => ({ ...x, [r.id]: '' }));
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Update failed'); }
  };

  const badge = (st) => {
    const m = { pending:{bg:'#fffbeb',c:'#d97706',l:'Pending'}, fulfilled:{bg:'#f0fdf4',c:'#16a34a',l:'Fulfilled'}, rejected:{bg:'#fef2f2',c:'#dc2626',l:'Rejected'} }[st] || {bg:'#f1f5f9',c:'#64748b',l:st};
    return <span style={{ fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:20, background:m.bg, color:m.c }}>{m.l}</span>;
  };
  const inp = { width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f6', borderRadius:10, fontSize:14, fontFamily:'inherit', boxSizing:'border-box' };
  const pending = requests.filter(r => r.status === 'pending');

  return (
    <>
      <div className="page-header"><div><h2>VR Learning</h2><p>Upload VR content PDFs and manage companion VR set requests</p></div></div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        {/* Upload VR Content */}
        <div style={{ background:'#fff', border:'1px solid #e8edf8', borderRadius:16, padding:20, marginBottom:20 }}>
          <h4 style={{ fontWeight:800, color:'#0f1f45', fontSize:15, margin:'0 0 14px' }}>⬆️ Upload VR Content PDF</h4>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14, alignItems:'end' }}>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, display:'block' }}>Title *</label>
              <input style={inp} value={upForm.title} onChange={e => setUpForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. VR Content Catalogue 2026" />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, display:'block' }}>PDF file *</label>
              <input type="file" accept="application/pdf" onChange={e => setUpForm(f => ({ ...f, file: e.target.files[0] || null }))} style={{ fontSize:13 }} />
            </div>
            <button onClick={upload} disabled={uploading} style={{ padding:'11px 24px', background: uploading ? '#93a5cc' : '#2554b8', color:'#fff', border:'none', borderRadius:10, fontWeight:800, fontSize:14, cursor: uploading ? 'default' : 'pointer' }}>{uploading ? 'Uploading…' : 'Upload'}</button>
          </div>
          <div style={{ marginTop:16, display:'grid', gap:10 }}>
            {contents.length === 0 ? <div style={{ fontSize:13, color:'#94a3b8' }}>No VR content uploaded yet.</div> :
              contents.map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'12px 14px', border:'1px solid #eef2fb', borderRadius:12, background:'#fafbff' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                    <span style={{ fontSize:20 }}>📄</span>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontWeight:700, color:'#1e293b', fontSize:14 }}>{c.title}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{c.file_name}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => viewPdf(c)} style={{ padding:'7px 14px', background:'#2554b8', color:'#fff', border:'none', borderRadius:9, fontWeight:700, fontSize:12.5, cursor:'pointer' }}>View</button>
                    <button onClick={() => deleteContent(c)} style={{ padding:'7px 14px', background:'#fff', color:'#dc2626', border:'1.5px solid #fecaca', borderRadius:9, fontWeight:700, fontSize:12.5, cursor:'pointer' }}>Delete</button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* VR Set Requests */}
        <div style={{ background:'#fff', border:'1px solid #e8edf8', borderRadius:16, padding:20 }}>
          <h4 style={{ fontWeight:800, color:'#0f1f45', fontSize:15, margin:'0 0 4px' }}>🕶️ VR Set Requests {pending.length > 0 && <span style={{ fontSize:12, fontWeight:800, color:'#d97706' }}>({pending.length} pending)</span>}</h4>
          <p style={{ fontSize:13, color:'#64748b', margin:'0 0 14px' }}>Requests submitted by companions.</p>
          {requests.length === 0 ? <div style={{ fontSize:13, color:'#94a3b8' }}>No requests yet.</div> : (
            <div style={{ display:'grid', gap:12 }}>
              {requests.map(r => (
                <div key={r.id} style={{ border:'1px solid #eef2fb', borderRadius:12, padding:'14px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:6 }}>
                    <span style={{ fontWeight:800, color:'#2554b8', fontSize:13 }}>{r.ticket_id}</span>
                    {badge(r.status)}
                  </div>
                  <div style={{ fontSize:13, color:'#475569', marginBottom:4 }}>🧑‍🏫 <strong>{r.tutor_name}</strong> → 👤 {r.student_name || '—'}{r.class_name ? ` · ${r.class_name}` : ''}{r.request_date ? ` · 📅 ${r.request_date}` : ''}</div>
                  <div style={{ fontSize:13, color:'#1e293b', marginBottom:4 }}>🎬 {r.vr_content}</div>
                  {r.has_doubt && <div style={{ fontSize:12, color:'#d97706', marginBottom:4 }}>❓ Doubt: {r.doubt_text}</div>}
                  {r.admin_reply && <div style={{ fontSize:12, color:'#16a34a', background:'#f0fdf4', borderRadius:8, padding:'8px 10px', margin:'6px 0' }}>💬 You: {r.admin_reply}</div>}
                  <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap', alignItems:'center' }}>
                    <input style={{ ...inp, flex:1, minWidth:180, padding:'8px 10px' }} placeholder="Reply (optional)" value={reply[r.id] || ''} onChange={e => setReply(x => ({ ...x, [r.id]: e.target.value }))} />
                    <button onClick={() => act(r, 'fulfilled')} style={{ padding:'8px 16px', background:'#16a34a', color:'#fff', border:'none', borderRadius:9, fontWeight:700, fontSize:12.5, cursor:'pointer' }}>Mark Fulfilled</button>
                    <button onClick={() => act(r, 'rejected')} style={{ padding:'8px 16px', background:'#fff', color:'#dc2626', border:'1.5px solid #fecaca', borderRadius:9, fontWeight:700, fontSize:12.5, cursor:'pointer' }}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Companion Queries ─────────────────────────────────────────────────────────
function CompanionQueriesAdmin() {
  const [queries, setQueries] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [reply, setReply] = useState('');
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('all');
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [newMsgForm, setNewMsgForm] = useState({ tutor_id: '', message: '' });
  const [newMsgLoading, setNewMsgLoading] = useState(false);

  const load = useCallback(() => {
    API.get('/companion-queries/').then(r => setQueries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    API.get('/auth/users/tutor?active=true').then(r => setTutors(r.data)).catch(() => {});
  }, [load]);

  const submitReply = async (q) => {
    if (!reply.trim()) { setMsg('Reply cannot be empty'); return; }
    try {
      await API.patch(`/companion-queries/${q.id}`, { admin_reply: reply.trim(), status: 'resolved' });
      setMsg('✓ Reply sent and query resolved');
      setExpanded(null); setReply(''); load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Error sending reply'); }
  };

  const adminResolveMsg = async (q) => {
    if (!window.confirm('Mark this message as resolved without a companion reply?')) return;
    try {
      await API.patch(`/companion-queries/${q.id}/admin-resolve`);
      setMsg('✓ Message marked resolved');
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed to mark resolved'); }
  };

  const sendNewMessage = async () => {
    if (!newMsgForm.tutor_id) { setMsg('Please select a companion'); return; }
    if (!newMsgForm.message.trim()) { setMsg('Message cannot be empty'); return; }
    setNewMsgLoading(true);
    try {
      await API.post('/companion-queries/admin-message', { tutor_id: parseInt(newMsgForm.tutor_id), message: newMsgForm.message.trim() });
      setMsg('✓ Message sent to companion');
      setShowNewMsg(false); setNewMsgForm({ tutor_id: '', message: '' }); load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed to send'); }
    finally { setNewMsgLoading(false); }
  };

  const filtered = filter === 'all' ? queries : queries.filter(q => q.status === filter);

  return (
    <>
      <div className="page-header">
        <div><h2>Companion Queries</h2><p>View and respond to queries, or send messages to companions</p></div>
      </div>
      <div className="page-body">
        {msg && <div className={`alert ${msg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setMsg('')}>{msg}</div>}

        {/* New Message Modal */}
        {showNewMsg && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:460, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontWeight:800, fontSize:17, color:'#0f1f45', marginBottom:18 }}>📨 New Message to Companion</h3>
              <div className="form-group">
                <label>Select Companion</label>
                <select className="form-control" value={newMsgForm.tutor_id} onChange={e => setNewMsgForm(f => ({ ...f, tutor_id: e.target.value }))}>
                  <option value="">— Select Companion —</option>
                  {tutors.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea className="form-control" rows={4} maxLength={2000} placeholder="Type your message…" value={newMsgForm.message} onChange={e => setNewMsgForm(f => ({ ...f, message: e.target.value }))} />
                <small style={{ color:'#94a3b8' }}>{newMsgForm.message.length}/2000</small>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button className="btn btn-primary" onClick={sendNewMessage} disabled={newMsgLoading}>{newMsgLoading ? 'Sending…' : 'Send Message'}</button>
                <button className="btn btn-secondary" onClick={() => { setShowNewMsg(false); setNewMsgForm({ tutor_id:'', message:'' }); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['all', 'pending', 'resolved'].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '6px 18px', fontSize: 13, textTransform: 'capitalize' }}>{f}</button>
              ))}
            </div>
            <button className="btn btn-primary" style={{ fontSize:13 }} onClick={() => setShowNewMsg(true)}>📨 New Message to Companion</button>
          </div>
        </div>

        <div className="card">
          {filtered.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">❓</div><p>No queries</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {filtered.map(q => {
                const isAdminMsg = q.admin_initiated;
                const borderColor = q.status === 'resolved' ? '#86efac' : isAdminMsg ? '#bfdbfe' : '#fde68a';
                const bgColor = q.status === 'resolved' ? '#f0fdf4' : isAdminMsg ? '#eff6ff' : '#fffbeb';
                return (
                  <div key={q.id} style={{ border: `1.5px solid ${borderColor}`, borderRadius: 12, padding: '16px 18px', background: bgColor }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap:'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#2554b8' }}>{q.ticket_id}</span>
                        {isAdminMsg
                          ? <span style={{ fontSize: 12, color: '#64748b' }}>📨 <strong>You</strong> → <strong>{q.tutor_name}</strong></span>
                          : <span style={{ fontSize: 12, color: '#64748b' }}>by <strong>{q.tutor_name}</strong> about <strong>{q.student_name}</strong></span>
                        }
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: q.status === 'resolved' ? '#dcfce7' : '#fef9c3', color: q.status === 'resolved' ? '#15803d' : '#92400e' }}>{q.status}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(q.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div style={{ padding:'8px 12px', borderRadius:8, background: isAdminMsg ? '#dbeafe' : '#f8fafc', marginBottom:8 }}>
                      <div style={{ fontSize:11, fontWeight:700, color: isAdminMsg ? '#1e40af' : '#64748b', marginBottom:3 }}>{isAdminMsg ? 'Your Message' : 'Query'}</div>
                      <div style={{ fontSize: 13, color: '#334155' }}>{q.message}</div>
                    </div>
                    {q.image_data && (
                      <img src={q.image_data} alt="attachment" style={{ maxWidth: 260, maxHeight: 180, borderRadius: 8, border: '1px solid #e2e8f6', marginBottom: 10, cursor: 'pointer', display: 'block' }}
                        onClick={() => window.open(q.image_data, '_blank')} title="Click to view full size" />
                    )}
                    {q.admin_reply && (
                      <div style={{ padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #86efac', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 3 }}>{isAdminMsg ? 'Companion Reply' : 'Your Reply'}</div>
                        <div style={{ fontSize: 13, color: '#166534' }}>{q.admin_reply}</div>
                      </div>
                    )}
                    {isAdminMsg && q.status === 'pending' && (
                      <button className="btn btn-secondary" style={{ fontSize: 13, marginTop: 8 }} onClick={() => adminResolveMsg(q)}>✓ Mark Resolved (no reply)</button>
                    )}
                    {!isAdminMsg && q.status === 'pending' && (
                      expanded === q.id ? (
                        <div style={{ marginTop: 10 }}>
                          <textarea className="form-control" rows={3} maxLength={2000} placeholder="Type your reply…" value={reply} onChange={e => setReply(e.target.value)} style={{ marginBottom: 8 }} />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => submitReply(q)}>Send Reply & Resolve</button>
                            <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => { setExpanded(null); setReply(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button className="btn btn-primary" style={{ fontSize: 13, marginTop: 8 }} onClick={() => { setExpanded(q.id); setReply(''); }}>Reply</button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}


// ── Admin Tests & Grades ──────────────────────────────────────────────────────
function AdminTestsGrades() {
  const [rows, setRows]       = useState([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  // ── Create test (assigned to the student's Companion) ──
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const emptyTest = { student_id:'', subject:'', test_name:'', topic:'', max_marks:'', test_date:'', start_time:'', end_time:'', exam_secret_code:'' };
  const [tForm, setTForm] = useState(emptyTest);

  const load = useCallback(async (name = '') => {
    setLoading(true);
    try {
      const p = name.trim() ? `?student_name=${encodeURIComponent(name.trim())}` : '';
      const r = await API.get(`/tests/admin/all-grades${p}`);
      setRows(r.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get('/auth/users/student?active=true').then(r => setStudents(r.data || [])).catch(() => {});
    API.get('/enrollment/').then(r => setEnrollments(r.data || [])).catch(() => {});
  }, []);

  // Active enrollments for the chosen student → drives the subject list and
  // auto-resolves the Companion who will do the marking.
  const studentEnrs = enrollments.filter(
    e => e.student_id === Number(tForm.student_id) && e.status !== 'inactive'
  );
  const subjectOptions = [...new Set(studentEnrs.map(e => e.subject).filter(Boolean))];
  const matchedEnr = studentEnrs.find(e => e.subject === tForm.subject) || null;
  const selectedStudent = students.find(s => s.id === Number(tForm.student_id)) || null;

  const submitTest = async () => {
    if (!tForm.student_id || !tForm.subject) { setCreateMsg('Select a student and subject.'); return; }
    if (!matchedEnr) { setCreateMsg('No active enrollment for that student and subject — cannot find the Companion.'); return; }
    if (!tForm.test_name || !tForm.max_marks || !tForm.test_date) { setCreateMsg('Test name, max marks and date are required.'); return; }
    setCreating(true); setCreateMsg('');
    try {
      await API.post('/tests/', {
        test_name: tForm.test_name,
        subject: tForm.subject,
        topic: tForm.topic || null,
        max_marks: parseInt(tForm.max_marks, 10),
        class_name: selectedStudent?.class_name || null,
        test_date: tForm.test_date,
        start_time: tForm.start_time || null,
        end_time: tForm.end_time || null,
        exam_secret_code: tForm.exam_secret_code || null,
        // Ownership goes to the Companion so marking appears in THEIR Test List
        tutor_id: matchedEnr.tutor_id,
        student_id: Number(tForm.student_id),
      });
      setCreateMsg(`✓ Test created and assigned to ${matchedEnr.tutor_name} for marking.`);
      setTForm(emptyTest);
      load(search);
    } catch (e) {
      setCreateMsg(e?.response?.data?.detail || 'Failed to create test');
    } finally { setCreating(false); }
  };

  const fmtT = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':').map(Number);
    const p = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${p}`;
  };

  const pct = (marks, max) => {
    if (marks === null || marks === undefined || !max) return null;
    return Math.round((marks / max) * 100);
  };

  const pctColor = (p) => {
    if (p === null) return '#94a3b8';
    if (p >= 80) return '#16a34a';
    if (p >= 50) return '#d97706';
    return '#dc2626';
  };

  return (
    <>
      <div className="page-header">
        <div><h2>📝 Tests & Grades</h2><p>View all exam schedules and student marks</p></div>
      </div>
      <div className="page-body">
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
          <button className="btn btn-primary" onClick={() => { setShowCreate(v => !v); setCreateMsg(''); }}>
            {showCreate ? '× Close' : '＋ Create Test'}
          </button>
        </div>

        {showCreate && (
          <div className="card" style={{ marginBottom:16, background:'linear-gradient(135deg,#eef3ff,#f5f3ff)', border:'1px solid #c7d4f5' }}>
            <div className="card-title">＋ Create Test</div>
            <p style={{ fontSize:12.5, color:'#64748b', marginBottom:14 }}>
              Pick the student and subject — the assigned Companion is filled in automatically and the test goes to
              their Test List so <strong>they</strong> record the marks.
            </p>
            {createMsg && (
              <div className={`alert ${createMsg.startsWith('✓') ? 'alert-success' : 'alert-error'}`} onClick={() => setCreateMsg('')}>{createMsg}</div>
            )}
            <div className="grid-2">
              <div className="form-group">
                <label>Student *</label>
                <SearchableSelect
                  value={String(tForm.student_id || '')}
                  onChange={v => setTForm(f => ({ ...f, student_id: v, subject: '' }))}
                  placeholder="— Select student —"
                  options={students.map(s => ({ value: String(s.id), label: s.class_name ? `${s.name} (${s.class_name})` : s.name }))}
                />
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <select className="form-control" value={tForm.subject}
                  disabled={!tForm.student_id}
                  onChange={e => setTForm(f => ({ ...f, subject: e.target.value }))}>
                  <option value="">{tForm.student_id ? '— Select subject —' : 'Pick a student first'}</option>
                  {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Auto-fetched Companion */}
            <div style={{ padding:'10px 14px', marginBottom:14, borderRadius:10,
              background: matchedEnr ? 'rgba(22,163,74,0.08)' : '#f8fafc',
              border: `1px solid ${matchedEnr ? 'rgba(22,163,74,0.25)' : '#e2e8f0'}` }}>
              <span style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.6 }}>Assigned Companion</span>
              <div style={{ fontSize:14, fontWeight:800, color: matchedEnr ? '#15803d' : '#94a3b8', marginTop:3 }}>
                {matchedEnr ? `👨‍🏫 ${matchedEnr.tutor_name}` : 'Select student + subject to auto-fetch'}
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Test Name *</label>
                <input className="form-control" placeholder="e.g. Chapter 3 Test" value={tForm.test_name}
                  onChange={e => setTForm(f => ({ ...f, test_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Topic</label>
                <input className="form-control" placeholder="e.g. Genetics" value={tForm.topic}
                  onChange={e => setTForm(f => ({ ...f, topic: e.target.value }))} />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label>Max Marks *</label>
                <input className="form-control" type="number" min="1" value={tForm.max_marks}
                  onChange={e => setTForm(f => ({ ...f, max_marks: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Test Date *</label>
                <input className="form-control" type="date" value={tForm.test_date}
                  onChange={e => setTForm(f => ({ ...f, test_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label>Start Time</label>
                <input className="form-control" type="time" value={tForm.start_time}
                  onChange={e => setTForm(f => ({ ...f, start_time: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input className="form-control" type="time" value={tForm.end_time}
                  onChange={e => setTForm(f => ({ ...f, end_time: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label>Exam Code (optional — link an uploaded question paper)</label>
              <input className="form-control" placeholder="EXAM-XXXXXXXX" value={tForm.exam_secret_code}
                onChange={e => setTForm(f => ({ ...f, exam_secret_code: e.target.value.toUpperCase() }))} />
            </div>
            <button className="btn btn-primary" style={{ width:'100%' }} onClick={submitTest} disabled={creating || !matchedEnr}>
              {creating ? 'Creating…' : '＋ Create Test'}
            </button>
          </div>
        )}

        <div className="card" style={{ marginBottom:16, display:'flex', gap:10, alignItems:'center' }}>
          <input className="form-control" placeholder="🔍 Search by student name…" value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(search)}
            style={{ maxWidth:300 }} />
          <button className="btn btn-primary" onClick={() => load(search)} disabled={loading} style={{ fontSize:13 }}>
            {loading ? 'Searching…' : 'Search'}
          </button>
          {search && <button className="btn btn-secondary" style={{ fontSize:13 }} onClick={() => { setSearch(''); load(''); }}>Clear</button>}
          <span style={{ fontSize:13, color:'#64748b', marginLeft:'auto' }}>{rows.length} result{rows.length !== 1 ? 's' : ''}</span>
        </div>

        {rows.length === 0 && !loading ? (
          <div className="card"><div className="empty-state"><div className="empty-icon">📝</div><p>No test records found</p></div></div>
        ) : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {['Date','Test Name','Subject','Exam Code','Student','Companion','Time','Max','Marks','%','Remarks'].map(h => (
                      <th key={h} style={{ padding:'11px 14px', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const p = pct(r.marks_obtained, r.max_marks);
                    return (
                      <tr key={r.grade_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbff' }}>
                        <td style={{ padding:'11px 14px', fontSize:13, whiteSpace:'nowrap', color:'#64748b' }}>{r.test_date || '—'}</td>
                        <td style={{ padding:'11px 14px', fontWeight:700, color:'#0f1f45', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                          title={r.test_name}>{r.test_name}</td>
                        <td style={{ padding:'11px 14px', fontSize:13 }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#eff6ff', color:'#2554b8' }}>{r.subject}</span>
                        </td>
                        <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                          {r.exam_secret_code
                            ? <span style={{ fontFamily:'monospace', fontSize:12, color:'#7c3aed', background:'#f5f3ff', padding:'3px 8px', borderRadius:6 }}>{r.exam_secret_code}</span>
                            : <span style={{ color:'#94a3b8', fontSize:12 }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', fontWeight:600, color:'#0f1f45' }}>{r.student_name}</td>
                        <td style={{ padding:'11px 14px', fontSize:13, color:'#64748b' }}>{r.tutor_name}</td>
                        <td style={{ padding:'11px 14px', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>
                          {r.start_time ? `${fmtT(r.start_time)} – ${fmtT(r.end_time)}` : '—'}
                        </td>
                        <td style={{ padding:'11px 14px', fontSize:13, textAlign:'center' }}>{r.max_marks ?? '—'}</td>
                        <td style={{ padding:'11px 14px', fontSize:13, fontWeight:700, textAlign:'center', color: pctColor(p) }}>
                          {r.marks_obtained !== null && r.marks_obtained !== undefined ? r.marks_obtained : <span style={{ color:'#94a3b8', fontWeight:400 }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', textAlign:'center' }}>
                          {p !== null ? (
                            <span style={{ fontSize:12, fontWeight:800, color: pctColor(p) }}>{p}%</span>
                          ) : <span style={{ color:'#94a3b8', fontSize:12 }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', fontSize:12, color:'#64748b', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                          title={r.remarks}>{r.remarks || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


// ── Admin Homework View (read-only with student search) ──────────────────────
// ── Inquiries (parent-submitted leads) ───────────────────────────────────────
function InquiriesAdmin({ setActive }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('new');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (q.trim()) params.set('q', q.trim());
      const r = await API.get(`/inquiries/?${params.toString()}`);
      setItems(r.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await API.patch(`/inquiries/${id}`, { status });
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to update'); }
    finally { setBusyId(null); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    setBusyId(id);
    try {
      await API.delete(`/inquiries/${id}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete'); }
    finally { setBusyId(null); }
  };

  const copyAndCreate = (inquiry) => {
    window.__inquiryPrefill = {
      inquiry_id: inquiry.id,
      parent_phone: inquiry.parent_phone,
      parent_whatsapp: inquiry.parent_whatsapp,
      parent_email: inquiry.parent_email,
      student_name: inquiry.student_name,
      student_dob: inquiry.student_dob,
      student_class: inquiry.student_class,
      syllabus: inquiry.syllabus,
      subjects_interested: inquiry.subjects_interested,
      mode: inquiry.mode,
      address_line: inquiry.address_line,
      post_office: inquiry.post_office,
      pincode: inquiry.pincode,
      district: inquiry.district,
      city: inquiry.city,
      about_student: inquiry.about_student,
    };
    // Mark as "created" so admin sees the conversion happened
    API.patch(`/inquiries/${inquiry.id}`, { status: 'created' }).catch(() => {});
    setActive('studreg');
  };

  const publicUrl = `${window.location.origin}/registration`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); alert('✓ Link copied'); }
    catch { window.prompt('Copy the link:', publicUrl); }
  };

  const statusBadge = (s) => {
    const map = {
      new:       { bg:'#dbeafe', color:'#1e40af', label:'New' },
      contacted: { bg:'#fef3c7', color:'#b45309', label:'Contacted' },
      created:   { bg:'#dcfce7', color:'#15803d', label:'✓ Created' },
      rejected:  { bg:'#fee2e2', color:'#b91c1c', label:'Rejected' },
    };
    const st = map[s] || map.new;
    return <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, background:st.bg, color:st.color }}>{st.label}</span>;
  };

  return (
    <>
      <div className="page-header">
        <div><h2>📨 Student Inquiries</h2><p>Parent-submitted inquiries. Review, contact, then create a student profile.</p></div>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom:16, background:'linear-gradient(135deg,#eef3ff 0%,#f5f3ff 100%)', border:'1px solid #c7d4f5' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
            <div style={{ fontSize:22 }}>🔗</div>
            <div style={{ flex:'1 1 240px', minWidth:200 }}>
              <div style={{ fontWeight:800, color:'#0f1f45', fontSize:14 }}>Share this link with parents</div>
              <div style={{ fontSize:12.5, color:'#475569', wordBreak:'break-all', marginTop:2 }}>{publicUrl}</div>
            </div>
            <button onClick={copyLink} className="btn btn-primary btn-sm">📋 Copy Link</button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">↗ Open</a>
          </div>
        </div>

        <div className="card" style={{ marginBottom:16, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <input className="form-control" placeholder="🔍 Search by parent / student name or phone…" value={q}
            onChange={e => setQ(e.target.value)} style={{ maxWidth:340 }} />
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {[['new','New'],['contacted','Contacted'],['created','Created'],['rejected','Rejected'],['all','All']].map(([v, lbl]) => (
              <button key={v} onClick={() => setStatusFilter(v)}
                style={{ padding:'5px 12px', borderRadius:8, border:'1.5px solid', fontSize:12, fontWeight:700, cursor:'pointer',
                  borderColor: statusFilter === v ? '#2554b8' : '#e2e8f0',
                  background: statusFilter === v ? '#2554b8' : '#fff',
                  color: statusFilter === v ? '#fff' : '#64748b' }}>{lbl}</button>
            ))}
          </div>
          <span style={{ fontSize:13, color:'#64748b', marginLeft:'auto' }}>{items.length} entr{items.length === 1 ? 'y' : 'ies'}</span>
        </div>

        {loading ? (
          <div className="card"><div className="empty-state"><p>Loading…</p></div></div>
        ) : items.length === 0 ? (
          <div className="card"><div className="empty-state"><div className="empty-icon">📨</div><p>No inquiries match your filters</p></div></div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {items.map(i => (
              <div key={i.id} className="card" style={{ padding:'14px 16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:10, alignItems:'flex-start' }}>
                  <div style={{ flex:'1 1 280px', minWidth:240 }}>
                    <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:4 }}>
                      <span style={{ fontWeight:800, color:'#0f1f45', fontSize:15 }}>{i.student_name}</span>
                      {i.student_class && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#eef2ff', color:'#4338ca' }}>{i.student_class}</span>}
                      {i.syllabus && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#f0fdf4', color:'#15803d' }}>{i.syllabus}</span>}
                      {statusBadge(i.status)}
                    </div>
                    <div style={{ fontSize:13, color:'#475569', marginBottom:4 }}>
                      <b>{i.parent_name}</b> · 📞 {i.parent_phone}{i.parent_whatsapp && i.parent_whatsapp !== i.parent_phone ? ` · 💬 ${i.parent_whatsapp}` : ''}{i.parent_email ? ` · ✉️ ${i.parent_email}` : ''}
                    </div>
                    <div style={{ fontSize:12, color:'#64748b' }}>
                      Submitted {i.submitted_at ? new Date(i.submitted_at + 'Z').toLocaleString() : '—'}
                      {i.student_dob ? ` · DOB ${i.student_dob}` : ''}
                      {i.city ? ` · ${i.city}` : ''}
                      {i.district && i.district !== i.city ? ` · ${i.district}` : ''}
                      {i.mode ? ` · ${i.mode}` : ''}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'flex-start' }}>
                    {i.status !== 'created' && (
                      <button onClick={() => copyAndCreate(i)} disabled={busyId === i.id}
                        title="Open Student Registration pre-filled with these details"
                        style={{ padding:'6px 12px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:12, cursor: busyId === i.id ? 'wait' : 'pointer' }}>
                        📋 Copy & Create Student
                      </button>
                    )}
                    {i.status === 'new' && (
                      <button onClick={() => updateStatus(i.id, 'contacted')} disabled={busyId === i.id} className="btn btn-ghost btn-sm">✓ Mark Contacted</button>
                    )}
                    {i.status !== 'rejected' && i.status !== 'created' && (
                      <button onClick={() => updateStatus(i.id, 'rejected')} disabled={busyId === i.id} className="btn btn-ghost btn-sm" style={{ color:'#dc2626' }}>✗ Reject</button>
                    )}
                    <button onClick={() => setExpandedId(expandedId === i.id ? null : i.id)} className="btn btn-ghost btn-sm">
                      {expandedId === i.id ? '▴ Hide' : '▾ Details'}
                    </button>
                    <button onClick={() => deleteInquiry(i.id)} disabled={busyId === i.id} className="btn btn-ghost btn-sm" style={{ color:'#94a3b8' }}>🗑</button>
                  </div>
                </div>
                {expandedId === i.id && (() => {
                  const addressParts = [i.address_line, i.post_office, i.pincode, i.district, i.city].filter(Boolean);
                  const hasAnything = i.subjects_interested || addressParts.length || i.about_student;
                  return (
                    <div style={{ marginTop:12, padding:'12px 14px', borderRadius:10, background:'#f8fafc', border:'1px solid #e2e8f0', fontSize:12.5, color:'#475569', lineHeight:1.7 }}>
                      {i.subjects_interested && <div><b>Subjects interested:</b> {i.subjects_interested}</div>}
                      {addressParts.length > 0 && <div><b>Address:</b> {addressParts.join(', ')}</div>}
                      {i.about_student && (
                        <div style={{ marginTop:6 }}>
                          <b>About the student (parent voice):</b>
                          <div style={{ marginTop:3, padding:'8px 10px', background:'#fff', borderRadius:8, border:'1px solid #e2e8f0', whiteSpace:'pre-wrap' }}>{i.about_student}</div>
                        </div>
                      )}
                      {!hasAnything && <div style={{ color:'#94a3b8' }}>No additional details provided.</div>}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}


function AdminHomeworkView() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [remindingId, setRemindingId] = useState(null);
  const [remindMsg, setRemindMsg] = useState('');

  const load = useCallback(async (name = '') => {
    setLoading(true);
    try {
      const p = name.trim() ? `?student_name=${encodeURIComponent(name.trim())}` : '';
      const r = await API.get(`/class/admin/homework${p}`);
      setRows(r.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendReminder = async (logId, studentName) => {
    if (!window.confirm(`Send WhatsApp homework reminder to ${studentName}?`)) return;
    setRemindingId(logId);
    setRemindMsg('');
    try {
      const r = await API.post(`/class/admin/homework/${logId}/remind`);
      setRemindMsg(r.data?.message || (r.data?.sent ? `✓ Reminder sent to ${studentName}` : 'Reminder not sent'));
    } catch (e) {
      setRemindMsg(e?.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setRemindingId(null);
      setTimeout(() => setRemindMsg(''), 5000);
    }
  };

  const today = new Date().toISOString().slice(0,10);
  const filtered = rows.filter(r => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return !r.homework_due_date || r.homework_due_date >= today;
    if (statusFilter === 'overdue')  return r.homework_due_date && r.homework_due_date < today;
    return true;
  });

  const statusBadge = (r) => {
    if (r.homework_due_date && r.homework_due_date < today) return <span className="badge badge-cancelled">⚠ Overdue</span>;
    return <span className="badge badge-scheduled">⏳ Pending</span>;
  };

  return (
    <>
      <div className="page-header">
        <div><h2>📚 Homework Tracker</h2><p>All homework assignments (read-only). Students share completed work in chat.</p></div>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom:16, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <input className="form-control" placeholder="🔍 Search by student name…" value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(search)}
            style={{ maxWidth:300 }} />
          <button className="btn btn-primary" onClick={() => load(search)} disabled={loading} style={{ fontSize:13 }}>
            {loading ? 'Searching…' : 'Search'}
          </button>
          {search && <button className="btn btn-secondary" style={{ fontSize:13 }} onClick={() => { setSearch(''); load(''); }}>Clear</button>}
          <div style={{ display:'flex', gap:6, marginLeft:'auto', flexWrap:'wrap' }}>
            {[['all','All'],['pending','⏳ Pending'],['overdue','⚠ Overdue']].map(([v, lbl]) => (
              <button key={v} onClick={() => setStatusFilter(v)}
                style={{ padding:'5px 12px', borderRadius:8, border:'1.5px solid', fontSize:12, fontWeight:700, cursor:'pointer',
                  borderColor: statusFilter === v ? '#2554b8' : '#e2e8f0',
                  background: statusFilter === v ? '#2554b8' : '#fff',
                  color: statusFilter === v ? '#fff' : '#64748b' }}>{lbl}</button>
            ))}
          </div>
          <span style={{ fontSize:13, color:'#64748b' }}>{filtered.length} entries</span>
        </div>
        {remindMsg && (
          <div className="card" style={{ marginBottom:12, padding:'10px 14px', background: remindMsg.startsWith('✓') || remindMsg.includes('sent to') ? 'rgba(22,163,74,0.08)' : 'rgba(217,119,6,0.08)', border:'1px solid', borderColor: remindMsg.startsWith('✓') || remindMsg.includes('sent to') ? 'rgba(22,163,74,0.25)' : 'rgba(217,119,6,0.25)', color: remindMsg.startsWith('✓') || remindMsg.includes('sent to') ? '#16a34a' : '#b45309', fontWeight:600, fontSize:13 }}>
            {remindMsg}
          </div>
        )}

        {filtered.length === 0 && !loading ? (
          <div className="card"><div className="empty-state"><div className="empty-icon">📚</div><p>No homework records found</p></div></div>
        ) : (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {['Class Date','Student','Subject','Companion','Homework','Due Date','Status','Action'].map(h => (
                      <th key={h} style={{ padding:'11px 14px', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.7, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r.log_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbff' }}>
                      <td style={{ padding:'11px 14px', fontSize:13, whiteSpace:'nowrap', color:'#64748b' }}>{r.class_date}</td>
                      <td style={{ padding:'11px 14px', fontWeight:600, color:'#0f1f45' }}>{r.student_name}</td>
                      <td style={{ padding:'11px 14px' }}>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#eff6ff', color:'#2554b8' }}>{r.subject}</span>
                      </td>
                      <td style={{ padding:'11px 14px', fontSize:13, color:'#64748b' }}>{r.tutor_name}</td>
                      <td style={{ padding:'11px 14px', fontSize:12, maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#475569' }}
                        title={r.homework}>{r.homework}</td>
                      <td style={{ padding:'11px 14px', fontSize:12, color:'#64748b', whiteSpace:'nowrap' }}>{r.homework_due_date || '—'}</td>
                      <td style={{ padding:'11px 14px' }}>{statusBadge(r)}</td>
                      <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                        <button
                          onClick={() => sendReminder(r.log_id, r.student_name)}
                          disabled={remindingId === r.log_id}
                          title={`Send WhatsApp homework reminder to ${r.student_name}`}
                          style={{ padding:'6px 12px', borderRadius:8, border:'none', background: remindingId === r.log_id ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', fontWeight:700, cursor: remindingId === r.log_id ? 'wait' : 'pointer', fontSize:11.5, boxShadow:'0 2px 8px rgba(22,163,74,0.25)' }}>
                          {remindingId === r.log_id ? '⏳ Sending…' : '💬 Send Reminder'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


// ── Root ───────────────────────────────────────────────────────────────────────
export default function Admin() {
  const [active, setActive] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadRooms, refreshUnread] = useUnreadChats();
  const unreadCount = Object.values(unreadRooms).filter(Boolean).length;
  // Per-admin feature flag — super-admin toggles this on for a specific admin.
  // Gates the profit-per-hour field on student profiles AND the Profit Report tab.
  const [canViewProfit, setCanViewProfit] = useState(false);
  React.useEffect(() => {
    API.get('/auth/me').then(r => setCanViewProfit(!!r.data?.can_view_profit)).catch(() => {});
  }, []);

  // Freeze body scroll when sidebar is open (prevents main content moving)
  React.useEffect(() => {
    document.body.classList.toggle('sidebar-is-open', sidebarOpen);
    return () => document.body.classList.remove('sidebar-is-open');
  }, [sidebarOpen]);

  // Allow child components (e.g. dashboard pending-actions card) to switch nav
  React.useEffect(() => {
    const handler = (e) => {
      if (e?.detail) setActive(e.detail);
    };
    window.addEventListener('admin-nav', handler);
    return () => window.removeEventListener('admin-nav', handler);
  }, []);

  // Deep-link from push notifications (?nav=chat etc.) and SW postMessage
  React.useEffect(() => {
    const applyNav = (target) => {
      if (!target) return;
      setActive(target);
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
    };
    const p = new URLSearchParams(window.location.search);
    applyNav(p.get('nav'));
    const onMsg = (e) => {
      if (e.data?.type === 'notification-click' && e.data?.url) {
        try {
          const u = new URL(e.data.url, window.location.origin);
          applyNav(u.searchParams.get('nav'));
        } catch (_) {}
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, []);

  // Touch swipe to open/close sidebar
  const touchStartX = React.useRef(null);
  const touchStartY = React.useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    // Only treat as horizontal swipe if horizontal movement dominates
    if (Math.abs(dx) < 40 || dy > Math.abs(dx)) return;
    if (dx > 0 && touchStartX.current < 30) setSidebarOpen(true);  // swipe right from left edge
    if (dx < 0 && sidebarOpen) setSidebarOpen(false);               // swipe left closes sidebar
    touchStartX.current = null;
  };

  const views = {
    dashboard: <Dashboard />,
    users:     <Users canViewProfit={canViewProfit} />,
    inquiries: <InquiriesAdmin setActive={setActive} />,
    studreg:   <StudentRegistration canViewProfit={canViewProfit} />,
    tutorreg:  <TutorRegistration />,
    classes:   <ClassManager />,
    subjects:  <SubjectManager />,
    enroll:    <Enroll />,
    schedule:  <ScheduleManager />,
    logs:      <ClassLogs />,
    payments:  <PaymentsModule canViewProfit={canViewProfit} />,
    chat:      <ChatRoomsAdmin unreadRooms={unreadRooms} onRoomRead={refreshUnread} />,
    classreq:    <ClassRequestsAdmin />,
    bulkleave:   <AdminBulkLeave />,
    studbulklv:  <AdminStudentBulkLeave />,
    leavereq:    <AdminLeaveRequests />,
    feedback:    <FeedbackAdmin />,
    emergency:   <EmergencyAdmin />,
    activitylog: <ActivityLog />,
    wasettings:  <WhatsAppSettings />,
    pushsettings: <PushNotificationSettings />,
    exams:       <ExamManagement />,
    cqueries:    <CompanionQueriesAdmin />,
    vr:          <VRAdmin />,
    teams:       <TeamsAdmin />,
    materials:   <AdminMaterials />,
    testgrades:  <AdminTestsGrades />,
    homework:    <AdminHomeworkView />,
  };

  return (
    <div className="app-shell" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar active={active} setActive={setActive} unreadCount={unreadCount} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="main-content">
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#fff', borderBottom:'1px solid #e8edf8', position:'sticky', top:0, zIndex:5 }} className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <img src="/logo.png" alt="logo" style={{ height:28, objectFit:'contain' }} />
            <span style={{ fontWeight:800, fontSize:14, color:'#0f1f45' }}>Sapience Edu Connect</span>
          </div>
        </div>
        {views[active]}
      </div>
    </div>
  );
}
